import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from 'chart.js';
import { Bar, Chart, Doughnut, Line, Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import styles from './BurnInAnalyticsPage.module.css';
import GoToDashboardButton from '../GoToDashboardButton';
import { calculateCapabilityMetrics } from '../controlChart/controlChartData';
import { applyBrandedPdfChrome, exportNodeAsBrandedPNG } from '../../branding/exportBranding';

const getApiBase = () =>
  process.env.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:3001' : '');
const CONTROL_CHART_RESULTS_STORAGE_KEY = 'smarti:last-control-chart-results';

const palette = ['#0f766e', '#0891b2', '#f59e0b', '#ef4444', '#7c3aed', '#2563eb', '#e11d48', '#84cc16'];

const formatNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return new Intl.NumberFormat().format(numeric);
};

const formatDecimal = (value, digits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return numeric.toFixed(digits);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsed);
};

const formatBurnInStepLabel = (row) => (
  row?.step_number
    ? `${row.step_number} · ${row.step_name}`
    : String(row?.step_name || '')
);

const truncateChartLabel = (value, maxLength = 26) => {
  const text = String(value || '').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
};

const formatBurnInParetoLabel = (row) => {
  const performanceLabel = row?.performance_test_number ? `Perf. test n° ${row.performance_test_number}` : 'Perf. test n° -';
  return [performanceLabel, truncateChartLabel(formatBurnInStepLabel(row))];
};

const getBurnInPieSegmentColors = (status) => {
  const normalized = String(status || '').trim().toUpperCase();
  if (normalized === 'PASS' || normalized === 'PASSED') {
    return { background: '#2f9e44', hover: '#40c057' };
  }
  if (normalized === 'FAIL' || normalized === 'FAILED') {
    return { background: '#d64545', hover: '#ef6b6b' };
  }
  return { background: '#94a3b8', hover: '#cbd5e1' };
};

const getFiniteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildBurnInCriticalityReason = ({
  outOfSpecLow,
  outOfSpecHigh,
  outOfControlLow,
  outOfControlHigh,
  nearSpec,
  nearControl,
}) => {
  const reasons = [];
  if (outOfSpecLow) reasons.push('Below LSL');
  if (outOfSpecHigh) reasons.push('Above USL');
  if (outOfControlLow) reasons.push('Below LCL');
  if (outOfControlHigh) reasons.push('Above UCL');
  if (!reasons.length && nearSpec) reasons.push('Near specification limits');
  if (!reasons.length && nearControl) reasons.push('Near control limits');
  return reasons.length ? reasons.join(' + ') : 'Within limits';
};

const annotateBurnInControlRows = (rows = [], limits = {}) => rows.map((row) => {
  const valueNumber = getFiniteNumber(row.value_number);
  const avgValue = getFiniteNumber(row.avg_value);
  const lowerControlLimit = getFiniteNumber(limits.lowerControlLimit ?? row.lower_control_limit);
  const upperControlLimit = getFiniteNumber(limits.upperControlLimit ?? row.upper_control_limit);
  const lowerSpecLimit = getFiniteNumber(limits.lowerSpecLimit ?? row.limit_low_number);
  const upperSpecLimit = getFiniteNumber(limits.upperSpecLimit ?? row.limit_high_number);
  const specSpan = Number.isFinite(lowerSpecLimit) && Number.isFinite(upperSpecLimit)
    ? Math.abs(upperSpecLimit - lowerSpecLimit)
    : null;
  const controlSpan = Number.isFinite(lowerControlLimit) && Number.isFinite(upperControlLimit)
    ? Math.abs(upperControlLimit - lowerControlLimit)
    : null;
  const nearestSpecDistance = Number.isFinite(valueNumber)
    ? Math.min(
      Number.isFinite(lowerSpecLimit) ? Math.abs(valueNumber - lowerSpecLimit) : Number.POSITIVE_INFINITY,
      Number.isFinite(upperSpecLimit) ? Math.abs(upperSpecLimit - valueNumber) : Number.POSITIVE_INFINITY
    )
    : Number.POSITIVE_INFINITY;
  const nearestControlDistance = Number.isFinite(valueNumber)
    ? Math.min(
      Number.isFinite(lowerControlLimit) ? Math.abs(valueNumber - lowerControlLimit) : Number.POSITIVE_INFINITY,
      Number.isFinite(upperControlLimit) ? Math.abs(upperControlLimit - valueNumber) : Number.POSITIVE_INFINITY
    )
    : Number.POSITIVE_INFINITY;

  const outOfSpecLow = Number.isFinite(valueNumber) && Number.isFinite(lowerSpecLimit) && valueNumber < lowerSpecLimit;
  const outOfSpecHigh = Number.isFinite(valueNumber) && Number.isFinite(upperSpecLimit) && valueNumber > upperSpecLimit;
  const outOfControlLow = Number.isFinite(valueNumber) && Number.isFinite(lowerControlLimit) && valueNumber < lowerControlLimit;
  const outOfControlHigh = Number.isFinite(valueNumber) && Number.isFinite(upperControlLimit) && valueNumber > upperControlLimit;
  const nearSpec = !outOfSpecLow && !outOfSpecHigh && Number.isFinite(specSpan) && specSpan > 0 && nearestSpecDistance <= (specSpan * 0.1);
  const nearControl = !outOfControlLow && !outOfControlHigh && Number.isFinite(controlSpan) && controlSpan > 0 && nearestControlDistance <= (controlSpan * 0.08);
  const isCritical = outOfSpecLow || outOfSpecHigh || outOfControlLow || outOfControlHigh;
  const isWarning = !isCritical && (nearSpec || nearControl);
  const criticalityLabel = isCritical ? 'Critical' : isWarning ? 'Warning' : 'Stable';

  return {
    ...row,
    measured_at_label: formatDateTime(row.measured_at),
    sequence_number_label: row.sequence_number ?? '-',
    effective_lcl: lowerControlLimit,
    effective_ucl: upperControlLimit,
    effective_lsl: lowerSpecLimit,
    effective_usl: upperSpecLimit,
    delta_from_mean: Number.isFinite(valueNumber) && Number.isFinite(avgValue)
      ? Number((valueNumber - avgValue).toFixed(4))
      : null,
    criticality_label: criticalityLabel,
    criticality_reason: buildBurnInCriticalityReason({
      outOfSpecLow,
      outOfSpecHigh,
      outOfControlLow,
      outOfControlHigh,
      nearSpec,
      nearControl,
    }),
    is_critical: isCritical,
    is_warning: isWarning,
    is_out_of_spec: outOfSpecLow || outOfSpecHigh,
    is_out_of_control: outOfControlLow || outOfControlHigh,
  };
});

const buildCapabilityMetricCards = (metrics) => [
  { label: 'LSL', value: Number.isFinite(metrics?.lsl) ? formatDecimal(metrics.lsl, 4) : '-' },
  { label: 'USL', value: Number.isFinite(metrics?.usl) ? formatDecimal(metrics.usl, 4) : '-' },
  { label: 'Mean', value: Number.isFinite(metrics?.mean) ? formatDecimal(metrics.mean, 4) : '-' },
  { label: 'Sigma within', value: Number.isFinite(metrics?.sigmaWithin) ? formatDecimal(metrics.sigmaWithin, 4) : '-' },
  { label: 'Sigma overall', value: Number.isFinite(metrics?.sigmaOverall) ? formatDecimal(metrics.sigmaOverall, 4) : '-' },
  { label: 'Cp', value: Number.isFinite(metrics?.cp) ? formatDecimal(metrics.cp, 3) : '-' },
  { label: 'Cpu', value: Number.isFinite(metrics?.cpu) ? formatDecimal(metrics.cpu, 3) : '-' },
  { label: 'Cpl', value: Number.isFinite(metrics?.cpl) ? formatDecimal(metrics.cpl, 3) : '-' },
  { label: 'Cpk', value: Number.isFinite(metrics?.cpk) ? formatDecimal(metrics.cpk, 3) : '-' },
  { label: 'Pp', value: Number.isFinite(metrics?.pp) ? formatDecimal(metrics.pp, 3) : '-' },
  { label: 'Ppu', value: Number.isFinite(metrics?.ppu) ? formatDecimal(metrics.ppu, 3) : '-' },
  { label: 'Ppl', value: Number.isFinite(metrics?.ppl) ? formatDecimal(metrics.ppl, 3) : '-' },
  { label: 'Ppk', value: Number.isFinite(metrics?.ppk) ? formatDecimal(metrics.ppk, 3) : '-' },
  { label: 'k', value: Number.isFinite(metrics?.k) ? formatDecimal(metrics.k, 3) : '-' },
  { label: 'Z bench', value: Number.isFinite(metrics?.zBench) ? formatDecimal(metrics.zBench, 3) : '-' },
];

const CONTROL_MODAL_SIZES = [
  { key: 'medium', label: 'Medio' },
  { key: 'large', label: 'Grande' },
  { key: 'full', label: 'Schermo intero' },
];

const isBurnInPassStatus = (value) => ['PASS', 'PASSED'].includes(String(value || '').trim().toUpperCase());

const computeControlSeriesStats = (points = [], overrides = {}) => {
  const numericPoints = points
    .map((point) => ({
      ...point,
      numericValue: Number(point?.value_number),
    }))
    .filter((point) => Number.isFinite(point.numericValue));

  const values = numericPoints.map((point) => point.numericValue);
  const sampleCount = values.length;
  const meanValue = sampleCount
    ? values.reduce((sum, value) => sum + value, 0) / sampleCount
    : 0;
  const variance = sampleCount
    ? values.reduce((sum, value) => sum + ((value - meanValue) ** 2), 0) / sampleCount
    : 0;
  const sigmaValue = Math.sqrt(variance);
  const movingRanges = values.slice(1).map((value, index) => Math.abs(value - values[index]));
  const mrBar = movingRanges.length
    ? movingRanges.reduce((sum, value) => sum + value, 0) / movingRanges.length
    : 0;
  const computedUcl = meanValue + (sigmaValue * 3);
  const computedLcl = meanValue - (sigmaValue * 3);
  const upperControlLimit = Number.isFinite(overrides.upper) ? overrides.upper : computedUcl;
  const lowerControlLimit = Number.isFinite(overrides.lower) ? overrides.lower : computedLcl;
  const mrUcl = mrBar * 3.267;

  const minValue = sampleCount ? Math.min(...values) : null;
  const maxValue = sampleCount ? Math.max(...values) : null;
  const range = sampleCount && minValue !== maxValue ? maxValue - minValue : 0;
  const binCount = sampleCount ? Math.max(10, Math.ceil(2 * Math.cbrt(sampleCount))) : 0;
  const binWidth = range > 0 ? range / binCount : 1;
  const histogramBins = binCount ? Array(binCount).fill(0) : [];

  values.forEach((value) => {
    if (!binCount) return;
    const binIndex = Math.min(binCount - 1, Math.max(0, Math.floor((value - minValue) / binWidth)));
    histogramBins[binIndex] += 1;
  });

  const histogramData = histogramBins.map((count, index) => ({
    x: minValue + ((index + 0.5) * binWidth),
    y: count,
  }));

  const fittedNormalCurve = [];
  if (sampleCount && Number.isFinite(sigmaValue) && sigmaValue > 0) {
    const axisMin = Math.min(minValue, meanValue - (4 * sigmaValue));
    const axisMax = Math.max(maxValue, meanValue + (4 * sigmaValue));
    const stepSize = (axisMax - axisMin) / 180;
    for (let x = axisMin; x <= axisMax; x += stepSize) {
      const density = (1 / (sigmaValue * Math.sqrt(2 * Math.PI)))
        * Math.exp(-0.5 * (((x - meanValue) / sigmaValue) ** 2));
      fittedNormalCurve.push({
        x,
        y: density * sampleCount * (binWidth || 1),
      });
    }
  }

  return {
    points: numericPoints,
    values,
    sampleCount,
    meanValue,
    sigmaValue,
    upperControlLimit,
    lowerControlLimit,
    movingRanges,
    mrBar,
    mrUcl,
    histogramData,
    fittedNormalCurve,
  };
};

const buildCumulativePercentageSeries = (rows = [], valueKey) => {
  const values = rows.map((row) => Number(row?.[valueKey]) || 0);
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!total) return values.map(() => 0);

  let runningTotal = 0;
  return values.map((value) => {
    runningTotal += value;
    return Number(((runningTotal / total) * 100).toFixed(2));
  });
};

const addCumulativePercentageToRows = (rows = [], valueKey, outputKey = 'cumulative_pct') => {
  const cumulativeSeries = buildCumulativePercentageSeries(rows, valueKey);
  return rows.map((row, index) => ({
    ...row,
    [outputKey]: cumulativeSeries[index] ?? 0,
  }));
};

const findParetoThresholdIndex = (rows = [], percentageKey = 'cumulative_pct', threshold = 80) =>
  rows.findIndex((row) => (Number(row?.[percentageKey]) || 0) >= threshold);

const paretoThresholdPlugin = {
  id: 'paretoThresholdPlugin',
  afterDatasetsDraw(chart, _args, pluginOptions) {
    const thresholdIndex = Number(pluginOptions?.thresholdIndex);
    if (!Number.isFinite(thresholdIndex) || thresholdIndex < 0) return;

    const xScale = chart.scales?.x;
    const yScale = chart.scales?.yPercent;
    const { ctx, chartArea } = chart;
    if (!xScale || !yScale || !chartArea) return;

    const thresholdLabel = chart.data?.labels?.[thresholdIndex];
    const x = xScale.getPixelForValue(thresholdIndex, thresholdLabel);
    const thresholdY = yScale.getPixelForValue(pluginOptions?.thresholdValue ?? 80);
    if (!Number.isFinite(x) || !Number.isFinite(thresholdY)) return;

    ctx.save();
    ctx.strokeStyle = pluginOptions?.color || '#f59e0b';
    ctx.lineWidth = pluginOptions?.lineWidth || 2;
    ctx.setLineDash(pluginOptions?.dash || [10, 6]);
    ctx.beginPath();
    ctx.moveTo(x, chartArea.bottom);
    ctx.lineTo(x, chartArea.top);
    ctx.stroke();

    if (pluginOptions?.showLabel !== false) {
      ctx.setLineDash([]);
      ctx.fillStyle = pluginOptions?.color || '#f59e0b';
      ctx.font = '700 12px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(pluginOptions?.label || '80% cutoff', x + 6, Math.max(chartArea.top + 14, thresholdY - 8));
    }
    ctx.restore();
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
  Filler,
  paretoThresholdPlugin
);

const slugifyFileName = (value) => String(value || 'burn-in-export')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const toExportCell = (value, format) => {
  const formatted = typeof format === 'function' ? format(value) : value;
  if (formatted === null || formatted === undefined || formatted === '') return '-';
  return String(formatted);
};

const toCsvCell = (value) => `"${String(value).replace(/"/g, '""')}"`;

const exportChartNodeAsPNG = async (node, fileName) => {
  if (!node) return;
  await exportNodeAsBrandedPNG(node, fileName, { backgroundColor: '#ffffff', scale: 2 });
};

const exportRowsAsCSV = (columns, rows, fileName) => {
  if (!rows.length) return;
  const csvRows = [
    columns.map((column) => toCsvCell(column.label)),
    ...rows.map((row) => columns.map((column) => toCsvCell(toExportCell(row[column.key], column.format)))),
  ];
  const csvContent = csvRows.map((row) => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${fileName}.csv`);
};

const exportRowsAsPDF = (title, columns, rows, fileName) => {
  if (!rows.length) return;
  const doc = new jsPDF();
  doc.text(title, 14, 15);
  doc.autoTable({
    head: [columns.map((column) => column.label)],
    body: rows.map((row) => columns.map((column) => toExportCell(row[column.key], column.format))),
    startY: 20,
    styles: {
      fontSize: 9,
      cellPadding: 2.5,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [15, 118, 110],
    },
  });
  applyBrandedPdfChrome(doc, { title });
  doc.save(`${fileName}.pdf`);
};

const apiGet = async (path, params = {}) => {
  const response = await axios.get(`${getApiBase()}${path}`, {
    params,
    withCredentials: true,
  });
  return response.data;
};

const apiPost = async (path, body = {}) => {
  const response = await axios.post(`${getApiBase()}${path}`, body, {
    withCredentials: true,
  });
  return response.data;
};

const burnInAdvancedFilterFields = [
  { key: 'workbookFormat', label: 'Workbook Format' },
  { key: 'sheetName', label: 'Sheet Name' },
  { key: 'performanceTestNumber', label: 'Performance test n°', multi: true },
  { key: 'sourceLabel', label: 'Source File' },
  { key: 'stepName', label: 'Step Name' },
  { key: 'status', label: 'Status' },
  { key: 'units', label: 'Units' },
  { key: 'stationId', label: 'Station' },
  { key: 'operatorName', label: 'Operator' },
  { key: 'uutProductCode', label: 'Product Code' },
  { key: 'uutSerialNumber', label: 'Serial Number' },
  { key: 'fixId', label: 'Fixture ID' },
  { key: 'lineName', label: 'Line' },
  { key: 'testSocketIndex', label: 'Socket Index' },
  { key: 'versionSw', label: 'Software Version' },
  { key: 'versionHw', label: 'Hardware Version' },
  { key: 'measuredHour', label: 'Measured Hour' },
];

const burnInUnitDisplayLimitOptions = [
  { value: '1', label: '1 unit' },
  { value: '5', label: '5 units' },
  { value: '10', label: '10 units' },
  { value: '15', label: '15 units' },
  { value: '20', label: '20 units' },
  { value: '30', label: '30 units' },
  { value: '50', label: '50 units' },
  { value: '100', label: '100 units' },
  { value: 'all', label: 'All units' },
];

const burnInDateFilterFields = [
  { key: 'measuredDateStart', label: 'Measured Date From' },
  { key: 'measuredDateEnd', label: 'Measured Date To' },
];

const burnInTemperatureFilterFields = [
  { key: 'workbookFormat', label: 'Workbook Format' },
  { key: 'sheetName', label: 'Sheet Name' },
  { key: 'performanceTestNumber', label: 'Performance test n°', multi: true },
  { key: 'sourceLabel', label: 'Source File' },
  { key: 'stationId', label: 'Station' },
  { key: 'operatorName', label: 'Operator' },
  { key: 'uutProductCode', label: 'Product Code' },
  { key: 'uutSerialNumber', label: 'Serial Number' },
  { key: 'fixId', label: 'Fixture ID' },
  { key: 'lineName', label: 'Line' },
  { key: 'testSocketIndex', label: 'Socket Index' },
  { key: 'versionSw', label: 'Software Version' },
  { key: 'versionHw', label: 'Hardware Version' },
  { key: 'measuredHour', label: 'Measured Hour' },
];

const parseControlChartSeriesKey = (value) => {
  const [stepName = '', units = ''] = String(value || '').split('|||');
  return {
    stepName,
    units,
  };
};

const buildDefaultBurnInFilters = () => ({
  workbookFormat: '',
  sheetName: '',
  performanceTestNumber: [],
  sourceLabel: '',
  stepName: '',
  status: '',
  units: '',
  stationId: '',
  operatorName: '',
  uutProductCode: '',
  uutSerialNumber: '',
  fixId: '',
  lineName: '',
  testSocketIndex: '',
  versionSw: '',
  versionHw: '',
  measuredHour: '',
  measuredDateStart: '',
  measuredDateEnd: '',
});

const stringifyFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean).join(',');
  }
  return String(value || '').trim();
};

const formatPerformanceTestCoverage = (row) => {
  const values = Array.isArray(row?.performance_test_numbers)
    ? row.performance_test_numbers.map((value) => String(value || '').trim()).filter(Boolean)
    : [];
  if (!values.length) return '-';
  return values.map((value) => `n° ${value}`).join(', ');
};

const getFirstUnselectedFilterOption = (options = [], selectedValues = []) => {
  const selectedSet = new Set((selectedValues || []).map((value) => String(value || '').trim()).filter(Boolean));
  return (options || []).map((value) => String(value || '').trim()).find((value) => value && !selectedSet.has(value)) || '';
};

export const burnInNavItems = [
  { path: '/burn-in-temperature-analyzer', label: 'Temperature Analyzer', shortLabel: 'Temperature' },
  { path: '/burn-in-status', label: 'Burn-In Status', shortLabel: 'Status' },
  { path: '/burn-in-step-fail-rate', label: 'Burn-In Step Fail Rate', shortLabel: 'Fail Rate' },
  { path: '/burn-in-step-volume', label: 'Burn-In Step Volume', shortLabel: 'Step Volume' },
  { path: '/burn-in-timeline', label: 'Burn-In Timeline', shortLabel: 'Timeline' },
  { path: '/burn-in-hourly-activity', label: 'Burn-In Hourly Activity', shortLabel: 'Hourly' },
  { path: '/burn-in-source-health', label: 'Burn-In Source Health', shortLabel: 'Source Health' },
  { path: '/burn-in-upload-comparison', label: 'Burn-In Upload Comparison', shortLabel: 'Uploads' },
  { path: '/burn-in-units', label: 'Burn-In Units', shortLabel: 'Units' },
  { path: '/burn-in-data-completeness', label: 'Burn-In Data Completeness', shortLabel: 'Completeness' },
  { path: '/burn-in-sequence-hotspots', label: 'Burn-In Sequence Hotspots', shortLabel: 'Sequence' },
  { path: '/burn-in-sequence-bands', label: 'Burn-In Sequence Bands', shortLabel: 'Seq Bands' },
  { path: '/burn-in-value-spread', label: 'Burn-In Value Spread', shortLabel: 'Value Spread' },
  { path: '/burn-in-limit-compliance', label: 'Burn-In Limit Compliance', shortLabel: 'Limits' },
  { path: '/burn-in-near-limit-risk', label: 'Burn-In Near-Limit Risk', shortLabel: 'Near Limit' },
  { path: '/burn-in-categorical-values', label: 'Burn-In Categorical Values', shortLabel: 'Values' },
];

export const burnInFeaturedNavItems = [
  { path: '/burn-in-pie-chart', label: 'Pie Chart Analysis', shortLabel: 'Pie Chart' },
  { path: '/burn-in-pareto-chart', label: 'Pareto Chart Analysis', shortLabel: 'Pareto Chart' },
  { path: '/burn-in-control-chart', label: 'Control Chart Analysis', shortLabel: 'Control Chart' },
  { path: '/burn-in-uut-status-summary', label: 'UUT Status Summary', shortLabel: 'UUT Status' },
  { path: '/burn-in-root-cause-analyzer', label: 'Root Cause Analyzer', shortLabel: 'Root Cause' },
];

export const burnInConfigs = {
  '/burn-in-temperature-analyzer': {
    eyebrow: 'Burn-in thermal behavior',
    title: 'Temperature Analyzer',
    supportsAdvancedFilters: true,
    description: 'This dashboard preview surfaces the first three critical steps from the burn-in thermal analysis and shows how their FAIL behavior changes across reconstructed chamber temperatures.',
    endpoint: '/api/burn-in/temperature-analyzer',
    chartType: 'bar',
    customWidget: 'temperatureAnalyzer',
    getRows: (data) => Array.isArray(data?.stepSensitivity) ? data.stepSensitivity : [],
    getMetrics: (rows) => {
      const safeRows = Array.isArray(rows) ? rows : [];
      const criticalRows = safeRows.filter((row) => Number(row.total_fails) > 0);
      return [
        { label: 'Critical steps', value: formatNumber(Math.min(criticalRows.length, 3)) },
        { label: 'Top step fails', value: formatNumber(criticalRows[0]?.total_fails || 0) },
        { label: 'Max spread', value: criticalRows.length ? `${formatDecimal(Math.max(...criticalRows.map((row) => Number(row.fail_rate_spread) || 0)))}%` : '0.00%' },
        { label: 'Temps seen', value: formatNumber(Math.max(0, ...criticalRows.slice(0, 3).map((row) => Number(row.temperature_count) || 0))) },
      ];
    },
  },
  '/burn-in-pie-chart': {
    eyebrow: 'Burn-in overview',
    title: 'Pie Chart Analysis',
    supportsAdvancedFilters: true,
    description: 'This burn-in pie chart works at the UUT or serial-number level instead of raw measurement rows. It groups each burn-in unit by its overall UUT status so the distribution reflects the final unit outcome in the selected burn-in scope.',
    widgetDescription: 'This preview shows how the selected burn-in units are split across final statuses such as PASS and FAIL.',
    endpoint: '/api/burn-in/status-overview',
    chartType: 'pie',
    getRows: (data) => data?.unitStatusCounts || [],
    getMetrics: (data) => {
      const rows = data?.unitStatusCounts || [];
      const retryAwareSummary = data?.retryAwareSummary || {};
      const totalUnits = Number(retryAwareSummary.total_units) || rows.reduce((sum, row) => sum + (Number(row.count) || 0), 0);
      const failedUnits = Number(retryAwareSummary.units_having_fail) || rows
        .filter((row) => String(row.status || '').toUpperCase() === 'FAIL')
        .reduce((sum, row) => sum + (Number(row.count) || 0), 0);
      return [
        { label: 'Units', value: formatNumber(totalUnits) },
        { label: 'Failed units', value: formatNumber(failedUnits) },
        { label: 'Units without fail', value: formatNumber(retryAwareSummary.units_without_fail || 0) },
        { label: 'Failed attempts', value: formatNumber(retryAwareSummary.failed_attempts || 0) },
        { label: 'Unit fail rate', value: totalUnits ? `${formatDecimal((failedUnits / totalUnits) * 100)}%` : '0.00%' },
        { label: 'Status buckets', value: formatNumber(rows.length) },
      ];
    },
    buildChartData: (data) => {
      const rows = data?.unitStatusCounts || [];
      return {
        labels: rows.map((row) => row.status),
        datasets: [
          {
            label: 'Units',
            data: rows.map((row) => Number(row.count) || 0),
            backgroundColor: rows.map((row) => getBurnInPieSegmentColors(row.status).background),
            hoverBackgroundColor: rows.map((row) => getBurnInPieSegmentColors(row.status).hover),
            borderColor: '#ffffff',
            borderWidth: 3,
          },
        ],
      };
    },
    tableColumns: [
      { key: 'status', label: 'Status' },
      { key: 'count', label: 'Count', format: formatNumber },
      { key: 'serials', label: 'Serials', format: (value) => Array.isArray(value) ? value.join(', ') : '-' },
    ],
  },
  '/burn-in-pareto-chart': {
    eyebrow: 'Burn-in failures',
    title: 'Pareto Chart Analysis',
    supportsAdvancedFilters: true,
    description: 'This burn-in Pareto view ranks the burn-in steps that generate the largest failure load. It uses only burn-in rows marked FAIL or FAILED, so the analysis reflects the burn-in database rather than production records.',
    endpoint: '/api/burn-in/failure-pareto',
    chartType: 'bar',
    getRows: (rows) => addCumulativePercentageToRows(Array.isArray(rows) ? rows : [], 'fail_count'),
    getMetrics: (rows) => [
      { label: 'Tracked fail steps', value: formatNumber(rows.length) },
      { label: 'Top step fails', value: formatNumber(rows[0]?.fail_count || 0) },
      { label: 'Top step fail rate', value: rows[0] ? `${formatDecimal(((Number(rows[0].fail_count) || 0) / Math.max(Number(rows[0].total_count) || 1, 1)) * 100)}%` : '0.00%' },
      { label: 'Sources touched', value: formatNumber(rows[0]?.source_count || 0) },
    ],
    buildChartData: (rows = []) => ({
      labels: rows.map((row) => formatBurnInParetoLabel(row)),
      datasets: [
        {
          label: 'Fail count',
          data: rows.map((row) => Number(row.fail_count) || 0),
          backgroundColor: '#ef4444',
          borderRadius: 6,
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Cumulative fail %',
          data: rows.map((row) => Number(row.cumulative_pct) || 0),
          borderColor: '#2563eb',
          backgroundColor: '#2563eb',
          pointBackgroundColor: '#2563eb',
          pointBorderColor: '#ffffff',
          pointRadius: 4,
          pointHoverRadius: 5,
          tension: 0.28,
          yAxisID: 'yPercent',
          order: 1,
        },
      ],
    }),
    buildChartOptions: (rows = []) => ({
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 38,
            minRotation: 38,
            font: {
              size: 11,
            },
          },
          title: {
            display: true,
            text: 'Burn-in step with related Performance test n° 1-9',
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Fail count',
          },
        },
        yPercent: {
          position: 'right',
          beginAtZero: true,
          min: 0,
          max: 100,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: (value) => `${value}%`,
          },
          title: {
            display: true,
            text: 'Cumulative %',
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return '';
              return formatBurnInStepLabel(row);
            },
            beforeBody: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return [];
              return [`Performance test n° ${row.performance_test_number || '-'}`];
            },
            afterBody: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return [];
              return [
                row.performance_test_label || null,
                row.temperature_points ? `Temperature fails: ${row.temperature_points}` : null,
                row.measured_value_summary ? `Measured values: ${row.measured_value_summary}` : null,
              ].filter(Boolean);
            },
          },
        },
        paretoThresholdPlugin: {
          thresholdIndex: findParetoThresholdIndex(rows),
          thresholdValue: 80,
          label: '80% cutoff',
        },
      },
    }),
    tableColumns: [
      { key: 'performance_test_number', label: 'Perf. Test No.' },
      { key: 'step_number', label: 'Step No.' },
      { key: 'step_name', label: 'Step', render: (_value, row) => formatBurnInStepLabel(row) },
      { key: 'performance_test_label', label: 'Performance Test' },
      { key: 'fail_count', label: 'Fail Count', format: formatNumber },
      { key: 'total_count', label: 'Total Rows', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
      { key: 'temperature_points', label: '0.1 / Temperature Fails' },
      { key: 'measured_value_summary', label: 'Measured Values by Temperature' },
      { key: 'cumulative_pct', label: 'Cumulative %', format: (value) => `${formatDecimal(value)}%` },
    ],
  },
  '/burn-in-status': {
    eyebrow: 'Burn-in analytics',
    title: 'Burn-In Status Overview',
    description: 'This page shows the overall status mix for the selected burn-in scope. Counts come directly from stored row statuses, while the summary tiles also show how many rows contain numeric values and valid timestamps, so you can quickly judge both result balance and data quality.',
    endpoint: '/api/burn-in/status-overview',
    chartType: 'doughnut',
    getRows: (data) => data?.statusCounts || [],
    getMetrics: (data) => {
      const totals = data?.totals || {};
      const total = Number(totals.total_measurements) || 0;
      const failed = Number(totals.failed_measurements) || 0;
      return [
        { label: 'Measurements', value: formatNumber(total) },
        { label: 'Fail rate', value: total ? `${formatDecimal((failed / total) * 100)}%` : '0.00%' },
        { label: 'Unique steps', value: formatNumber(totals.unique_step_count) },
        { label: 'Numeric values', value: formatNumber(totals.numeric_measurements) },
      ];
    },
    buildChartData: (data) => {
      const rows = data?.statusCounts || [];
      return {
        labels: rows.map((row) => row.status),
        datasets: [
          {
            label: 'Rows',
            data: rows.map((row) => Number(row.count) || 0),
            backgroundColor: palette,
            borderWidth: 0,
          },
        ],
      };
    },
    tableColumns: [
      { key: 'status', label: 'Status' },
      { key: 'count', label: 'Count', format: formatNumber },
    ],
  },
  '/burn-in-failure-pareto': {
    eyebrow: 'Burn-in failures',
    title: 'Burn-In Failure Pareto',
    description: 'This page ranks steps by failed-row count. The chart groups rows by step name and sums only rows marked FAIL or FAILED, so it answers a simple question: which steps create the biggest absolute failure load in the burn-in dataset.',
    endpoint: '/api/burn-in/failure-pareto',
    chartType: 'bar',
    getRows: (rows) => addCumulativePercentageToRows(Array.isArray(rows) ? rows : [], 'fail_count'),
    getMetrics: (rows) => [
      { label: 'Tracked fail steps', value: formatNumber(rows.length) },
      { label: 'Top step fails', value: formatNumber(rows[0]?.fail_count || 0) },
      { label: 'Top step fail rate', value: rows[0] ? `${formatDecimal(((Number(rows[0].fail_count) || 0) / Math.max(Number(rows[0].total_count) || 1, 1)) * 100)}%` : '0.00%' },
      { label: 'Sources touched', value: formatNumber(rows[0]?.source_count || 0) },
    ],
    buildChartData: (rows = []) => ({
      labels: rows.map((row) => formatBurnInParetoLabel(row)),
      datasets: [
        {
          label: 'Fail count',
          data: rows.map((row) => Number(row.fail_count) || 0),
          backgroundColor: '#ef4444',
          borderRadius: 6,
          yAxisID: 'y',
          order: 2,
        },
        {
          type: 'line',
          label: 'Cumulative fail %',
          data: rows.map((row) => Number(row.cumulative_pct) || 0),
          borderColor: '#2563eb',
          backgroundColor: '#2563eb',
          pointBackgroundColor: '#2563eb',
          pointBorderColor: '#ffffff',
          pointRadius: 4,
          pointHoverRadius: 5,
          tension: 0.28,
          yAxisID: 'yPercent',
          order: 1,
        },
      ],
    }),
    buildChartOptions: (rows = []) => ({
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 38,
            minRotation: 38,
            font: {
              size: 11,
            },
          },
          title: {
            display: true,
            text: 'Burn-in step with related Performance test n° 1-9',
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Fail count',
          },
        },
        yPercent: {
          position: 'right',
          beginAtZero: true,
          min: 0,
          max: 100,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: (value) => `${value}%`,
          },
          title: {
            display: true,
            text: 'Cumulative %',
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return '';
              return formatBurnInStepLabel(row);
            },
            beforeBody: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return [];
              return [`Performance test n° ${row.performance_test_number || '-'}`];
            },
            afterBody: (items) => {
              const row = rows[items?.[0]?.dataIndex];
              if (!row) return [];
              return [
                row.performance_test_label || null,
                row.temperature_points ? `Temperature fails: ${row.temperature_points}` : null,
                row.measured_value_summary ? `Measured values: ${row.measured_value_summary}` : null,
              ].filter(Boolean);
            },
          },
        },
        paretoThresholdPlugin: {
          thresholdIndex: findParetoThresholdIndex(rows),
          thresholdValue: 80,
          label: '80% cutoff',
        },
      },
    }),
    tableColumns: [
      { key: 'performance_test_number', label: 'Perf. Test No.' },
      { key: 'step_number', label: 'Step No.' },
      { key: 'step_name', label: 'Step', render: (_value, row) => formatBurnInStepLabel(row) },
      { key: 'performance_test_label', label: 'Performance Test' },
      { key: 'fail_count', label: 'Fail Count', format: formatNumber },
      { key: 'total_count', label: 'Total Rows', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
      { key: 'temperature_points', label: '0.1 / Temperature Fails' },
      { key: 'measured_value_summary', label: 'Measured Values by Temperature' },
      { key: 'cumulative_pct', label: 'Cumulative %', format: (value) => `${formatDecimal(value)}%` },
    ],
  },
  '/burn-in-step-fail-rate': {
    eyebrow: 'Burn-in quality',
    title: 'Burn-In Step Fail Rate',
    description: 'This page highlights failure percentage rather than raw fail volume. For each step with enough samples, fail rate is calculated as failed rows divided by total rows for that step, which helps expose unstable steps that may be hidden in a basic Pareto chart.',
    endpoint: '/api/burn-in/step-fail-rate',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Qualified steps', value: formatNumber(rows.length) },
      { label: 'Highest fail rate', value: rows.length ? `${formatDecimal(rows[0].fail_rate)}%` : '0.00%' },
      { label: 'Top step fails', value: formatNumber(rows[0]?.fail_count || 0) },
      { label: 'Largest sample', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.total_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.step_name),
      datasets: [
        {
          label: 'Fail rate %',
          data: rows.map((row) => Number(row.fail_rate) || 0),
          backgroundColor: '#dc2626',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
      { key: 'fail_count', label: 'Fail Count', format: formatNumber },
      { key: 'total_count', label: 'Total Rows', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
    ],
  },
  '/burn-in-step-volume': {
    eyebrow: 'Burn-in flow',
    title: 'Burn-In Step Volume',
    description: 'This page shows how often each step appears in the imported burn-in flow. Total rows and fail rows are plotted together, so users can see whether a step is important because it happens everywhere, because it fails often, or both.',
    endpoint: '/api/burn-in/step-volume',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Top listed steps', value: formatNumber(rows.length) },
      { label: 'Largest volume', value: formatNumber(rows[0]?.total_count || 0) },
      { label: 'Highest fail count in view', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.fail_count) || 0))) },
      { label: 'Most sources hit', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.source_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.step_name),
      datasets: [
        {
          label: 'Total rows',
          data: rows.map((row) => Number(row.total_count) || 0),
          backgroundColor: '#0f766e',
          borderRadius: 6,
        },
        {
          label: 'Fail rows',
          data: rows.map((row) => Number(row.fail_count) || 0),
          backgroundColor: '#f59e0b',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'total_count', label: 'Total Rows', format: formatNumber },
      { key: 'fail_count', label: 'Fail Rows', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
      { key: 'avg_value', label: 'Avg Numeric Value', format: (value) => formatDecimal(value, 4) },
    ],
  },
  '/burn-in-control-chart': {
    eyebrow: 'Burn-in variation',
    title: 'Control Chart Analysis',
    supportsAdvancedFilters: true,
    description: 'This burn-in control chart lets you choose the numeric burn-in step to analyze, then plots the measured values in time or sequence order with calculated average and three-sigma control limits. It gives you a quick signal for drift, spread, and unstable numeric behavior inside the burn-in dataset.',
    endpoint: '/api/burn-in/control-chart',
    chartType: 'line',
    supportsControlChartSeriesSelection: true,
    supportsExpandedControlAnalysis: true,
    supportsSelectedFileCalculator: true,
    getRows: (data) => data?.points || [],
    getMetrics: (data) => {
      const meta = data?.meta || {};
      return [
        { label: 'Tracked step', value: meta.selected_step_name || '-' },
        { label: 'Units', value: meta.units || '-' },
        { label: 'Samples', value: formatNumber(meta.sample_count) },
        { label: 'Fail rows in series', value: formatNumber(meta.fail_count) },
      ];
    },
    buildChartData: (data) => {
      const rows = data?.points || [];
      return {
        labels: rows.map((row) => row.index_label),
        datasets: [
        {
          label: 'Measured value',
          data: rows.map((row) => Number(row.value_number) || 0),
          borderColor: '#0f766e',
          backgroundColor: 'rgba(15, 118, 110, 0.12)',
          tension: 0.22,
          fill: false,
        },
        {
          label: 'Average',
          data: rows.map((row) => Number(row.avg_value) || 0),
          borderColor: '#2563eb',
          borderDash: [8, 6],
          pointRadius: 0,
          tension: 0,
          fill: false,
        },
        {
          label: 'Upper control limit',
          data: rows.map((row) => Number(row.upper_control_limit) || 0),
          borderColor: '#ef4444',
          borderDash: [6, 6],
          pointRadius: 0,
          tension: 0,
          fill: false,
        },
        {
          label: 'Lower control limit',
          data: rows.map((row) => Number(row.lower_control_limit) || 0),
          borderColor: '#f59e0b',
          borderDash: [6, 6],
          pointRadius: 0,
          tension: 0,
            fill: false,
          },
        ],
      };
    },
    tableColumns: [
      {
        key: 'criticality_label',
        label: 'Criticality',
        render: (value, row) => (
          <span
            className={
              row.is_critical
                ? styles.criticalBadge
                : row.is_warning
                  ? styles.warningBadge
                  : styles.stableBadge
            }
          >
            {value}
          </span>
        ),
      },
      { key: 'criticality_reason', label: 'Reason' },
      { key: 'index_label', label: 'Order' },
      { key: 'measured_at_label', label: 'Measured At' },
      { key: 'source_label', label: 'Source File' },
      { key: 'sequence_text', label: 'Sequence' },
      { key: 'sequence_number_label', label: 'Sequence No.' },
      { key: 'row_index', label: 'Row', format: formatNumber },
      { key: 'status', label: 'Status' },
      { key: 'value_number', label: 'Value', format: (value) => formatDecimal(value, 4) },
      { key: 'delta_from_mean', label: 'Delta from CL', format: (value) => formatDecimal(value, 4) },
      { key: 'effective_lsl', label: 'LSL', format: (value) => formatDecimal(value, 4) },
      { key: 'effective_lcl', label: 'LCL', format: (value) => formatDecimal(value, 4) },
      { key: 'avg_value', label: 'CL', format: (value) => formatDecimal(value, 4) },
      { key: 'effective_ucl', label: 'UCL', format: (value) => formatDecimal(value, 4) },
      { key: 'effective_usl', label: 'USL', format: (value) => formatDecimal(value, 4) },
    ],
  },
  '/burn-in-uut-status-summary': {
    eyebrow: 'Burn-in units',
    title: 'UUT Status Summary',
    supportsAdvancedFilters: true,
    supportsUnitDisplayLimit: true,
    description: 'This burn-in UUT summary collapses the burn-in measurements into unit-level results. When the burn-in file contains a UUT serial number, that serial becomes the unit key; otherwise the analysis falls back to the source label so each burn-in source can still be tracked as its own unit context.',
    endpoint: '/api/burn-in/uut-status-summary',
    chartType: 'bar',
    getRows: (data) => data?.rows || [],
    getMetrics: (data) => {
      const rows = data?.rows || [];
      const totalUnits = Number(data?.meta?.total_units) || rows.length;
      const failedUnits = rows.filter((row) => row.overall_status === 'FAIL').length;
      const unitsWithoutFail = rows.filter((row) => row.overall_status !== 'FAIL').length;
      const unitsWithSerials = rows.filter((row) => Number(row.has_serial_number) === 1).length;
      return [
        { label: 'Units in view', value: totalUnits > rows.length ? `${formatNumber(rows.length)} / ${formatNumber(totalUnits)}` : formatNumber(rows.length) },
        { label: 'Total units in scope', value: formatNumber(totalUnits) },
        { label: 'Units without fail', value: formatNumber(unitsWithoutFail) },
        { label: 'Failed units', value: formatNumber(failedUnits) },
        { label: 'Units with serial number', value: formatNumber(unitsWithSerials) },
        { label: 'Worst fail rate', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => Number(row.fail_rate) || 0)))}%` : '0.00%' },
      ];
    },
    buildChartData: (data) => {
      const rows = data?.rows || [];
      return {
        labels: rows.map((row) => row.unit_key),
        datasets: [
        {
          label: 'Fail rows',
          data: rows.map((row) => Number(row.fail_count) || 0),
          backgroundColor: '#ef4444',
          borderRadius: 6,
        },
        {
          label: 'Pass rows',
          data: rows.map((row) => Number(row.pass_count) || 0),
          backgroundColor: '#0f766e',
          borderRadius: 6,
        },
        ],
      };
    },
    tableColumns: [
      { key: 'unit_key', label: 'Unit' },
      { key: 'uut_product_code', label: 'Product Code' },
      { key: 'overall_status', label: 'Overall Status' },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
      { key: 'fail_count', label: 'Fail Rows', format: formatNumber },
      { key: 'pass_count', label: 'Pass Rows', format: formatNumber },
      { key: 'other_count', label: 'Other Rows', format: formatNumber },
      { key: 'unique_step_count', label: 'Unique Steps', format: formatNumber },
    ],
  },
  '/burn-in-root-cause-analyzer': {
    eyebrow: 'Burn-in failures',
    title: 'Root Cause Analyzer',
    supportsAdvancedFilters: true,
    description: 'This burn-in root-cause view looks at the first failing step inside each burn-in source or unit context. By counting which step shows up first most often, it highlights the strongest candidate root causes in the burn-in flow instead of just the loudest downstream fail counts.',
    endpoint: '/api/burn-in/root-cause-analyzer',
    chartType: 'bar',
    getRows: (data) => data?.rows || [],
    getMetrics: (data) => {
      const rows = data?.rows || [];
      const failingContextCount = Number(data?.meta?.failing_context_count) || 0;
      const topShare = rows[0] && failingContextCount
        ? ((Number(rows[0].first_fail_context_count) || 0) * 100) / failingContextCount
        : 0;
      return [
        { label: 'Failing contexts', value: formatNumber(failingContextCount) },
        { label: 'Candidate first-fail steps', value: formatNumber(rows.length) },
        { label: 'Top root-cause share', value: `${formatDecimal(topShare)}%` },
        { label: 'Earliest recurring first fail', value: rows.length ? formatDecimal(Math.min(...rows.map((row) => Number(row.earliest_first_position) || 0)), 2) : '-' },
      ];
    },
    buildChartData: (data) => {
      const rows = data?.rows || [];
      return {
        labels: rows.map((row) => row.step_name),
        datasets: [
        {
          label: 'First-fail contexts',
          data: rows.map((row) => Number(row.first_fail_context_count) || 0),
          backgroundColor: '#7c3aed',
          borderRadius: 6,
        },
        ],
      };
    },
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'first_fail_context_count', label: 'First-Fail Contexts', format: formatNumber },
      { key: 'avg_first_position', label: 'Avg First Position', format: (value) => formatDecimal(value, 2) },
      { key: 'earliest_first_position', label: 'Earliest First Position', format: (value) => formatDecimal(value, 2) },
      { key: 'latest_first_position', label: 'Latest First Position', format: (value) => formatDecimal(value, 2) },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
      { key: 'upload_count', label: 'Uploads', format: formatNumber },
    ],
  },
  '/burn-in-timeline': {
    eyebrow: 'Burn-in pacing',
    title: 'Burn-In Timeline',
    description: 'This page trends burn-in activity by day. Rows are grouped by measured date when available, otherwise by import date, and the chart compares total traffic versus failed rows so users can spot spikes, quiet periods, and bad days.',
    endpoint: '/api/burn-in/timeline',
    chartType: 'line',
    getMetrics: (rows) => [
      { label: 'Days covered', value: formatNumber(rows.length) },
      { label: 'Peak daily rows', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.total_count) || 0))) },
      { label: 'Peak daily fails', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.fail_count) || 0))) },
      { label: 'Peak daily sources', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.source_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.day),
      datasets: [
        {
          label: 'Measurements',
          data: rows.map((row) => Number(row.total_count) || 0),
          borderColor: '#0f766e',
          backgroundColor: 'rgba(15, 118, 110, 0.12)',
          tension: 0.25,
          fill: true,
        },
        {
          label: 'Fails',
          data: rows.map((row) => Number(row.fail_count) || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.25,
          fill: true,
        },
      ],
    }),
    tableColumns: [
      { key: 'day', label: 'Day' },
      { key: 'total_count', label: 'Measurements', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
    ],
  },
  '/burn-in-hourly-activity': {
    eyebrow: 'Burn-in timing',
    title: 'Burn-In Hourly Activity',
    description: 'This page groups timestamped rows by hour of day using the stored measured time. It helps users see when activity is concentrated and whether failures cluster in specific hours, shifts, or operating windows.',
    endpoint: '/api/burn-in/hourly-activity',
    chartType: 'line',
    getMetrics: (rows) => [
      { label: 'Hours with traffic', value: formatNumber(rows.length) },
      { label: 'Peak hourly rows', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.total_count) || 0))) },
      { label: 'Peak hourly fails', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.fail_count) || 0))) },
      { label: 'Peak hourly sources', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.source_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => `${row.hour_bucket}:00`),
      datasets: [
        {
          label: 'Measurements',
          data: rows.map((row) => Number(row.total_count) || 0),
          borderColor: '#0891b2',
          backgroundColor: 'rgba(8, 145, 178, 0.12)',
          tension: 0.25,
          fill: true,
        },
        {
          label: 'Fails',
          data: rows.map((row) => Number(row.fail_count) || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.25,
          fill: true,
        },
      ],
    }),
    tableColumns: [
      { key: 'hour_bucket', label: 'Hour' },
      { key: 'total_count', label: 'Measurements', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
    ],
  },
  '/burn-in-source-health': {
    eyebrow: 'Burn-in sources',
    title: 'Burn-In Source Health',
    description: 'This page compares the embedded source files contained in the burn-in workbook. For each source label it calculates row count, fail count, fail rate, and unique step coverage, making it easier to identify weaker source files or subsets of the run.',
    endpoint: '/api/burn-in/source-health',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Source files in view', value: formatNumber(rows.length) },
      { label: 'Largest source volume', value: formatNumber(rows[0]?.total_count || 0) },
      { label: 'Highest fail rate', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => Number(row.fail_rate) || 0)))}%` : '0.00%' },
      { label: 'Widest step coverage', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.unique_step_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.source_label),
      datasets: [
        {
          label: 'Fail rate %',
          data: rows.map((row) => Number(row.fail_rate) || 0),
          backgroundColor: '#0891b2',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'source_label', label: 'Source File' },
      { key: 'total_count', label: 'Rows', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
      { key: 'unique_step_count', label: 'Unique Steps', format: formatNumber },
      { key: 'avg_numeric_value', label: 'Avg Numeric Value', format: (value) => formatDecimal(value, 4) },
    ],
  },
  '/burn-in-upload-comparison': {
    eyebrow: 'Burn-in imports',
    title: 'Burn-In Upload Comparison',
    description: 'This page compares imported burn-in workbooks against each other. The metrics come from the upload-level summary plus distinct step and unit counts, so users can see which uploads are larger, riskier, or richer for analysis.',
    endpoint: '/api/burn-in/upload-comparison',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Uploads compared', value: formatNumber(rows.length) },
      { label: 'Largest upload', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.parsed_rows) || 0))) },
      { label: 'Highest upload fail rate', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => Number(row.fail_rate) || 0)))}%` : '0.00%' },
      { label: 'Most unique steps', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.unique_step_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.original_filename),
      datasets: [
        {
          label: 'Fail rate %',
          data: rows.map((row) => Number(row.fail_rate) || 0),
          backgroundColor: '#14b8a6',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'original_filename', label: 'Upload' },
      { key: 'parsed_rows', label: 'Parsed Rows', format: formatNumber },
      { key: 'source_file_count', label: 'Source Files', format: formatNumber },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
      { key: 'unique_step_count', label: 'Unique Steps', format: formatNumber },
      { key: 'unit_group_count', label: 'Unit Groups', format: formatNumber },
    ],
  },
  '/burn-in-units': {
    eyebrow: 'Burn-in measurements',
    title: 'Burn-In Units Breakdown',
    description: 'This page breaks the dataset into measurement families using the Units column. It counts total rows, numeric rows, and unique steps per unit group, helping users understand whether the dataset is mostly numeric instrumentation, logical checks, or mixed content.',
    endpoint: '/api/burn-in/units-breakdown',
    chartType: 'doughnut',
    getMetrics: (rows) => [
      { label: 'Unit groups', value: formatNumber(rows.length) },
      { label: 'Largest unit bucket', value: formatNumber(rows[0]?.total_count || 0) },
      { label: 'Numeric rows in top bucket', value: formatNumber(rows[0]?.numeric_count || 0) },
      { label: 'Broadest unit step coverage', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.unique_step_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.units),
      datasets: [
        {
          label: 'Rows',
          data: rows.map((row) => Number(row.total_count) || 0),
          backgroundColor: palette,
          borderWidth: 0,
        },
      ],
    }),
    tableColumns: [
      { key: 'units', label: 'Units' },
      { key: 'total_count', label: 'Rows', format: formatNumber },
      { key: 'numeric_count', label: 'Numeric Rows', format: formatNumber },
      { key: 'unique_step_count', label: 'Unique Steps', format: formatNumber },
    ],
  },
  '/burn-in-data-completeness': {
    eyebrow: 'Burn-in data quality',
    title: 'Burn-In Data Completeness',
    description: 'This page is a completeness audit. It checks key fields such as timestamps, units, numeric values, limits, and reference values, then counts how many rows are populated versus missing, so users know how trustworthy later analyses will be.',
    endpoint: '/api/burn-in/data-completeness',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Tracked fields', value: formatNumber(rows.length) },
      { label: 'Best field coverage', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => ((Number(row.present_count) || 0) * 100) / Math.max((Number(row.present_count) || 0) + (Number(row.missing_count) || 0), 1))))}%` : '0.00%' },
      { label: 'Weakest field coverage', value: rows.length ? `${formatDecimal(Math.min(...rows.map((row) => ((Number(row.present_count) || 0) * 100) / Math.max((Number(row.present_count) || 0) + (Number(row.missing_count) || 0), 1))))}%` : '0.00%' },
      { label: 'Most missing rows', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.missing_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.field_name),
      datasets: [
        {
          label: 'Present',
          data: rows.map((row) => Number(row.present_count) || 0),
          backgroundColor: '#0f766e',
          borderRadius: 6,
        },
        {
          label: 'Missing',
          data: rows.map((row) => Number(row.missing_count) || 0),
          backgroundColor: '#f97316',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'field_name', label: 'Field' },
      { key: 'present_count', label: 'Present', format: formatNumber },
      { key: 'missing_count', label: 'Missing', format: formatNumber },
    ],
  },
  '/burn-in-sequence-hotspots': {
    eyebrow: 'Burn-in order',
    title: 'Burn-In Sequence Hotspots',
    description: 'This page ranks individual sequence numbers by fail count and fail rate. Rows are grouped by exact sequence text, so users can isolate which position in the burn-in flow is most failure-prone, even when the step names repeat across files.',
    endpoint: '/api/burn-in/sequence-hotspots',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Failing sequence slots', value: formatNumber(rows.length) },
      { label: 'Top slot fails', value: formatNumber(rows[0]?.fail_count || 0) },
      { label: 'Top slot fail rate', value: rows[0] ? `${formatDecimal(rows[0].fail_rate)}%` : '0.00%' },
      { label: 'Largest slot traffic', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.total_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.sequence_text),
      datasets: [
        {
          label: 'Fail count',
          data: rows.map((row) => Number(row.fail_count) || 0),
          backgroundColor: '#e11d48',
          borderRadius: 6,
        },
        {
          label: 'Fail rate %',
          data: rows.map((row) => Number(row.fail_rate) || 0),
          backgroundColor: '#f59e0b',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'sequence_text', label: 'Sequence' },
      { key: 'step_name', label: 'Example Step' },
      { key: 'total_count', label: 'Rows', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
    ],
  },
  '/burn-in-sequence-bands': {
    eyebrow: 'Burn-in order',
    title: 'Burn-In Sequence Bands',
    description: 'This page groups sequence numbers into 100-step bands to show broader flow phases instead of single points. For each band it calculates total rows, fail rows, fail rate, and unique step coverage, which helps reveal whether early, middle, or late burn-in stages are weaker.',
    endpoint: '/api/burn-in/sequence-bands',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Bands in view', value: formatNumber(rows.length) },
      { label: 'Highest band fail rate', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => Number(row.fail_rate) || 0)))}%` : '0.00%' },
      { label: 'Peak band volume', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.total_count) || 0))) },
      { label: 'Broadest band coverage', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.unique_step_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.sequence_band),
      datasets: [
        {
          label: 'Fail rate %',
          data: rows.map((row) => Number(row.fail_rate) || 0),
          backgroundColor: '#f59e0b',
          borderRadius: 6,
        },
        {
          label: 'Total rows',
          data: rows.map((row) => Number(row.total_count) || 0),
          backgroundColor: '#2563eb',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'sequence_band', label: 'Sequence Band' },
      { key: 'total_count', label: 'Rows', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'fail_rate', label: 'Fail Rate %', format: (value) => formatDecimal(value) },
      { key: 'unique_step_count', label: 'Unique Steps', format: formatNumber },
    ],
  },
  '/burn-in-value-spread': {
    eyebrow: 'Burn-in variation',
    title: 'Burn-In Numeric Spread',
    description: 'This page focuses on numeric variation. For each step and unit pair with enough numeric rows, it calculates min, max, average, and spread, so users can quickly find steps whose measurements swing the most across the selected burn-in scope.',
    endpoint: '/api/burn-in/value-spread',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Numeric steps ranked', value: formatNumber(rows.length) },
      { label: 'Widest spread', value: formatDecimal(rows[0]?.spread_value || 0, 4) },
      { label: 'Largest numeric sample', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.numeric_count) || 0))) },
      { label: 'Highest max value', value: formatDecimal(Math.max(0, ...rows.map((row) => Number(row.max_value) || 0)), 4) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.step_name),
      datasets: [
        {
          label: 'Spread',
          data: rows.map((row) => Number(row.spread_value) || 0),
          backgroundColor: '#7c3aed',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'units', label: 'Units' },
      { key: 'numeric_count', label: 'Numeric Rows', format: formatNumber },
      { key: 'min_value', label: 'Min', format: (value) => formatDecimal(value, 4) },
      { key: 'avg_value', label: 'Avg', format: (value) => formatDecimal(value, 4) },
      { key: 'max_value', label: 'Max', format: (value) => formatDecimal(value, 4) },
      { key: 'spread_value', label: 'Spread', format: (value) => formatDecimal(value, 4) },
    ],
  },
  '/burn-in-limit-compliance': {
    eyebrow: 'Burn-in specifications',
    title: 'Burn-In Limit Compliance',
    description: 'This page checks measured numeric values against parsed low and high limits from the file. It counts how often a value falls outside those boundaries, independent of the stored pass/fail label, so users can validate limit behavior directly from the raw data.',
    endpoint: '/api/burn-in/limit-compliance',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Comparable steps', value: formatNumber(rows.length) },
      { label: 'Highest out-of-spec count', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.out_of_spec_count) || 0))) },
      { label: 'Highest out-of-spec rate', value: rows.length ? `${formatDecimal(Math.max(...rows.map((row) => Number(row.out_of_spec_rate) || 0)))}%` : '0.00%' },
      { label: 'Largest comparable sample', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.comparable_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.step_name),
      datasets: [
        {
          label: 'Out-of-spec rows',
          data: rows.map((row) => Number(row.out_of_spec_count) || 0),
          backgroundColor: '#b91c1c',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'comparable_count', label: 'Comparable Rows', format: formatNumber },
      { key: 'out_of_spec_count', label: 'Out of Spec', format: formatNumber },
      { key: 'out_of_spec_rate', label: 'Out-of-Spec %', format: (value) => formatDecimal(value) },
      { key: 'avg_limit_low', label: 'Avg Low', format: (value) => formatDecimal(value, 4) },
      { key: 'avg_limit_high', label: 'Avg High', format: (value) => formatDecimal(value, 4) },
    ],
  },
  '/burn-in-near-limit-risk': {
    eyebrow: 'Burn-in specifications',
    title: 'Burn-In Near-Limit Risk',
    description: 'This page finds values that are still inside limits but too close for comfort. Margin is calculated as the percentage distance to the nearest limit within the allowed range, so lower margin means higher risk even if the row is technically still in spec.',
    endpoint: '/api/burn-in/near-limit-risk',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Comparable steps', value: formatNumber(rows.length) },
      { label: 'Tightest avg margin', value: rows.length ? `${formatDecimal(Math.min(...rows.map((row) => Number(row.avg_margin_pct) || 0)))}%` : '0.00%' },
      { label: 'Worst single margin', value: rows.length ? `${formatDecimal(Math.min(...rows.map((row) => Number(row.min_margin_pct) || 0)))}%` : '0.00%' },
      { label: 'Most near-limit rows', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.near_limit_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.step_name),
      datasets: [
        {
          label: 'Average margin % to nearest limit',
          data: rows.map((row) => Number(row.avg_margin_pct) || 0),
          backgroundColor: '#7c3aed',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'step_name', label: 'Step' },
      { key: 'comparable_count', label: 'Comparable Rows', format: formatNumber },
      { key: 'avg_margin_pct', label: 'Avg Margin %', format: (value) => formatDecimal(value) },
      { key: 'min_margin_pct', label: 'Min Margin %', format: (value) => formatDecimal(value) },
      { key: 'near_limit_count', label: 'Near-Limit Rows', format: formatNumber },
    ],
  },
  '/burn-in-categorical-values': {
    eyebrow: 'Burn-in values',
    title: 'Burn-In Categorical Values',
    description: 'This page summarizes the most common non-numeric value patterns such as OK states, hex codes, addresses, and discrete flags. It groups rows by value text and shows how many steps and source files each value appears in, helping users understand the logical side of the dataset.',
    endpoint: '/api/burn-in/categorical-values',
    chartType: 'bar',
    getMetrics: (rows) => [
      { label: 'Value patterns ranked', value: formatNumber(rows.length) },
      { label: 'Most common value count', value: formatNumber(rows[0]?.total_count || 0) },
      { label: 'Most common value fail count', value: formatNumber(rows[0]?.fail_count || 0) },
      { label: 'Broadest step coverage', value: formatNumber(Math.max(0, ...rows.map((row) => Number(row.step_count) || 0))) },
    ],
    buildChartData: (rows) => ({
      labels: rows.map((row) => row.value_text),
      datasets: [
        {
          label: 'Occurrences',
          data: rows.map((row) => Number(row.total_count) || 0),
          backgroundColor: '#0ea5e9',
          borderRadius: 6,
        },
      ],
    }),
    tableColumns: [
      { key: 'value_text', label: 'Value Text' },
      { key: 'total_count', label: 'Rows', format: formatNumber },
      { key: 'fail_count', label: 'Fails', format: formatNumber },
      { key: 'step_count', label: 'Steps', format: formatNumber },
      { key: 'source_count', label: 'Source Files', format: formatNumber },
    ],
  },
};

function BurnInAnalyticsPage({ configPath = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedPath = configPath || location.pathname;
  const config = burnInConfigs[resolvedPath] || burnInConfigs['/burn-in-status'];
  const chartWrapRef = useRef(null);
  const burnInPassOnlyChartRef = useRef(null);
  const burnInPassFailChartRef = useRef(null);
  const burnInNormalChartRef = useRef(null);
  const burnInMrChartRef = useRef(null);
  const burnInPassOnlyExportRef = useRef(null);
  const burnInPassFailExportRef = useRef(null);
  const burnInNormalExportRef = useRef(null);
  const burnInMrExportRef = useRef(null);

  const [uploads, setUploads] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState('');
  const [unitDisplayLimit, setUnitDisplayLimit] = useState('20');
  const [filters, setFilters] = useState(buildDefaultBurnInFilters);
  const [pendingPerformanceTestNumber, setPendingPerformanceTestNumber] = useState('');
  const [filterOptions, setFilterOptions] = useState({});
  const [controlChartSeriesOptions, setControlChartSeriesOptions] = useState([]);
  const [selectedControlChartSeries, setSelectedControlChartSeries] = useState('');
  const [manualControlLimits, setManualControlLimits] = useState({ upper: '', lower: '' });
  const [manualSpecLimits, setManualSpecLimits] = useState({ upper: '', lower: '' });
  const [overview, setOverview] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [expandedControlChartKey, setExpandedControlChartKey] = useState('');
  const [isDetailsTableExpanded, setIsDetailsTableExpanded] = useState(false);
  const [tableSort, setTableSort] = useState({ key: 'criticality_label', direction: 'desc' });
  const [chartModalSize, setChartModalSize] = useState('large');
  const [openControlChartDetails, setOpenControlChartDetails] = useState({});
  const [includeControlChartDetailsInExport, setIncludeControlChartDetailsInExport] = useState({});
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
  const [calculatorFiles, setCalculatorFiles] = useState([]);
  const [selectedCalculatorFileKeys, setSelectedCalculatorFileKeys] = useState([]);
  const [calculatorSortOrder, setCalculatorSortOrder] = useState('newest');
  const [calculatorSearch, setCalculatorSearch] = useState('');
  const [calculatorStatusFilter, setCalculatorStatusFilter] = useState('');
  const [calculatorVersionFilter, setCalculatorVersionFilter] = useState('');
  const [calculatorSheetFilter, setCalculatorSheetFilter] = useState('');
  const [calculatorDateFrom, setCalculatorDateFrom] = useState('');
  const [calculatorDateTo, setCalculatorDateTo] = useState('');
  const [calculatorError, setCalculatorError] = useState('');

  useEffect(() => {
    const loadUploads = async () => {
      try {
        const uploadRows = await apiGet('/api/burn-in/uploads');
        setUploads(uploadRows || []);
      } catch (loadError) {
        console.error('Failed to load burn-in uploads:', loadError);
      }
    };

    loadUploads();
  }, []);

  useEffect(() => {
    if (!config.supportsAdvancedFilters) return undefined;

    let cancelled = false;
    const loadFilterOptions = async () => {
      try {
        const params = selectedUploadId ? { uploadId: selectedUploadId } : {};
        const options = await apiGet('/api/burn-in/filter-options', params);
        if (!cancelled) {
          setFilterOptions(options || {});
        }
      } catch (loadError) {
        console.error('Failed to load burn-in filter options:', loadError);
        if (!cancelled) setFilterOptions({});
      }
    };

    loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [config.supportsAdvancedFilters, selectedUploadId]);

  useEffect(() => {
    const options = Array.isArray(filterOptions.performanceTestNumber) ? filterOptions.performanceTestNumber : [];
    const selected = Array.isArray(filters.performanceTestNumber) ? filters.performanceTestNumber : [];
    if (pendingPerformanceTestNumber && options.includes(pendingPerformanceTestNumber) && !selected.includes(pendingPerformanceTestNumber)) {
      return;
    }
    setPendingPerformanceTestNumber(getFirstUnselectedFilterOption(options, selected));
  }, [filterOptions.performanceTestNumber, filters.performanceTestNumber, pendingPerformanceTestNumber]);

  useEffect(() => {
    if (!config.supportsControlChartSeriesSelection) {
      setControlChartSeriesOptions([]);
      setSelectedControlChartSeries('');
      return undefined;
    }

    let cancelled = false;
    const loadSeriesOptions = async () => {
      try {
        const params = selectedUploadId ? { uploadId: selectedUploadId } : {};
        Object.entries(filters).forEach(([key, value]) => {
          const normalized = stringifyFilterValue(value);
          if (normalized) params[key] = normalized;
        });
        const seriesRows = await apiGet('/api/burn-in/control-chart-series', params);
        if (cancelled) return;
        setControlChartSeriesOptions(seriesRows || []);
        setSelectedControlChartSeries((current) => {
          if (current && (seriesRows || []).some((row) => row.series_key === current)) return current;
          return seriesRows?.[0]?.series_key || '';
        });
      } catch (loadError) {
        console.error('Failed to load burn-in control chart series:', loadError);
        if (!cancelled) {
          setControlChartSeriesOptions([]);
          setSelectedControlChartSeries('');
        }
      }
    };

    loadSeriesOptions();
    return () => {
      cancelled = true;
    };
  }, [config.supportsControlChartSeriesSelection, filters, selectedUploadId]);

  const pageParams = useMemo(() => {
    const params = selectedUploadId ? { uploadId: selectedUploadId } : {};
    Object.entries(filters).forEach(([key, value]) => {
      const normalized = stringifyFilterValue(value);
      if (normalized) params[key] = normalized;
    });
    if (config.supportsControlChartSeriesSelection && selectedControlChartSeries) {
      const parsedSeries = parseControlChartSeriesKey(selectedControlChartSeries);
      if (parsedSeries.stepName) params.controlChartStepName = parsedSeries.stepName;
      if (parsedSeries.units) params.controlChartUnits = parsedSeries.units;
    }
    if (config.supportsUnitDisplayLimit && unitDisplayLimit) {
      params.unitLimit = unitDisplayLimit;
    }
    return params;
  }, [config.supportsControlChartSeriesSelection, config.supportsUnitDisplayLimit, filters, selectedControlChartSeries, selectedUploadId, unitDisplayLimit]);

  const calculatorStatuses = useMemo(
    () => [...new Set(calculatorFiles.map((file) => String(file.overall_status || '').trim()).filter(Boolean))].sort(),
    [calculatorFiles]
  );
  const calculatorVersions = useMemo(
    () => [...new Set(calculatorFiles.map((file) => String(file.version_sw || '').trim()).filter(Boolean))].sort(),
    [calculatorFiles]
  );
  const calculatorSheets = useMemo(
    () => [...new Set(calculatorFiles.map((file) => String(file.sheet_name || '').trim()).filter(Boolean))].sort(),
    [calculatorFiles]
  );
  const filteredCalculatorFiles = useMemo(() => {
    const searchTerm = calculatorSearch.trim().toLowerCase();
    const filtered = calculatorFiles.filter((file) => {
      const haystack = [
        file.source_file,
        file.unit_serial,
        file.workbook_filename,
        file.version_sw,
        file.sheet_name,
        file.overall_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const searchMatches = !searchTerm || haystack.includes(searchTerm);
      const statusMatches = !calculatorStatusFilter || String(file.overall_status || '') === calculatorStatusFilter;
      const versionMatches = !calculatorVersionFilter || String(file.version_sw || '') === calculatorVersionFilter;
      const sheetMatches = !calculatorSheetFilter || String(file.sheet_name || '') === calculatorSheetFilter;
      const fileDate = String(file.first_measured_at || '').slice(0, 10).replace(/\//g, '-');
      const dateFromMatches = !calculatorDateFrom || (fileDate && fileDate >= calculatorDateFrom);
      const dateToMatches = !calculatorDateTo || (fileDate && fileDate <= calculatorDateTo);
      return searchMatches && statusMatches && versionMatches && sheetMatches && dateFromMatches && dateToMatches;
    });
    filtered.sort((left, right) => {
      const leftDate = new Date(left.first_measured_at || left.last_measured_at || 0).getTime() || 0;
      const rightDate = new Date(right.first_measured_at || right.last_measured_at || 0).getTime() || 0;
      return calculatorSortOrder === 'oldest' ? leftDate - rightDate : rightDate - leftDate;
    });
    return filtered;
  }, [
    calculatorDateFrom,
    calculatorDateTo,
    calculatorFiles,
    calculatorSearch,
    calculatorSheetFilter,
    calculatorSortOrder,
    calculatorStatusFilter,
    calculatorVersionFilter,
  ]);

  useEffect(() => {
    if (!config.supportsSelectedFileCalculator || !isCalculatorModalOpen) return undefined;
    let cancelled = false;

    const loadCalculatorFiles = async () => {
      try {
        const files = await apiGet('/api/burn-in/calculator-files', pageParams);
        if (!cancelled) {
          setCalculatorFiles(files || []);
        }
      } catch (loadError) {
        console.error('Failed to load burn-in calculator files:', loadError);
        if (!cancelled) {
          setCalculatorFiles([]);
          setCalculatorError('Failed to load burn-in source files for the calculator.');
        }
      }
    };

    loadCalculatorFiles();
    return () => {
      cancelled = true;
    };
  }, [config.supportsSelectedFileCalculator, isCalculatorModalOpen, pageParams]);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError('');

      try {
        const [overviewResponse, pageResponse] = await Promise.all([
          apiGet('/api/burn-in/status-overview', pageParams),
          apiGet(config.endpoint, pageParams),
        ]);

        setOverview(overviewResponse);
        setPageData(pageResponse);
      } catch (loadError) {
        console.error('Failed to load burn-in analytics page:', loadError);
        setError('Failed to load burn-in analytics. Please try again.');
        setOverview(null);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [config.endpoint, pageParams]);

  const rows = useMemo(() => {
    if (typeof config.getRows === 'function') return config.getRows(pageData);
    return Array.isArray(pageData) ? pageData : [];
  }, [config, pageData]);

  const chartInput = useMemo(() => (
    Array.isArray(pageData) ? rows : (pageData || rows)
  ), [pageData, rows]);

  const metrics = useMemo(() => {
    if (typeof config.getMetrics === 'function') return config.getMetrics(pageData || rows, overview);
    return [];
  }, [config, overview, pageData, rows]);

  const chartData = useMemo(() => {
    const baseChartData = config.buildChartData(chartInput, overview);

    if (!config.supportsControlChartSeriesSelection) return baseChartData;
    const criticalRows = annotateBurnInControlRows(rows, {
      lowerControlLimit: manualControlLimits.lower !== '' ? manualControlLimits.lower : (pageData?.meta?.lower_control_limit ?? ''),
      upperControlLimit: manualControlLimits.upper !== '' ? manualControlLimits.upper : (pageData?.meta?.upper_control_limit ?? ''),
      lowerSpecLimit: manualSpecLimits.lower !== '' ? manualSpecLimits.lower : (pageData?.meta?.lower_spec_limit ?? ''),
      upperSpecLimit: manualSpecLimits.upper !== '' ? manualSpecLimits.upper : (pageData?.meta?.upper_spec_limit ?? ''),
    });

    const meta = pageData?.meta || {};
    const upperOverride = Number(manualControlLimits.upper);
    const lowerOverride = Number(manualControlLimits.lower);
    const hasUpperOverride = manualControlLimits.upper !== '' && Number.isFinite(upperOverride);
    const hasLowerOverride = manualControlLimits.lower !== '' && Number.isFinite(lowerOverride);
    const upperSpecOverride = Number(manualSpecLimits.upper);
    const lowerSpecOverride = Number(manualSpecLimits.lower);
    const resolvedUpperSpec = manualSpecLimits.upper !== '' && Number.isFinite(upperSpecOverride)
      ? upperSpecOverride
      : Number(meta.upper_spec_limit);
    const resolvedLowerSpec = manualSpecLimits.lower !== '' && Number.isFinite(lowerSpecOverride)
      ? lowerSpecOverride
      : Number(meta.lower_spec_limit);

    let nextChartData = baseChartData;

    if (hasUpperOverride || hasLowerOverride) {
      nextChartData = {
        ...baseChartData,
        datasets: (baseChartData.datasets || []).map((dataset) => {
          if (dataset.label === 'Upper control limit' && hasUpperOverride) {
            return {
              ...dataset,
              data: (dataset.data || []).map(() => upperOverride),
            };
          }
          if (dataset.label === 'Lower control limit' && hasLowerOverride) {
            return {
              ...dataset,
              data: (dataset.data || []).map(() => lowerOverride),
            };
          }
          return dataset;
        }),
      };
    }

    const specDatasets = [];
    const labelCount = Array.isArray(nextChartData.labels) ? nextChartData.labels.length : 0;
    if (Number.isFinite(resolvedLowerSpec)) {
      specDatasets.push({
        label: 'Lower spec limit',
        data: Array(labelCount).fill(resolvedLowerSpec),
        borderColor: '#7c3aed',
        borderDash: [10, 6],
        pointRadius: 0,
        tension: 0,
        fill: false,
      });
    }
    if (Number.isFinite(resolvedUpperSpec)) {
      specDatasets.push({
        label: 'Upper spec limit',
        data: Array(labelCount).fill(resolvedUpperSpec),
        borderColor: '#8b5cf6',
        borderDash: [10, 6],
        pointRadius: 0,
        tension: 0,
        fill: false,
      });
    }
    if (specDatasets.length) {
      nextChartData = {
        ...nextChartData,
        datasets: [...(nextChartData.datasets || []), ...specDatasets],
      };
    }

    const criticalDataset = {
      label: 'Critical points',
      data: criticalRows.map((row) => (row.is_critical ? (Number(row.value_number) || 0) : null)),
      borderColor: 'transparent',
      backgroundColor: criticalRows.map((row) => (
        row.is_out_of_spec
          ? '#dc2626'
          : row.is_out_of_control
            ? '#f97316'
            : 'transparent'
      )),
      pointBorderColor: criticalRows.map((row) => (
        row.is_out_of_spec
          ? '#991b1b'
          : row.is_out_of_control
            ? '#9a3412'
            : 'transparent'
      )),
      pointRadius: criticalRows.map((row) => (row.is_critical ? 5 : 0)),
      pointHoverRadius: criticalRows.map((row) => (row.is_critical ? 7 : 0)),
      pointBorderWidth: criticalRows.map((row) => (row.is_critical ? 2 : 0)),
      showLine: false,
      order: -1,
    };

    return {
      ...nextChartData,
      datasets: [...(nextChartData.datasets || []), criticalDataset],
    };
  }, [chartInput, config, manualControlLimits.lower, manualControlLimits.upper, manualSpecLimits.lower, manualSpecLimits.upper, overview, pageData, rows]);
  const chartOptions = useMemo(() => {
    const customOptions = typeof config.buildChartOptions === 'function'
      ? config.buildChartOptions(chartInput, overview)
      : {};
    const customPlugins = customOptions.plugins || {};

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: ['doughnut', 'pie'].includes(config.chartType) ? 'bottom' : 'top',
        },
        ...customPlugins,
      },
      ...customOptions,
    };
  }, [chartInput, config, overview]);

  const totals = overview?.totals || {};
  const pageTotal = Number(totals.total_measurements) || 0;
  const pageFailed = Number(totals.failed_measurements) || 0;
  const exportBaseName = useMemo(() => slugifyFileName(config.title), [config.title]);
  const activeAdvancedFilterCount = useMemo(
    () => Object.values(filters).filter((value) => stringifyFilterValue(value)).length,
    [filters]
  );

  const ChartComponent = config.chartType === 'line'
    ? Line
    : config.chartType === 'doughnut'
      ? Doughnut
      : config.chartType === 'pie'
        ? Pie
        : Bar;
  const controlChartMeta = config.supportsControlChartSeriesSelection ? (pageData?.meta || {}) : null;
  const resolvedUpperControlLimit = manualControlLimits.upper !== '' ? manualControlLimits.upper : (controlChartMeta?.upper_control_limit ?? '');
  const resolvedLowerControlLimit = manualControlLimits.lower !== '' ? manualControlLimits.lower : (controlChartMeta?.lower_control_limit ?? '');
  const resolvedUpperSpecLimit = manualSpecLimits.upper !== '' ? manualSpecLimits.upper : (controlChartMeta?.upper_spec_limit ?? '');
  const resolvedLowerSpecLimit = manualSpecLimits.lower !== '' ? manualSpecLimits.lower : (controlChartMeta?.lower_spec_limit ?? '');
  const annotatedControlChartRows = useMemo(() => {
    if (!config.supportsControlChartSeriesSelection) return rows;
    return annotateBurnInControlRows(rows, {
      lowerControlLimit: resolvedLowerControlLimit,
      upperControlLimit: resolvedUpperControlLimit,
      lowerSpecLimit: resolvedLowerSpecLimit,
      upperSpecLimit: resolvedUpperSpecLimit,
    });
  }, [
    config.supportsControlChartSeriesSelection,
    resolvedLowerControlLimit,
    resolvedLowerSpecLimit,
    resolvedUpperControlLimit,
    resolvedUpperSpecLimit,
    rows,
  ]);
  const displayRowsBase = config.supportsControlChartSeriesSelection ? annotatedControlChartRows : rows;
  const displayTableColumns = config.tableColumns;
  const displayRows = useMemo(() => {
    const sortableRows = [...displayRowsBase];
    if (!tableSort?.key) return sortableRows;

    const criticalityRank = {
      Critical: 3,
      Warning: 2,
      Stable: 1,
    };

    sortableRows.sort((left, right) => {
      let leftValue = left?.[tableSort.key];
      let rightValue = right?.[tableSort.key];

      if (tableSort.key === 'criticality_label') {
        leftValue = criticalityRank[leftValue] ?? 0;
        rightValue = criticalityRank[rightValue] ?? 0;
      }

      const leftNumber = Number(leftValue);
      const rightNumber = Number(rightValue);
      let comparison = 0;

      if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftValue !== '' && rightValue !== '') {
        comparison = leftNumber - rightNumber;
      } else {
        comparison = String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      }

      if (comparison === 0) {
        comparison = String(left.index_label ?? '').localeCompare(String(right.index_label ?? ''), undefined, { numeric: true, sensitivity: 'base' });
      }

      return tableSort.direction === 'asc' ? comparison : -comparison;
    });

    return sortableRows;
  }, [displayRowsBase, tableSort]);
  const controlChartPassOnlyStats = useMemo(() => {
    if (!config.supportsExpandedControlAnalysis) return null;
    const passOnlyPoints = annotatedControlChartRows.filter((point) => isBurnInPassStatus(point.status));
    const upperOverride = Number(manualControlLimits.upper);
    const lowerOverride = Number(manualControlLimits.lower);
    return computeControlSeriesStats(passOnlyPoints, {
      upper: manualControlLimits.upper !== '' && Number.isFinite(upperOverride) ? upperOverride : undefined,
      lower: manualControlLimits.lower !== '' && Number.isFinite(lowerOverride) ? lowerOverride : undefined,
    });
  }, [annotatedControlChartRows, config.supportsExpandedControlAnalysis, manualControlLimits.lower, manualControlLimits.upper]);
  const controlChartAllStats = useMemo(() => {
    if (!config.supportsExpandedControlAnalysis) return null;
    const upperOverride = Number(manualControlLimits.upper);
    const lowerOverride = Number(manualControlLimits.lower);
    return computeControlSeriesStats(annotatedControlChartRows, {
      upper: manualControlLimits.upper !== '' && Number.isFinite(upperOverride) ? upperOverride : undefined,
      lower: manualControlLimits.lower !== '' && Number.isFinite(lowerOverride) ? lowerOverride : undefined,
    });
  }, [annotatedControlChartRows, config.supportsExpandedControlAnalysis, manualControlLimits.lower, manualControlLimits.upper]);
  const burnInCapabilityPassOnly = useMemo(
    () => calculateCapabilityMetrics({
      values: controlChartPassOnlyStats?.values || [],
      lsl: resolvedLowerSpecLimit,
      usl: resolvedUpperSpecLimit,
    }),
    [controlChartPassOnlyStats?.values, resolvedLowerSpecLimit, resolvedUpperSpecLimit]
  );
  const burnInCapabilityAll = useMemo(
    () => calculateCapabilityMetrics({
      values: controlChartAllStats?.values || [],
      lsl: resolvedLowerSpecLimit,
      usl: resolvedUpperSpecLimit,
    }),
    [controlChartAllStats?.values, resolvedLowerSpecLimit, resolvedUpperSpecLimit]
  );
  const burnInCapabilityPassOnlyCards = useMemo(
    () => buildCapabilityMetricCards(burnInCapabilityPassOnly),
    [burnInCapabilityPassOnly]
  );
  const burnInCapabilityAllCards = useMemo(
    () => buildCapabilityMetricCards(burnInCapabilityAll),
    [burnInCapabilityAll]
  );
  const burnInPassOnlyChartData = useMemo(() => {
    if (!controlChartPassOnlyStats) return null;
    const passOnlyRows = annotatedControlChartRows.filter((point) => isBurnInPassStatus(point.status));
    const datasets = [
      {
        label: 'Individual values',
        data: controlChartPassOnlyStats.values,
        borderColor: '#2563eb',
        backgroundColor: '#2563eb',
        pointRadius: 3,
        fill: false,
      },
      {
        label: 'CL',
        data: controlChartPassOnlyStats.values.map(() => controlChartPassOnlyStats.meanValue),
        borderColor: '#16a34a',
        borderDash: [5, 5],
        pointRadius: 0,
      },
      {
        label: 'UCL',
        data: controlChartPassOnlyStats.values.map(() => controlChartPassOnlyStats.upperControlLimit),
        borderColor: '#ef4444',
        borderDash: [5, 5],
        pointRadius: 0,
      },
      {
        label: 'LCL',
        data: controlChartPassOnlyStats.values.map(() => controlChartPassOnlyStats.lowerControlLimit),
        borderColor: '#f59e0b',
        borderDash: [5, 5],
        pointRadius: 0,
      },
    ];
    if (Number.isFinite(Number(resolvedLowerSpecLimit))) {
      datasets.push({
        label: 'LSL',
        data: controlChartPassOnlyStats.values.map(() => Number(resolvedLowerSpecLimit)),
        borderColor: '#7c3aed',
        borderDash: [10, 6],
        pointRadius: 0,
      });
    }
    if (Number.isFinite(Number(resolvedUpperSpecLimit))) {
      datasets.push({
        label: 'USL',
        data: controlChartPassOnlyStats.values.map(() => Number(resolvedUpperSpecLimit)),
        borderColor: '#8b5cf6',
        borderDash: [10, 6],
        pointRadius: 0,
      });
    }
    datasets.push({
      label: 'Critical points',
      data: passOnlyRows.map((row) => (row.is_critical ? (Number(row.value_number) || 0) : null)),
      borderColor: 'transparent',
      backgroundColor: passOnlyRows.map((row) => (
        row.is_out_of_spec
          ? '#dc2626'
          : row.is_out_of_control
            ? '#f97316'
            : 'transparent'
      )),
      pointBorderColor: passOnlyRows.map((row) => (
        row.is_out_of_spec
          ? '#991b1b'
          : row.is_out_of_control
            ? '#9a3412'
            : 'transparent'
      )),
      pointRadius: passOnlyRows.map((row) => (row.is_critical ? 5 : 0)),
      pointHoverRadius: passOnlyRows.map((row) => (row.is_critical ? 7 : 0)),
      pointBorderWidth: passOnlyRows.map((row) => (row.is_critical ? 2 : 0)),
      showLine: false,
      order: -1,
    });
    return {
      labels: passOnlyRows.map((point) => point.index_label),
      datasets,
    };
  }, [annotatedControlChartRows, controlChartPassOnlyStats, resolvedLowerSpecLimit, resolvedUpperSpecLimit]);
  const burnInPassFailChartData = useMemo(() => {
    if (!controlChartAllStats) return null;
    const datasets = [
      {
        label: 'Individual values (PASS + FAIL)',
        data: controlChartAllStats.values,
        borderColor: '#f97316',
        backgroundColor: '#f97316',
        pointRadius: 3,
        fill: false,
      },
      {
        label: 'CL',
        data: controlChartAllStats.values.map(() => controlChartAllStats.meanValue),
        borderColor: '#16a34a',
        borderDash: [5, 5],
        pointRadius: 0,
      },
      {
        label: 'UCL',
        data: controlChartAllStats.values.map(() => controlChartAllStats.upperControlLimit),
        borderColor: '#ef4444',
        borderDash: [5, 5],
        pointRadius: 0,
      },
      {
        label: 'LCL',
        data: controlChartAllStats.values.map(() => controlChartAllStats.lowerControlLimit),
        borderColor: '#f59e0b',
        borderDash: [5, 5],
        pointRadius: 0,
      },
    ];
    if (Number.isFinite(Number(resolvedLowerSpecLimit))) {
      datasets.push({
        label: 'LSL',
        data: controlChartAllStats.values.map(() => Number(resolvedLowerSpecLimit)),
        borderColor: '#7c3aed',
        borderDash: [10, 6],
        pointRadius: 0,
      });
    }
    if (Number.isFinite(Number(resolvedUpperSpecLimit))) {
      datasets.push({
        label: 'USL',
        data: controlChartAllStats.values.map(() => Number(resolvedUpperSpecLimit)),
        borderColor: '#8b5cf6',
        borderDash: [10, 6],
        pointRadius: 0,
      });
    }
    datasets.push({
      label: 'Critical points',
      data: annotatedControlChartRows.map((row) => (row.is_critical ? (Number(row.value_number) || 0) : null)),
      borderColor: 'transparent',
      backgroundColor: annotatedControlChartRows.map((row) => (
        row.is_out_of_spec
          ? '#dc2626'
          : row.is_out_of_control
            ? '#f97316'
            : 'transparent'
      )),
      pointBorderColor: annotatedControlChartRows.map((row) => (
        row.is_out_of_spec
          ? '#991b1b'
          : row.is_out_of_control
            ? '#9a3412'
            : 'transparent'
      )),
      pointRadius: annotatedControlChartRows.map((row) => (row.is_critical ? 5 : 0)),
      pointHoverRadius: annotatedControlChartRows.map((row) => (row.is_critical ? 7 : 0)),
      pointBorderWidth: annotatedControlChartRows.map((row) => (row.is_critical ? 2 : 0)),
      showLine: false,
      order: -1,
    });
    return {
      labels: annotatedControlChartRows.map((point) => point.index_label),
      datasets,
    };
  }, [annotatedControlChartRows, controlChartAllStats, resolvedLowerSpecLimit, resolvedUpperSpecLimit]);
  const burnInNormalDistributionChartData = useMemo(() => {
    if (!controlChartPassOnlyStats) return null;
    return {
      datasets: [
        {
          type: 'bar',
          label: 'Histogram',
          data: controlChartPassOnlyStats.histogramData,
          parsing: { xAxisKey: 'x', yAxisKey: 'y' },
          backgroundColor: 'rgba(15, 118, 110, 0.18)',
          borderColor: '#0f766e',
          borderWidth: 1,
          barPercentage: 1,
          categoryPercentage: 1,
        },
        {
          type: 'line',
          label: 'Normal curve',
          data: controlChartPassOnlyStats.fittedNormalCurve,
          parsing: { xAxisKey: 'x', yAxisKey: 'y' },
          borderColor: '#7c3aed',
          pointRadius: 0,
          tension: 0.18,
        },
      ],
    };
  }, [controlChartPassOnlyStats]);
  const burnInMrChartData = useMemo(() => {
    if (!controlChartPassOnlyStats) return null;
    const passOnlyRows = annotatedControlChartRows.filter((point) => isBurnInPassStatus(point.status));
    const movingRangeCritical = controlChartPassOnlyStats.movingRanges.map((value) => (
      value > controlChartPassOnlyStats.mrUcl ? value : null
    ));
    return {
      labels: passOnlyRows.slice(1).map((point) => point.index_label),
      datasets: [
        {
          label: 'Moving Range',
          data: controlChartPassOnlyStats.movingRanges,
          borderColor: '#7c3aed',
          backgroundColor: '#7c3aed',
          pointRadius: 3,
          fill: false,
        },
        {
          label: 'MR-bar (CL)',
          data: controlChartPassOnlyStats.movingRanges.map(() => controlChartPassOnlyStats.mrBar),
          borderColor: '#16a34a',
          borderDash: [5, 5],
          pointRadius: 0,
        },
        {
          label: 'UCL',
          data: controlChartPassOnlyStats.movingRanges.map(() => controlChartPassOnlyStats.mrUcl),
          borderColor: '#ef4444',
          borderDash: [5, 5],
          pointRadius: 0,
        },
        {
          label: 'Critical MR points',
          data: movingRangeCritical,
          borderColor: 'transparent',
          backgroundColor: '#dc2626',
          pointBorderColor: '#991b1b',
          pointRadius: movingRangeCritical.map((value) => (value == null ? 0 : 5)),
          pointHoverRadius: movingRangeCritical.map((value) => (value == null ? 0 : 7)),
          pointBorderWidth: movingRangeCritical.map((value) => (value == null ? 0 : 2)),
          showLine: false,
          order: -1,
        },
      ],
    };
  }, [annotatedControlChartRows, controlChartPassOnlyStats]);
  const burnInPassOnlyChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Individual Chart (PASS only)' },
    },
  }), []);
  const burnInPassFailChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Individual Chart (PASS + FAIL)' },
    },
  }), []);
  const burnInNormalDistributionChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Normal Distribution Chart' },
    },
    scales: {
      x: { type: 'linear' },
    },
  }), []);
  const burnInMrChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'MR Chart' },
    },
  }), []);
  const expandedControlCharts = useMemo(() => {
    if (!config.supportsExpandedControlAnalysis || !controlChartPassOnlyStats || !controlChartAllStats) return [];

    return [
      {
        key: 'pass-only',
        title: 'Individual Chart (PASS only)',
        description: 'PASS-only measurements for the selected burn-in numeric series.',
        fileSuffix: 'burn-in-pass-only-control-chart',
        chartRef: burnInPassOnlyChartRef,
        exportRef: burnInPassOnlyExportRef,
        detailCards: burnInCapabilityPassOnlyCards,
        ChartRenderer: Line,
        data: burnInPassOnlyChartData,
        options: burnInPassOnlyChartOptions,
        isEmpty: !burnInPassOnlyChartData?.labels?.length,
        emptyMessage: 'No PASS-only control chart data available for this scope.',
      },
      {
        key: 'pass-fail',
        title: 'Individual Chart (PASS + FAIL)',
        description: 'All measurements in scope, including PASS and FAIL rows, for the selected numeric series.',
        fileSuffix: 'burn-in-pass-fail-control-chart',
        chartRef: burnInPassFailChartRef,
        exportRef: burnInPassFailExportRef,
        detailCards: burnInCapabilityAllCards,
        ChartRenderer: Line,
        data: burnInPassFailChartData,
        options: burnInPassFailChartOptions,
        isEmpty: !burnInPassFailChartData?.labels?.length,
        emptyMessage: 'Nessun dato PASS + FAIL disponibile per questo perimetro.',
      },
      {
        key: 'normal-distribution',
        title: 'Normal Distribution Chart',
        description: 'Histogram and fitted normal curve for PASS-only burn-in measurements.',
        fileSuffix: 'burn-in-normal-distribution-chart',
        chartRef: burnInNormalChartRef,
        exportRef: burnInNormalExportRef,
        detailCards: burnInCapabilityPassOnlyCards,
        ChartRenderer: Chart,
        chartType: 'bar',
        data: burnInNormalDistributionChartData,
        options: burnInNormalDistributionChartOptions,
        isEmpty: !controlChartPassOnlyStats.histogramData.length,
        emptyMessage: 'Nessun dato di distribuzione normale disponibile per questo perimetro.',
      },
      {
        key: 'mr-chart',
        title: 'MR Chart',
        description: 'Moving-range variation for PASS-only burn-in measurements in the selected numeric series.',
        fileSuffix: 'burn-in-mr-chart',
        chartRef: burnInMrChartRef,
        exportRef: burnInMrExportRef,
        detailCards: [
          { label: 'Samples', value: formatNumber(controlChartPassOnlyStats.sampleCount) },
          { label: 'MR bar', value: formatDecimal(controlChartPassOnlyStats.mrBar, 4) },
          { label: 'MR UCL', value: formatDecimal(controlChartPassOnlyStats.mrUcl, 4) },
          { label: 'Mean', value: Number.isFinite(burnInCapabilityPassOnly?.mean) ? formatDecimal(burnInCapabilityPassOnly.mean, 4) : '-' },
          { label: 'Sigma within', value: Number.isFinite(burnInCapabilityPassOnly?.sigmaWithin) ? formatDecimal(burnInCapabilityPassOnly.sigmaWithin, 4) : '-' },
          { label: 'Sigma overall', value: Number.isFinite(burnInCapabilityPassOnly?.sigmaOverall) ? formatDecimal(burnInCapabilityPassOnly.sigmaOverall, 4) : '-' },
          { label: 'Cp', value: Number.isFinite(burnInCapabilityPassOnly?.cp) ? formatDecimal(burnInCapabilityPassOnly.cp, 3) : '-' },
          { label: 'Cpk', value: Number.isFinite(burnInCapabilityPassOnly?.cpk) ? formatDecimal(burnInCapabilityPassOnly.cpk, 3) : '-' },
          { label: 'Pp', value: Number.isFinite(burnInCapabilityPassOnly?.pp) ? formatDecimal(burnInCapabilityPassOnly.pp, 3) : '-' },
          { label: 'Ppk', value: Number.isFinite(burnInCapabilityPassOnly?.ppk) ? formatDecimal(burnInCapabilityPassOnly.ppk, 3) : '-' },
        ],
        ChartRenderer: Line,
        data: burnInMrChartData,
        options: burnInMrChartOptions,
        isEmpty: !burnInMrChartData?.labels?.length,
        emptyMessage: 'Nessun dato moving-range disponibile per questo perimetro.',
      },
    ];
  }, [
    burnInMrChartData,
    burnInMrChartOptions,
    burnInNormalDistributionChartData,
    burnInNormalDistributionChartOptions,
    burnInPassFailChartData,
    burnInCapabilityAllCards,
    burnInCapabilityPassOnly,
    burnInCapabilityPassOnlyCards,
    burnInPassFailChartOptions,
    burnInPassOnlyChartData,
    burnInPassOnlyChartOptions,
    config.supportsExpandedControlAnalysis,
    controlChartAllStats,
    controlChartPassOnlyStats,
  ]);
  const activeExpandedControlChart = useMemo(
    () => expandedControlCharts.find((chart) => chart.key === expandedControlChartKey) || null,
    [expandedControlChartKey, expandedControlCharts]
  );
  const toggleControlChartDetails = (chartKey) => {
    setOpenControlChartDetails((current) => ({
      ...current,
      [chartKey]: !current[chartKey],
    }));
  };
  const toggleControlChartDetailsInExport = (chartKey) => {
    setIncludeControlChartDetailsInExport((current) => ({
      ...current,
      [chartKey]: !current[chartKey],
    }));
  };
  const toggleTableSort = (columnKey) => {
    setTableSort((current) => {
      if (current.key === columnKey) {
        return {
          key: columnKey,
          direction: current.direction === 'desc' ? 'asc' : 'desc',
        };
      }
      return {
        key: columnKey,
        direction: columnKey === 'criticality_label' ? 'desc' : 'asc',
      };
    });
  };
  const chartModalSizeClass = chartModalSize === 'full'
    ? styles.chartModalFull
    : chartModalSize === 'medium'
      ? styles.chartModalMedium
      : styles.chartModalLarge;
  const chartModalBodySizeClass = chartModalSize === 'full'
    ? styles.chartModalBodyFull
    : chartModalSize === 'medium'
      ? styles.chartModalBodyMedium
      : styles.chartModalBodyLarge;

  return (
    <div className={styles.container}>
      <GoToDashboardButton />

      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>{config.eyebrow}</p>
            <h2 className={styles.title}>{config.title}</h2>
            <p className={styles.description}>{config.description}</p>
          </div>
          <div className={styles.scopeSummary}>
            <span>Measurements in scope</span>
            <strong>{formatNumber(pageTotal)}</strong>
            <small>{pageTotal ? `${formatDecimal((pageFailed / pageTotal) * 100)}% fail rate` : 'No data yet'}</small>
          </div>
        </header>

        <section className={styles.navSection}>
          <div className={styles.navActions}>
            <button
              type="button"
              onClick={() => navigate('/burn-in-dashboard')}
              className={styles.hubButton}
            >
              Back to Burn-In Dashboard
            </button>
            <button
              type="button"
              onClick={() => navigate('/burn-in-upload')}
              className={styles.inlineGhostButton}
            >
              Upload Burn-In Files
            </button>
          </div>
        </section>

        <section className={styles.filterBar}>
          <div>
            <h3>Scope</h3>
            <p>Switch between all burn-in uploads or inspect a single imported workbook.</p>
          </div>
          <select
            value={selectedUploadId}
            onChange={(event) => setSelectedUploadId(event.target.value)}
            className={styles.select}
          >
            <option value="">All burn-in uploads</option>
            {uploads.map((upload) => (
              <option key={upload.id} value={upload.id}>
                {upload.original_filename}
              </option>
            ))}
          </select>
        </section>

        {config.supportsAdvancedFilters && (
          <section className={styles.advancedFilterPanel}>
            <div className={styles.advancedFilterHeader}>
              <div>
                <h3>Filters</h3>
                <p>Refine this burn-in analysis by metadata, row content, date, and measured hour.</p>
              </div>
              <div className={styles.filterSummary}>
                <span>{activeAdvancedFilterCount} active</span>
                <button
                  type="button"
                  className={styles.inlineGhostButton}
                  onClick={() => {
                    setFilters(buildDefaultBurnInFilters());
                    setPendingPerformanceTestNumber('');
                    setSelectedControlChartSeries('');
                    setManualControlLimits({ upper: '', lower: '' });
                    setManualSpecLimits({ upper: '', lower: '' });
                  }}
                >
                  Reset filters
                </button>
              </div>
            </div>

            <div className={styles.filterGrid}>
              {config.supportsControlChartSeriesSelection && (
                <>
                  <label className={`${styles.filterField} ${styles.controlChartSeriesField}`}>
                    <span>Numeric step series</span>
                    <select
                      className={styles.filterInput}
                      value={selectedControlChartSeries}
                      onChange={(event) => setSelectedControlChartSeries(event.target.value)}
                    >
                      {!controlChartSeriesOptions.length && <option value="">Nessuna serie numerica nel perimetro</option>}
                      {controlChartSeriesOptions.map((series) => (
                        <option key={series.series_key} value={series.series_key}>
                          {series.step_number ? `${series.step_number} · ` : ''}{series.step_name} [{series.units}] - {series.sample_count} samples - {series.fail_count || 0} fails
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.filterField}>
                    <span>Manual UCL</span>
                    <input
                      type="number"
                      step="any"
                      className={styles.filterInput}
                      value={manualControlLimits.upper}
                      placeholder={controlChartMeta?.upper_control_limit ?? ''}
                      onChange={(event) => setManualControlLimits((current) => ({ ...current, upper: event.target.value }))}
                    />
                  </label>

                  <label className={styles.filterField}>
                    <span>Manual LCL</span>
                    <input
                      type="number"
                      step="any"
                      className={styles.filterInput}
                      value={manualControlLimits.lower}
                      placeholder={controlChartMeta?.lower_control_limit ?? ''}
                      onChange={(event) => setManualControlLimits((current) => ({ ...current, lower: event.target.value }))}
                    />
                  </label>

                  <label className={styles.filterField}>
                    <span>Manual LSL</span>
                    <input
                      type="number"
                      step="any"
                      className={styles.filterInput}
                      value={manualSpecLimits.lower}
                      placeholder={controlChartMeta?.lower_spec_limit ?? ''}
                      onChange={(event) => setManualSpecLimits((current) => ({ ...current, lower: event.target.value }))}
                    />
                  </label>

                  <label className={styles.filterField}>
                    <span>Manual USL</span>
                    <input
                      type="number"
                      step="any"
                      className={styles.filterInput}
                      value={manualSpecLimits.upper}
                      placeholder={controlChartMeta?.upper_spec_limit ?? ''}
                      onChange={(event) => setManualSpecLimits((current) => ({ ...current, upper: event.target.value }))}
                    />
                  </label>
                </>
              )}

              {burnInDateFilterFields.map((field) => {
                const bounds = filterOptions.measuredDateBounds || {};
                const isStart = field.key === 'measuredDateStart';
                return (
                  <label key={field.key} className={styles.filterField}>
                    <span>{field.label}</span>
                    <input
                      type="date"
                      className={styles.filterInput}
                      value={filters[field.key]}
                      min={isStart ? bounds.min || undefined : filters.measuredDateStart || bounds.min || undefined}
                      max={isStart ? filters.measuredDateEnd || bounds.max || undefined : bounds.max || undefined}
                      onChange={(event) => setFilters((current) => ({ ...current, [field.key]: event.target.value }))}
                    />
                  </label>
                );
              })}

              {burnInAdvancedFilterFields.map((field) => (
                <label key={field.key} className={styles.filterField}>
                  <span>{field.label}</span>
                  {field.multi ? (
                    <div className={styles.filterMultiBuilder}>
                      <div className={styles.filterMultiActions}>
                        <button
                          type="button"
                          className={styles.inlineGhostButton}
                          onClick={() => {
                            setFilters((current) => ({ ...current, [field.key]: [] }));
                            setPendingPerformanceTestNumber(getFirstUnselectedFilterOption(filterOptions[field.key] || [], []));
                          }}
                        >
                          All
                        </button>
                        <select
                          className={styles.filterInput}
                          value={pendingPerformanceTestNumber}
                          onChange={(event) => setPendingPerformanceTestNumber(event.target.value)}
                        >
                          <option value="">Select number</option>
                          {(filterOptions[field.key] || []).map((value) => (
                            <option key={value} value={value}>
                              n° {value}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className={styles.filterAddButton}
                          onClick={() => {
                            if (!pendingPerformanceTestNumber) return;
                            setFilters((current) => {
                              const existing = Array.isArray(current[field.key]) ? current[field.key] : [];
                              if (existing.includes(pendingPerformanceTestNumber)) return current;
                              return {
                                ...current,
                                [field.key]: [...existing, pendingPerformanceTestNumber],
                              };
                            });
                          }}
                          disabled={!pendingPerformanceTestNumber || (Array.isArray(filters[field.key]) && filters[field.key].includes(pendingPerformanceTestNumber))}
                        >
                          + Add
                        </button>
                      </div>
                      <div className={styles.filterMultiSelected}>
                        {Array.isArray(filters[field.key]) && filters[field.key].length ? (
                          filters[field.key].map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={styles.filterChip}
                              onClick={() => setFilters((current) => ({
                                ...current,
                                [field.key]: (Array.isArray(current[field.key]) ? current[field.key] : []).filter((item) => item !== value),
                              }))}
                            >
                              {`n° ${value}`} ×
                            </button>
                          ))
                        ) : (
                          <span className={styles.filterAllState}>All performance tests</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <select
                      className={styles.filterInput}
                      value={filters[field.key]}
                      onChange={(event) => setFilters((current) => ({ ...current, [field.key]: event.target.value }))}
                    >
                      <option value="">All</option>
                      {(filterOptions[field.key] || []).map((value) => (
                        <option key={value} value={value}>
                          {field.key === 'measuredHour' ? `${value}:00` : value}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              ))}
            </div>

            {config.supportsControlChartSeriesSelection && (
              <div className={styles.filterSummary}>
                <span>
                  UCL: {resolvedUpperControlLimit !== '' ? formatDecimal(resolvedUpperControlLimit, 4) : '-'}
                  {'  '}
                  LCL: {resolvedLowerControlLimit !== '' ? formatDecimal(resolvedLowerControlLimit, 4) : '-'}
                  {'  '}
                  LSL: {resolvedLowerSpecLimit !== '' ? formatDecimal(resolvedLowerSpecLimit, 4) : '-'}
                  {'  '}
                  USL: {resolvedUpperSpecLimit !== '' ? formatDecimal(resolvedUpperSpecLimit, 4) : '-'}
                </span>
                <button
                  type="button"
                  className={styles.inlineGhostButton}
                  onClick={() => {
                    setManualControlLimits({ upper: '', lower: '' });
                    setManualSpecLimits({ upper: '', lower: '' });
                  }}
                >
                  Reset limits
                </button>
              </div>
            )}
          </section>
        )}

        {config.supportsUnitDisplayLimit && (
          <section className={styles.filterBar}>
            <div>
              <h3>Units shown on chart</h3>
              <p>Choose how many burn-in units should be visible in the ranking chart and table.</p>
            </div>
            <select
              value={unitDisplayLimit}
              onChange={(event) => setUnitDisplayLimit(event.target.value)}
              className={styles.select}
            >
              {burnInUnitDisplayLimitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </section>
        )}

        {error && <div className={styles.errorMessage}>{error}</div>}

        <section className={styles.metricsGrid}>
          {metrics.map((metric) => (
            <div key={metric.label} className={styles.metricCard}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </section>

        {config.supportsExpandedControlAnalysis && controlChartPassOnlyStats && controlChartAllStats && (
          <section className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <span>PASS-only count</span>
              <strong>{formatNumber(controlChartPassOnlyStats.sampleCount)}</strong>
            </div>
            <div className={styles.metricCard}>
              <span>PASS + FAIL count</span>
              <strong>{formatNumber(controlChartAllStats.sampleCount)}</strong>
            </div>
            <div className={styles.metricCard}>
              <span>PASS-only mean</span>
              <strong>{formatDecimal(controlChartPassOnlyStats.meanValue, 4)}</strong>
            </div>
            <div className={styles.metricCard}>
              <span>PASS + FAIL mean</span>
              <strong>{formatDecimal(controlChartAllStats.meanValue, 4)}</strong>
            </div>
          </section>
        )}

        {config.supportsSelectedFileCalculator && config.supportsExpandedControlAnalysis && controlChartPassOnlyStats && controlChartAllStats && (
          <section className={styles.calculatorSpotlight}>
            <div>
              <p className={styles.eyebrow}>Alternative baseline</p>
              <h3>Burn-In Control Limits Calculator</h3>
              <p>Select the exact burn-in source files you want to use, then rebuild the current numeric series with a narrower reference scope.</p>
            </div>
            <button
              type="button"
              className={styles.calculatorSpotlightButton}
              onClick={() => {
                setCalculatorError('');
                setSelectedCalculatorFileKeys([]);
                setIsCalculatorModalOpen(true);
              }}
              disabled={!selectedControlChartSeries}
            >
              Open Control Limits Calculator
            </button>
          </section>
        )}

        {!config.supportsExpandedControlAnalysis && (
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Chart</h3>
                <p>{loading ? 'Loading burn-in data...' : 'The chart reflects the currently selected burn-in scope.'}</p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => {
                    setExpandedControlChartKey('');
                    setChartModalSize('large');
                    setIsChartModalOpen(true);
                  }}
                  disabled={loading || !rows.length}
                >
                  Espandi
                </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(chartWrapRef.current, `${exportBaseName}-chart`)}
                  disabled={loading || !rows.length}
                >
                  Esporta PNG
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={chartWrapRef}>
              {loading ? (
                <div className={styles.emptyState}>Loading chart data...</div>
              ) : rows.length ? (
                <ChartComponent data={chartData} options={chartOptions} />
              ) : (
                <div className={styles.emptyState}>No burn-in data available for this view yet.</div>
              )}
            </div>
          </section>
        )}

        {config.supportsExpandedControlAnalysis && controlChartPassOnlyStats && controlChartAllStats && (
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Expanded Control Analysis</h3>
                <p>This burn-in control analysis mirrors the four production-style views for the selected numeric series.</p>
              </div>
            </div>
            <div className={styles.stackedPanels}>
              {expandedControlCharts.map((chart) => {
                const ExpandedChartComponent = chart.ChartRenderer;
                return (
                  <section key={chart.key} className={styles.chartPanel}>
                    <div className={styles.panelHeader}>
                      <div>
                        <h3>{chart.title}</h3>
                        <p>{chart.description}</p>
                      </div>
                      <div className={styles.exportActions}>
                        <button
                          type="button"
                          className={styles.exportButton}
                          onClick={() => {
                            setExpandedControlChartKey(chart.key);
                            setChartModalSize('large');
                            setIsChartModalOpen(true);
                          }}
                          disabled={chart.isEmpty}
                        >
                          Espandi
                        </button>
                        <button
                          type="button"
                          className={styles.exportButton}
                          onClick={() => toggleControlChartDetails(chart.key)}
                        >
                          {openControlChartDetails[chart.key] ? 'Hide details' : 'Details'}
                        </button>
                        <label className={styles.exportCheckbox}>
                          <input
                            type="checkbox"
                            checked={!!includeControlChartDetailsInExport[chart.key]}
                            onChange={() => toggleControlChartDetailsInExport(chart.key)}
                          />
                          Includi dettagli nel PNG
                        </label>
                        <button
                          type="button"
                          className={styles.exportButton}
                          onClick={() => exportChartNodeAsPNG(chart.exportRef.current, `${exportBaseName}-${chart.fileSuffix}`)}
                          disabled={chart.isEmpty}
                        >
                          Esporta PNG
                        </button>
                      </div>
                    </div>
                    <div ref={chart.exportRef}>
                      <div className={styles.chartWrap} ref={chart.chartRef}>
                        {chart.isEmpty ? (
                          <div className={styles.emptyState}>{chart.emptyMessage}</div>
                        ) : chart.chartType ? (
                          <ExpandedChartComponent type={chart.chartType} data={chart.data} options={chart.options} />
                        ) : (
                          <ExpandedChartComponent data={chart.data} options={chart.options} />
                        )}
                      </div>
                      {(openControlChartDetails[chart.key] || includeControlChartDetailsInExport[chart.key]) && !!chart.detailCards?.length && (
                        <div className={styles.chartMetricPanel}>
                          <div className={styles.chartMetricGrid}>
                            {chart.detailCards.map((metric) => (
                              <div key={`${chart.key}-${metric.label}`} className={styles.chartMetricCard}>
                                <span>{metric.label}</span>
                                <strong>{metric.value}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </section>
        )}

        <section className={styles.tablePanel}>
          <div className={styles.panelHeader}>
            <div>
              <h3>Underlying details</h3>
              <p>The table shows the rows used to build the charts above.</p>
            </div>
            <div className={styles.exportActions}>
              <button
                type="button"
                className={styles.exportButton}
                onClick={() => setIsDetailsTableExpanded((current) => !current)}
              >
                {isDetailsTableExpanded ? 'Hide table' : 'Expand table'}
              </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportRowsAsCSV(displayTableColumns, displayRows, `${exportBaseName}-table`)}
                  disabled={loading || !displayRows.length}
                >
                  CSV
                </button>
              <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportRowsAsPDF(config.title, displayTableColumns, displayRows, `${exportBaseName}-table`)}
                  disabled={loading || !displayRows.length}
                >
                  PDF
                </button>
            </div>
          </div>
          {isDetailsTableExpanded ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>#</th>
                    {displayTableColumns.map((column) => (
                      <th key={column.key}>
                        <button
                          type="button"
                          className={styles.tableSortButton}
                          onClick={() => toggleTableSort(column.key)}
                        >
                          <span>{column.label}</span>
                          <span className={styles.tableSortIndicator}>
                            {tableSort.key === column.key
                              ? (tableSort.direction === 'asc' ? '↑' : '↓')
                              : '↕'}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!loading && displayRows.length === 0 && (
                    <tr>
                      <td colSpan={displayTableColumns.length + 1} className={styles.emptyTableCell}>
                        No detail rows to show for this scope.
                      </td>
                    </tr>
                  )}
                  {displayRows.map((row, index) => (
                    <tr
                      key={`${row[displayTableColumns[0].key] || 'row'}-${index}`}
                      className={
                        row.is_critical
                          ? styles.tableRowCritical
                          : row.is_warning
                            ? styles.tableRowWarning
                            : ''
                      }
                    >
                      <td>{index + 1}</td>
                      {displayTableColumns.map((column) => (
                        <td key={column.key}>
                          {typeof column.render === 'function'
                            ? column.render(row[column.key], row)
                            : typeof column.format === 'function'
                            ? column.format(row[column.key])
                            : row[column.key] ?? '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>Table collapsed. Click "Expand table" whenever you want to inspect the raw rows.</div>
          )}
        </section>

        {config.supportsSelectedFileCalculator && isCalculatorModalOpen && (
          <div
            className={styles.modalOverlay}
            onClick={() => {
              setIsCalculatorModalOpen(false);
              setCalculatorError('');
            }}
            role="presentation"
          >
            <div
              className={styles.calculatorModal}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Burn-in control limits calculator"
            >
              <div className={styles.modalHeader}>
                <div>
                  <h3>Burn-In Control Limits Calculator</h3>
                  <p>Select the exact burn-in source files you want to use for the current numeric series.</p>
                </div>
                <div className={styles.exportActions}>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => setCalculatorSortOrder((current) => (current === 'newest' ? 'oldest' : 'newest'))}
                  >
                    Sort: {calculatorSortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
                  </button>
                  <button
                    type="button"
                    className={styles.inlineGhostButton}
                    onClick={() => {
                      setCalculatorSearch('');
                      setCalculatorStatusFilter('');
                      setCalculatorVersionFilter('');
                      setCalculatorSheetFilter('');
                      setCalculatorDateFrom('');
                      setCalculatorDateTo('');
                    }}
                  >
                    Clear filters
                  </button>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => {
                      setIsCalculatorModalOpen(false);
                      setCalculatorError('');
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className={styles.calculatorSummaryBox}>
                <span>Selected numeric series</span>
                <strong>
                  {(() => {
                    const parsed = parseControlChartSeriesKey(selectedControlChartSeries);
                    return parsed.stepName
                      ? `${parsed.stepName}${parsed.units ? ` [${parsed.units}]` : ''}`
                      : 'No series selected';
                  })()}
                </strong>
              </div>

              <div className={styles.filterGrid}>
                <label className={styles.filterField}>
                  <span>Search</span>
                  <input
                    type="text"
                    className={styles.filterInput}
                    placeholder="Source file, unit, workbook, version"
                    value={calculatorSearch}
                    onChange={(event) => setCalculatorSearch(event.target.value)}
                  />
                </label>
                <label className={styles.filterField}>
                  <span>File Status</span>
                  <select
                    className={styles.filterInput}
                    value={calculatorStatusFilter}
                    onChange={(event) => setCalculatorStatusFilter(event.target.value)}
                  >
                    <option value="">All statuses</option>
                    {calculatorStatuses.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.filterField}>
                  <span>Software Version</span>
                  <select
                    className={styles.filterInput}
                    value={calculatorVersionFilter}
                    onChange={(event) => setCalculatorVersionFilter(event.target.value)}
                  >
                    <option value="">All versions</option>
                    {calculatorVersions.map((version) => (
                      <option key={version} value={version}>{version}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.filterField}>
                  <span>Sheet Name</span>
                  <select
                    className={styles.filterInput}
                    value={calculatorSheetFilter}
                    onChange={(event) => setCalculatorSheetFilter(event.target.value)}
                  >
                    <option value="">All sheets</option>
                    {calculatorSheets.map((sheet) => (
                      <option key={sheet} value={sheet}>{sheet}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.filterField}>
                  <span>Measured Date From</span>
                  <input
                    type="date"
                    className={styles.filterInput}
                    value={calculatorDateFrom}
                    onChange={(event) => setCalculatorDateFrom(event.target.value)}
                  />
                </label>
                <label className={styles.filterField}>
                  <span>Measured Date To</span>
                  <input
                    type="date"
                    className={styles.filterInput}
                    value={calculatorDateTo}
                    onChange={(event) => setCalculatorDateTo(event.target.value)}
                  />
                </label>
              </div>

              <div className={styles.calculatorSelectionBar}>
                <span>{filteredCalculatorFiles.length} visible source files</span>
                <span>{selectedCalculatorFileKeys.length} selected</span>
              </div>

              <div className={styles.calculatorFileTable}>
                {filteredCalculatorFiles.map((file) => {
                  const checked = selectedCalculatorFileKeys.includes(file.selection_key);
                  return (
                    <label key={file.selection_key} className={styles.calculatorFileRow}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const isChecked = event.target.checked;
                          setSelectedCalculatorFileKeys((current) => {
                            if (isChecked) return [...new Set([...current, file.selection_key])];
                            return current.filter((value) => value !== file.selection_key);
                          });
                        }}
                      />
                      <div className={styles.calculatorFileMeta}>
                        <strong>{file.source_file}</strong>
                        <span>Unit: {file.unit_serial || '-'}</span>
                        <span>Workbook: {file.workbook_filename || '-'}</span>
                        <span>Sheet: {file.sheet_name || '-'}</span>
                        <span>Software Version: {file.version_sw || '-'}</span>
                        <span>First Measured: {formatDateTime(file.first_measured_at)}</span>
                        <span>Status: {file.overall_status || '-'}</span>
                      </div>
                    </label>
                  );
                })}
                {!filteredCalculatorFiles.length && (
                  <div className={styles.emptyState}>No burn-in source files match the current search and filters.</div>
                )}
              </div>

              {calculatorError && <div className={styles.errorMessage}>{calculatorError}</div>}

              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={async () => {
                    const parsedSeries = parseControlChartSeriesKey(selectedControlChartSeries);
                    if (!parsedSeries.stepName) {
                      setCalculatorError('Select a numeric series before running the calculator.');
                      return;
                    }
                    if (!selectedCalculatorFileKeys.length) {
                      setCalculatorError('Select at least one burn-in source file.');
                      return;
                    }

                    try {
                      const selectedFiles = calculatorFiles
                        .filter((file) => selectedCalculatorFileKeys.includes(file.selection_key))
                        .map((file) => ({
                          uploadId: Number(file.upload_id),
                          sourceFile: String(file.source_file || ''),
                        }));

                      const response = await apiPost('/api/burn-in/calculate-ucl-lcl', {
                        selectedFiles,
                        stepName: parsedSeries.stepName,
                        units: parsedSeries.units,
                      });

                      try {
                        window.localStorage.setItem(CONTROL_CHART_RESULTS_STORAGE_KEY, JSON.stringify({
                          mode: 'burn-in',
                          results: response.results,
                        }));
                      } catch (storageError) {
                        console.warn('Could not persist burn-in control-chart results:', storageError);
                      }

                      navigate('/new-control-chart', {
                        state: {
                          results: response.results,
                          mode: 'burn-in',
                        },
                      });
                    } catch (loadError) {
                      console.error('Failed to calculate burn-in control limits:', loadError);
                      setCalculatorError(loadError.response?.data?.error || 'Failed to calculate burn-in control limits.');
                    }
                  }}
                  disabled={!filteredCalculatorFiles.length}
                >
                  Calculate Limits
                </button>
              </div>
            </div>
          </div>
        )}

        {isChartModalOpen && (
          <div
            className={styles.modalOverlay}
            onClick={() => {
              setIsChartModalOpen(false);
              setExpandedControlChartKey('');
              setChartModalSize('large');
            }}
            role="presentation"
          >
            <div
              className={`${styles.chartModal} ${chartModalSizeClass}`}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`${config.title} expanded chart`}
            >
              <div className={styles.modalHeader}>
                <div>
                  <h3>{activeExpandedControlChart?.title || config.title}</h3>
                  <p>Usa i pulsanti di dimensione per ispezionare il grafico a scala maggiore.</p>
                </div>
                <div className={styles.exportActions}>
                  {CONTROL_MODAL_SIZES.map((size) => (
                    <button
                      key={size.key}
                      type="button"
                      className={styles.exportButton}
                      onClick={() => setChartModalSize(size.key)}
                    >
                      {size.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => {
                      if (activeExpandedControlChart) {
                        exportChartNodeAsPNG(
                          activeExpandedControlChart.exportRef.current,
                          `${exportBaseName}-${activeExpandedControlChart.fileSuffix}`
                        );
                        return;
                      }
                      exportChartNodeAsPNG(chartWrapRef.current, `${exportBaseName}-chart`);
                    }}
                    disabled={activeExpandedControlChart ? activeExpandedControlChart.isEmpty : (loading || !rows.length)}
                  >
                    Esporta PNG
                  </button>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => {
                      setIsChartModalOpen(false);
                      setExpandedControlChartKey('');
                      setChartModalSize('large');
                    }}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
              <div className={`${styles.chartModalBody} ${chartModalBodySizeClass}`}>
                {activeExpandedControlChart ? (
                  activeExpandedControlChart.isEmpty ? (
                    <div className={styles.emptyState}>{activeExpandedControlChart.emptyMessage}</div>
                  ) : activeExpandedControlChart.chartType ? (
                    <activeExpandedControlChart.ChartRenderer
                      type={activeExpandedControlChart.chartType}
                      data={activeExpandedControlChart.data}
                      options={activeExpandedControlChart.options}
                    />
                  ) : (
                    <activeExpandedControlChart.ChartRenderer
                      data={activeExpandedControlChart.data}
                      options={activeExpandedControlChart.options}
                    />
                  )
                ) : loading ? (
                  <div className={styles.emptyState}>Loading chart data...</div>
                ) : rows.length ? (
                  <ChartComponent data={chartData} options={chartOptions} />
                ) : (
                  <div className={styles.emptyState}>No burn-in data available for this view yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function BurnInTemperatureAnalyzerPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.startsWith('it');
  const tx = useCallback((english, italian) => (isItalian ? italian : english), [isItalian]);
  const [uploads, setUploads] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState('');
  const [selectedStepKey, setSelectedStepKey] = useState('');
  const [filters, setFilters] = useState(buildDefaultBurnInFilters);
  const [pendingTemperaturePerformanceTestNumber, setPendingTemperaturePerformanceTestNumber] = useState('');
  const [filterOptions, setFilterOptions] = useState({});
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const overviewChartRef = useRef(null);
  const headerChartRef = useRef(null);
  const sensitivityChartRef = useRef(null);
  const correlationChartRef = useRef(null);
  const selectedStepChartRef = useRef(null);
  const [temperatureChartModalKey, setTemperatureChartModalKey] = useState('');
  const [temperatureChartModalSize, setTemperatureChartModalSize] = useState('large');
  const [temperatureTableModal, setTemperatureTableModal] = useState(null);
  const [temperatureTableSort, setTemperatureTableSort] = useState({ key: '__severity', direction: 'desc' });

  const queryParams = useMemo(() => {
    const params = {};
    if (selectedUploadId) params.uploadId = selectedUploadId;
    Object.entries(filters).forEach(([key, value]) => {
      const normalized = stringifyFilterValue(value);
      if (normalized) {
        params[key] = normalized;
      }
    });
    return params;
  }, [filters, selectedUploadId]);

  useEffect(() => {
    let active = true;
    const loadUploads = async () => {
      try {
        const uploadRows = await apiGet('/api/burn-in/uploads');
        if (!active) return;
        setUploads(Array.isArray(uploadRows) ? uploadRows : []);
      } catch (loadError) {
        if (!active) return;
        setUploads([]);
      }
    };

    loadUploads();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadFilterOptions = async () => {
      setFilterOptionsLoading(true);
      try {
        const options = await apiGet('/api/burn-in/filter-options', queryParams);
        if (!active) return;
        setFilterOptions(options || {});
      } catch (loadError) {
        if (!active) return;
        setFilterOptions({});
      } finally {
        if (active) setFilterOptionsLoading(false);
      }
    };

    loadFilterOptions();
    return () => {
      active = false;
    };
  }, [queryParams, tx]);

  useEffect(() => {
    const options = Array.isArray(filterOptions.performanceTestNumber) ? filterOptions.performanceTestNumber : [];
    const selected = Array.isArray(filters.performanceTestNumber) ? filters.performanceTestNumber : [];
    if (
      pendingTemperaturePerformanceTestNumber
      && options.includes(pendingTemperaturePerformanceTestNumber)
      && !selected.includes(pendingTemperaturePerformanceTestNumber)
    ) {
      return;
    }
    setPendingTemperaturePerformanceTestNumber(getFirstUnselectedFilterOption(options, selected));
  }, [filterOptions.performanceTestNumber, filters.performanceTestNumber, pendingTemperaturePerformanceTestNumber]);

  useEffect(() => {
    let active = true;
    const loadTemperatureAnalysis = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiGet('/api/burn-in/temperature-analyzer', queryParams);
        if (!active) return;
        setPageData(data || null);
      } catch (loadError) {
        if (!active) return;
        setPageData(null);
        setError(loadError.response?.data?.error || tx(
          'Unable to load the burn-in temperature analysis.',
          'Impossibile caricare l’analisi temperatura del burn-in.'
        ));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadTemperatureAnalysis();
    return () => {
      active = false;
    };
  }, [queryParams, tx]);

  const summary = pageData?.summary || {};
  const temperatureSummary = useMemo(
    () => (Array.isArray(pageData?.temperatureSummary) ? pageData.temperatureSummary : []),
    [pageData]
  );
  const headerAnalysis = useMemo(
    () => (Array.isArray(pageData?.headerAnalysis) ? pageData.headerAnalysis : []),
    [pageData]
  );
  const stepAnalysis = useMemo(
    () => (Array.isArray(pageData?.stepAnalysis) ? pageData.stepAnalysis : []),
    [pageData]
  );
  const stepSensitivity = useMemo(
    () => (Array.isArray(pageData?.stepSensitivity) ? pageData.stepSensitivity : []),
    [pageData]
  );
  const stepCorrelation = useMemo(
    () => (Array.isArray(pageData?.stepCorrelation) ? pageData.stepCorrelation : []),
    [pageData]
  );
  const cycleDetails = useMemo(
    () => (Array.isArray(pageData?.cycleDetails) ? pageData.cycleDetails : []),
    [pageData]
  );
  const stepThermalVariationMap = useMemo(() => {
    const map = new Map();

    stepSensitivity.forEach((row) => {
      const key = `${row.sequence_text}|||${row.step_name}`;
      const temperatureCount = Number(row.temperature_count) || 0;
      const spread = Number(row.fail_rate_spread) || 0;
      map.set(key, {
        key,
        temperatureCount,
        spread,
        hasEnoughVariation: temperatureCount >= 2 && spread > 0,
        reason:
          temperatureCount < 2
            ? 'insufficient_temperatures'
            : spread > 0
              ? 'enough_variation'
              : 'flat_variation',
      });
    });

    stepCorrelation.forEach((row) => {
      const key = `${row.sequence_text}|||${row.step_name}`;
      const existing = map.get(key) || {
        key,
        temperatureCount: Number(row.temperature_count) || 0,
        spread: 0,
        hasEnoughVariation: false,
        reason: 'insufficient_temperatures',
      };
      const hasCorrelation = Number.isFinite(Number(row.temperature_fail_correlation));
      const temperatureCount = Math.max(existing.temperatureCount, Number(row.temperature_count) || 0);
      const hasEnoughVariation = existing.hasEnoughVariation || hasCorrelation;
      map.set(key, {
        ...existing,
        temperatureCount,
        hasCorrelation,
        hasEnoughVariation,
        reason: hasEnoughVariation
          ? 'enough_variation'
          : temperatureCount < 2
            ? 'insufficient_temperatures'
            : 'flat_variation',
      });
    });

    return map;
  }, [stepCorrelation, stepSensitivity]);
  const stepOptions = useMemo(() => {
    const optionMap = new Map();
    stepAnalysis.forEach((row) => {
      const key = `${row.sequence_text}|||${row.step_name}`;
      if (!optionMap.has(key)) {
        const variation = stepThermalVariationMap.get(key);
        const sensitivity = stepSensitivity.find((entry) => `${entry.sequence_text}|||${entry.step_name}` === key);
        const correlation = stepCorrelation.find((entry) => `${entry.sequence_text}|||${entry.step_name}` === key);
        const totalFails = Number(sensitivity?.total_fails) || 0;
        const spread = Number(sensitivity?.fail_rate_spread) || 0;
        const isCritical = totalFails > 0;
        const variationLabel = variation?.hasEnoughVariation
          ? tx('enough thermal variation', 'variazione termica sufficiente')
          : tx('low thermal variation', 'variazione termica bassa');
        optionMap.set(key, {
          key,
          sequence_text: row.sequence_text,
          step_name: row.step_name,
          label: `${row.sequence_text} · ${row.step_name}`,
          thermalVariation: variation || null,
          sensitivity: sensitivity || null,
          correlation: correlation || null,
          isCritical,
          optionLabel: isCritical
            ? `${tx('CRITICAL', 'CRITICO')} • ${row.sequence_text} · ${row.step_name} • ${tx('FAILs', 'FAIL')} ${formatNumber(totalFails)} • ${tx('Spread', 'Spread')} ${formatDecimal(spread)}%`
            : `${row.sequence_text} · ${row.step_name} (${variationLabel})`,
        });
      }
    });
    return Array.from(optionMap.values()).sort((left, right) => (
      Number(right.isCritical) - Number(left.isCritical)
      || Number(right.sensitivity?.total_fails || 0) - Number(left.sensitivity?.total_fails || 0)
      || Number(right.sensitivity?.fail_rate_spread || 0) - Number(left.sensitivity?.fail_rate_spread || 0)
      || String(left.sequence_text).localeCompare(String(right.sequence_text), undefined, { numeric: true, sensitivity: 'base' })
      || String(left.step_name).localeCompare(String(right.step_name), undefined, { sensitivity: 'base' })
    ));
  }, [stepAnalysis, stepCorrelation, stepSensitivity, stepThermalVariationMap, tx]);

  useEffect(() => {
    if (!selectedStepKey) return;
    const stillExists = stepOptions.some((option) => option.key === selectedStepKey);
    if (!stillExists) {
      setSelectedStepKey('');
    }
  }, [selectedStepKey, stepOptions]);

  const filteredStepAnalysis = useMemo(() => {
    if (!selectedStepKey) return stepAnalysis;
    return stepAnalysis.filter((row) => `${row.sequence_text}|||${row.step_name}` === selectedStepKey);
  }, [selectedStepKey, stepAnalysis]);

  const filteredStepSensitivity = useMemo(() => {
    if (!selectedStepKey) return stepSensitivity;
    return stepSensitivity.filter((row) => `${row.sequence_text}|||${row.step_name}` === selectedStepKey);
  }, [selectedStepKey, stepSensitivity]);

  const filteredStepCorrelation = useMemo(() => {
    if (!selectedStepKey) return stepCorrelation;
    return stepCorrelation.filter((row) => `${row.sequence_text}|||${row.step_name}` === selectedStepKey);
  }, [selectedStepKey, stepCorrelation]);

  const selectedStepOption = useMemo(
    () => stepOptions.find((option) => option.key === selectedStepKey) || null,
    [selectedStepKey, stepOptions]
  );
  const selectedStepVariation = selectedStepOption?.thermalVariation || null;
  const selectedStepIsCritical = Boolean(selectedStepOption?.isCritical);
  const criticalStepQuickPicks = useMemo(
    () => stepOptions
      .filter((option) => option.isCritical)
      .slice(0, 8),
    [stepOptions]
  );
  const stepPickerRows = useMemo(
    () => [...stepSensitivity]
      .sort((left, right) => (
        Number(right.total_fails) - Number(left.total_fails)
        || Number(right.fail_rate_spread) - Number(left.fail_rate_spread)
        || String(left.sequence_text).localeCompare(String(right.sequence_text), undefined, { numeric: true, sensitivity: 'base' })
      ))
      .slice(0, 24),
    [stepSensitivity]
  );
  const failTemperatureLabels = Array.isArray(summary.fail_temperature_labels) ? summary.fail_temperature_labels : [];
  const readinessSummaryText = useMemo(() => {
    if (!summary.analysis_readiness) return '';
    if (summary.analysis_readiness === 'insufficient_temperature_cycles') {
      return tx(
        'This scope does not have enough distinct temperature cycles yet for strong thermal conclusions.',
        'Questo perimetro non ha ancora abbastanza cicli di temperatura distinti per conclusioni termiche forti.'
      );
    }
    if (summary.analysis_readiness === 'fails_concentrated_in_too_few_temperatures') {
      return tx(
        `Fails are concentrated in only ${formatNumber(summary.fail_temperature_point_count || 0)} temperature point, so many correlations will remain limited.`,
        `I FAIL sono concentrati in solo ${formatNumber(summary.fail_temperature_point_count || 0)} punto temperatura, quindi molte correlazioni resteranno limitate.`
      );
    }
    if (summary.analysis_readiness === 'ready') {
      return tx(
        'This scope has enough temperature and fail variation to support meaningful thermal comparisons.',
        'Questo perimetro ha abbastanza variazione di temperatura e di FAIL per supportare confronti termici significativi.'
      );
    }
    return tx(
      'This scope has partial thermal evidence, but some steps may still be limited or flat.',
      'Questo perimetro ha evidenza termica parziale, ma alcuni step possono ancora essere limitati o piatti.'
    );
  }, [summary.analysis_readiness, summary.fail_temperature_point_count, tx]);

  const topHeaders = useMemo(() => {
    const totals = new Map();
    headerAnalysis.forEach((row) => {
      const key = `${row.header_sequence}|||${row.header_name}`;
      const existing = totals.get(key) || {
        key,
        header_sequence: row.header_sequence,
        header_name: row.header_name,
        fail_rows: 0,
      };
      existing.fail_rows += Number(row.fail_rows) || 0;
      totals.set(key, existing);
    });
    return Array.from(totals.values())
      .sort((left, right) => (
        Number(right.fail_rows) - Number(left.fail_rows)
        || String(left.header_sequence).localeCompare(String(right.header_sequence), undefined, { numeric: true, sensitivity: 'base' })
      ))
      .slice(0, 6);
  }, [headerAnalysis]);

  const temperatureOverviewData = useMemo(() => ({
    labels: temperatureSummary.map((row) => row.temperature_label),
    datasets: [
      {
        type: 'bar',
        label: tx('Failed rows', 'Righe FAIL'),
        data: temperatureSummary.map((row) => Number(row.fail_rows) || 0),
        backgroundColor: '#ef4444',
        borderRadius: 8,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: tx('Total rows', 'Righe totali'),
        data: temperatureSummary.map((row) => Number(row.total_rows) || 0),
        backgroundColor: 'rgba(15, 118, 110, 0.28)',
        borderColor: '#0f766e',
        borderWidth: 1,
        borderRadius: 8,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: tx('FAIL rate %', 'Tasso FAIL %'),
        data: temperatureSummary.map((row) => Number(row.fail_rate) || 0),
        borderColor: '#2563eb',
        backgroundColor: '#2563eb',
        pointBackgroundColor: '#2563eb',
        pointBorderColor: '#ffffff',
        pointRadius: 4,
        pointHoverRadius: 5,
        tension: 0.25,
        yAxisID: 'yPercent',
      },
    ],
  }), [temperatureSummary, tx]);

  const temperatureOverviewOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: tx('Row count', 'Numero righe'),
        },
      },
      yPercent: {
        position: 'right',
        beginAtZero: true,
        max: 100,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
        title: {
          display: true,
          text: tx('FAIL rate', 'Tasso FAIL'),
        },
      },
    },
  }), [tx]);

  const headerChartData = useMemo(() => ({
    labels: temperatureSummary.map((row) => row.temperature_label),
    datasets: topHeaders.map((header, index) => ({
      label: `${header.header_sequence} · ${header.header_name}`,
      data: temperatureSummary.map((temperatureRow) => {
        const matched = headerAnalysis.find((entry) => (
          String(entry.temperature_bucket) === String(temperatureRow.temperature_bucket)
          && String(entry.header_sequence) === String(header.header_sequence)
          && String(entry.header_name) === String(header.header_name)
        ));
        return Number(matched?.fail_rows) || 0;
      }),
      backgroundColor: palette[index % palette.length],
      borderRadius: 8,
    })),
  }), [headerAnalysis, temperatureSummary, topHeaders]);

  const headerChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: tx('Failed rows', 'Righe FAIL'),
        },
      },
    },
  }), [tx]);

  const sensitivityChartRows = useMemo(
    () => filteredStepSensitivity.slice(0, selectedStepKey ? 6 : 15),
    [filteredStepSensitivity, selectedStepKey]
  );
  const correlationChartRows = useMemo(
    () => filteredStepCorrelation
      .filter((row) => Number.isFinite(Number(row.temperature_fail_correlation)))
      .slice(0, selectedStepKey ? 6 : 15),
    [filteredStepCorrelation, selectedStepKey]
  );
  const selectedStepTrendRows = useMemo(
    () => [...filteredStepAnalysis].sort((left, right) => Number(left.temperature_bucket) - Number(right.temperature_bucket)),
    [filteredStepAnalysis]
  );
  const selectedSingleStepMode = Boolean(selectedStepKey && selectedStepOption);
  const toVisibleSingleBarValue = useCallback((value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    if (!selectedSingleStepMode) return numeric;
    if (Math.abs(numeric) >= 0.0001) return numeric;
    return numeric < 0 ? -0.0001 : 0.0001;
  }, [selectedSingleStepMode]);

  const sensitivityChartData = useMemo(() => ({
    labels: sensitivityChartRows.map((row) => `${row.sequence_text} · ${row.step_name}`),
    datasets: [
      {
        label: tx('FAIL-rate spread %', 'Spread tasso FAIL %'),
        data: sensitivityChartRows.map((row) => toVisibleSingleBarValue(row.fail_rate_spread)),
        backgroundColor: '#7c3aed',
        borderRadius: 8,
        minBarLength: selectedSingleStepMode ? 10 : 0,
      },
    ],
  }), [selectedSingleStepMode, sensitivityChartRows, toVisibleSingleBarValue, tx]);

  const sensitivityChartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: tx('FAIL-rate spread %', 'Spread tasso FAIL %'),
        },
      },
    },
  }), [tx]);

  const correlationChartData = useMemo(() => ({
    labels: correlationChartRows.map((row) => `${row.sequence_text} · ${row.step_name}`),
    datasets: [
      {
        label: tx('Temp/FAIL correlation', 'Correlazione temp/FAIL'),
        data: correlationChartRows.map((row) => toVisibleSingleBarValue(row.temperature_fail_correlation)),
        backgroundColor: correlationChartRows.map((row) => (
          Number(row.temperature_fail_correlation) >= 0 ? '#dc2626' : '#2563eb'
        )),
        borderRadius: 8,
        minBarLength: selectedSingleStepMode ? 10 : 0,
      },
    ],
  }), [correlationChartRows, selectedSingleStepMode, toVisibleSingleBarValue, tx]);

  const correlationChartOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        min: -1,
        max: 1,
        title: {
          display: true,
          text: tx('Correlation coefficient', 'Coefficiente di correlazione'),
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          afterBody: (items) => {
            const row = correlationChartRows[items?.[0]?.dataIndex];
            if (!row) return [];
            return [
              `${tx('Direction', 'Direzione')}: ${row.correlation_direction || '-'}`,
              `${tx('Strength', 'Forza')}: ${row.correlation_strength || '-'}`,
              `${tx('Cold', 'Freddo')}: ${row.coldest_temperature_label || '-'} -> ${formatDecimal(row.coldest_fail_rate)}%`,
              `${tx('Hot', 'Caldo')}: ${row.hottest_temperature_label || '-'} -> ${formatDecimal(row.hottest_fail_rate)}%`,
              `${tx('Hot-cold delta', 'Delta caldo-freddo')}: ${formatDecimal(row.fail_rate_delta_hot_vs_cold)}%`,
            ];
          },
        },
      },
    },
  }), [correlationChartRows, tx]);
  const selectedStepTrendData = useMemo(() => ({
    labels: selectedStepTrendRows.map((row) => row.temperature_label),
    datasets: [
      {
        type: 'bar',
        label: tx('Failed rows', 'Righe FAIL'),
        data: selectedStepTrendRows.map((row) => Number(row.fail_rows) || 0),
        backgroundColor: '#ef4444',
        borderRadius: 8,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: tx('FAIL rate %', 'Tasso FAIL %'),
        data: selectedStepTrendRows.map((row) => Number(row.fail_rate) || 0),
        borderColor: '#2563eb',
        backgroundColor: '#2563eb',
        pointBackgroundColor: '#2563eb',
        pointBorderColor: '#ffffff',
        pointRadius: 4,
        pointHoverRadius: 5,
        tension: 0.22,
        yAxisID: 'yPercent',
      },
      {
        type: 'line',
        label: tx('Total rows', 'Righe totali'),
        data: selectedStepTrendRows.map((row) => Number(row.total_rows) || 0),
        borderColor: '#0f766e',
        backgroundColor: '#0f766e',
        pointBackgroundColor: '#0f766e',
        pointBorderColor: '#ffffff',
        pointRadius: 3,
        pointHoverRadius: 4,
        tension: 0.2,
        yAxisID: 'y',
      },
    ],
  }), [selectedStepTrendRows, tx]);
  const selectedStepTrendOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: tx('Row count', 'Numero righe'),
        },
      },
      yPercent: {
        position: 'right',
        beginAtZero: true,
        max: 100,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
        title: {
          display: true,
          text: tx('FAIL rate', 'Tasso FAIL'),
        },
      },
    },
  }), [tx]);

  const headerTableRows = useMemo(() => headerAnalysis.slice(0, 30), [headerAnalysis]);
  const stepTableRows = useMemo(() => filteredStepAnalysis.slice(0, 40), [filteredStepAnalysis]);
  const stepCorrelationRows = useMemo(() => filteredStepCorrelation.slice(0, 40), [filteredStepCorrelation]);
  const cycleTableRows = useMemo(() => cycleDetails.slice(0, 50), [cycleDetails]);
  const headerInsightRows = useMemo(() => headerAnalysis.slice(0, 8), [headerAnalysis]);
  const stepInsightRows = useMemo(() => filteredStepAnalysis.slice(0, 10), [filteredStepAnalysis]);
  const correlationInsightRows = useMemo(() => correlationChartRows.slice(0, 8), [correlationChartRows]);
  const selectedStepSensitivitySummary = useMemo(
    () => (selectedStepOption && filteredStepSensitivity.length ? filteredStepSensitivity[0] : null),
    [filteredStepSensitivity, selectedStepOption]
  );
  const selectedStepCorrelationSummary = useMemo(
    () => (selectedStepOption && filteredStepCorrelation.length ? filteredStepCorrelation[0] : null),
    [filteredStepCorrelation, selectedStepOption]
  );
  const selectedStepTemperatureDetailRows = useMemo(
    () => [...filteredStepAnalysis].sort((left, right) => Number(left.temperature_bucket) - Number(right.temperature_bucket)),
    [filteredStepAnalysis]
  );
  const selectedStepHasVisibleSensitivity = useMemo(
    () => sensitivityChartRows.some((row) => Number(row.fail_rate_spread) > 0),
    [sensitivityChartRows]
  );
  const selectedStepHasCorrelationValue = useMemo(
    () => correlationChartRows.some((row) => Number.isFinite(Number(row.temperature_fail_correlation))),
    [correlationChartRows]
  );

  const exportBaseName = useMemo(() => (
    `burn-in-temperature-analyzer${selectedUploadId ? `-upload-${selectedUploadId}` : ''}`
  ), [selectedUploadId]);

  const metricCards = [
    { label: tx('Temperature cycles', 'Cicli temperatura'), value: formatNumber(summary.cycle_count) },
    { label: tx('Temperature points', 'Punti temperatura'), value: formatNumber(summary.temperature_point_count) },
    { label: tx('Fail-bearing temp points', 'Punti temperatura con FAIL'), value: formatNumber(summary.fail_temperature_point_count) },
    { label: tx('Total failed rows', 'Righe FAIL totali'), value: formatNumber(summary.total_fails) },
    { label: tx('Overall FAIL rate', 'Tasso FAIL complessivo'), value: `${formatDecimal(summary.overall_fail_rate)}%` },
    { label: tx('Worst temperature', 'Temperatura peggiore'), value: summary.worst_temperature_label || '-' },
    { label: tx('Worst FAIL rate', 'Peggior tasso FAIL'), value: `${formatDecimal(summary.worst_temperature_fail_rate)}%` },
    { label: tx('Most critical performance test', 'Test performance più critico'), value: summary.hottest_header_name || '-' },
    { label: tx('Performance-test FAIL rows', 'FAIL test performance'), value: formatNumber(summary.hottest_header_fail_rows) },
    { label: tx('Overall temp/FAIL corr.', 'Corr. temp/FAIL totale'), value: Number.isFinite(Number(summary.overall_temperature_fail_correlation)) ? formatDecimal(summary.overall_temperature_fail_correlation, 3) : '-' },
    { label: tx('Overall trend', 'Trend complessivo'), value: summary.overall_temperature_fail_trend || '-' },
    { label: tx('Strongest step corr.', 'Corr. step più forte'), value: Number.isFinite(Number(summary.strongest_step_correlation)) ? formatDecimal(summary.strongest_step_correlation, 3) : '-' },
    { label: tx('Most correlated step', 'Step più correlato'), value: summary.strongest_step_name || '-' },
    { label: tx('Steps ready for sensitivity', 'Step pronti per sensibilità'), value: formatNumber(summary.steps_ready_for_sensitivity) },
    { label: tx('Steps ready for correlation', 'Step pronti per correlazione'), value: formatNumber(summary.steps_ready_for_correlation) },
  ];

  const temperatureSummaryColumns = [
    { key: 'temperature_label', label: tx('Temperature', 'Temperatura') },
    { key: 'average_actual_temperature', label: tx('Actual avg temp', 'Temp media reale'), format: (value) => formatDecimal(value, 2) },
    { key: 'cycle_count', label: tx('Cycles', 'Cicli'), format: formatNumber },
    { key: 'source_count', label: tx('Source files', 'File sorgente'), format: formatNumber },
    { key: 'total_rows', label: tx('Total rows', 'Righe totali'), format: formatNumber },
    { key: 'fail_rows', label: tx('Failed rows', 'Righe FAIL'), format: formatNumber },
    { key: 'fail_rate', label: tx('FAIL rate %', 'Tasso FAIL %'), format: (value) => formatDecimal(value) },
  ];

  const headerAnalysisColumns = [
    { key: 'temperature_label', label: 'Temperatura' },
    { key: 'header_sequence', label: 'Perf. test n°' },
    { key: 'header_name', label: 'Performance test' },
    { key: 'cycle_count', label: 'Cicli', format: formatNumber },
    { key: 'total_rows', label: 'Righe totali', format: formatNumber },
    { key: 'fail_rows', label: 'Righe FAIL', format: formatNumber },
    { key: 'fail_rate', label: 'Tasso FAIL %', format: (value) => formatDecimal(value) },
  ];

  const stepSensitivityColumns = [
    { key: 'sequence_text', label: 'N. step' },
    { key: 'step_name', label: 'Nome step' },
    { key: 'performance_test_numbers', label: 'Perf. test n°', render: (_value, row) => formatPerformanceTestCoverage(row) },
    { key: 'temperature_count', label: 'Temp viste', format: formatNumber },
    { key: 'total_rows', label: 'Righe totali', format: formatNumber },
    { key: 'total_fails', label: 'FAIL totali', format: formatNumber },
    { key: 'min_fail_rate', label: 'FAIL min %', format: (value) => formatDecimal(value) },
    { key: 'max_fail_rate', label: 'FAIL max %', format: (value) => formatDecimal(value) },
    { key: 'fail_rate_spread', label: 'Spread %', format: (value) => formatDecimal(value) },
    { key: 'worst_temperature_label', label: 'Temp peggiore' },
  ];

  const stepAnalysisColumns = [
    { key: 'temperature_label', label: 'Temperatura' },
    { key: 'sequence_text', label: 'N. step' },
    { key: 'step_name', label: 'Nome step' },
    { key: 'performance_test_numbers', label: 'Perf. test n°', render: (_value, row) => formatPerformanceTestCoverage(row) },
    { key: 'cycle_count', label: 'Cicli', format: formatNumber },
    { key: 'total_rows', label: 'Righe totali', format: formatNumber },
    { key: 'fail_rows', label: 'Righe FAIL', format: formatNumber },
    { key: 'fail_rate', label: 'Tasso FAIL %', format: (value) => formatDecimal(value) },
  ];

  const stepCorrelationColumns = [
    { key: 'sequence_text', label: 'N. step' },
    { key: 'step_name', label: 'Nome step' },
    { key: 'performance_test_numbers', label: 'Perf. test n°', render: (_value, row) => formatPerformanceTestCoverage(row) },
    { key: 'temperature_count', label: 'Temp viste', format: formatNumber },
    { key: 'total_fails', label: 'FAIL totali', format: formatNumber },
    { key: 'temperature_fail_correlation', label: 'Correlazione', format: (value) => formatDecimal(value, 3) },
    { key: 'correlation_strength', label: 'Forza' },
    { key: 'correlation_direction', label: 'Direzione' },
    { key: 'coldest_temperature_label', label: 'Temp più fredda' },
    { key: 'coldest_fail_rate', label: 'FAIL freddo %', format: (value) => formatDecimal(value) },
    { key: 'hottest_temperature_label', label: 'Temp più calda' },
    { key: 'hottest_fail_rate', label: 'FAIL caldo %', format: (value) => formatDecimal(value) },
    { key: 'fail_rate_delta_hot_vs_cold', label: 'Delta caldo-freddo %', format: (value) => formatDecimal(value) },
  ];

  const cycleDetailsColumns = [
    { key: 'temperature_label', label: 'Temperatura' },
    { key: 'source_label', label: 'File sorgente' },
    { key: 'uut_serial_number', label: 'Numero seriale' },
    { key: 'version_sw', label: 'Versione SW' },
    { key: 'version_hw', label: 'Versione HW' },
    { key: 'total_rows', label: 'Righe totali', format: formatNumber },
    { key: 'fail_rows', label: 'Righe FAIL', format: formatNumber },
    { key: 'fail_rate', label: 'Tasso FAIL %', format: (value) => formatDecimal(value) },
    { key: 'first_fail_step', label: 'Primo step FAIL' },
    { key: 'first_measured_at', label: 'Inizio', format: formatDateTime },
    { key: 'last_measured_at', label: 'Fine', format: formatDateTime },
  ];

  const renderInsightCards = (rows, renderBody) => (
    <div className={styles.insightGrid}>
      {rows.length ? rows.map((row, index) => (
        <article
          key={`${row.sequence_text || row.header_sequence || row.temperature_label || row.source_label || 'insight'}-${index}`}
          className={styles.insightCard}
        >
          {renderBody(row)}
        </article>
      )) : (
        <div className={styles.emptyState}>{tx('No ranked items are available for this scope.', 'Nessun elemento classificato disponibile per questo perimetro.')}</div>
      )}
    </div>
  );

  const classifyTemperatureTableRow = useCallback((row) => {
    const failRows = Number(row?.fail_rows) || 0;
    const totalFails = Number(row?.total_fails) || 0;
    const failRate = Number(row?.fail_rate) || 0;
    const spread = Number(row?.fail_rate_spread) || 0;
    const absoluteCorrelation = Number(row?.absolute_correlation);
    const totalRows = Number(row?.total_rows) || 0;

    if (failRows > 0 || totalFails > 0) {
      if (
        failRate >= 20
        || spread > 0
        || totalFails >= 3
        || failRows >= 3
        || (Number.isFinite(absoluteCorrelation) && absoluteCorrelation >= 0.5)
        || totalRows === 0
      ) {
        return { label: tx('Critical', 'Critico'), score: 2, className: styles.tableRowCritical, badgeClassName: styles.criticalBadge };
      }
      return { label: tx('Warning', 'Attenzione'), score: 1, className: styles.tableRowWarning, badgeClassName: styles.warningBadge };
    }

    if ((Number.isFinite(absoluteCorrelation) && absoluteCorrelation >= 0.3) || failRate > 0) {
      return { label: tx('Warning', 'Attenzione'), score: 1, className: styles.tableRowWarning, badgeClassName: styles.warningBadge };
    }

    return { label: tx('Stable', 'Stabile'), score: 0, className: '', badgeClassName: styles.stableBadge };
  }, [tx]);

  const compareTemperatureTableValues = useCallback((leftValue, rightValue) => {
    const leftNumber = Number(leftValue);
    const rightNumber = Number(rightValue);
    const leftIsNumber = Number.isFinite(leftNumber);
    const rightIsNumber = Number.isFinite(rightNumber);

    if (leftIsNumber && rightIsNumber) {
      return leftNumber - rightNumber;
    }

    if (leftValue instanceof Date && rightValue instanceof Date) {
      return leftValue.getTime() - rightValue.getTime();
    }

    return String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }, []);

  const sortableTemperatureTableRows = useMemo(() => {
    if (!temperatureTableModal?.rows) return [];
    const rowsWithSeverity = temperatureTableModal.rows.map((row) => ({
      ...row,
      __severity: classifyTemperatureTableRow(row),
    }));
    const { key, direction } = temperatureTableSort || {};
    if (!key) return rowsWithSeverity;

    return [...rowsWithSeverity].sort((left, right) => {
      let comparison = 0;
      if (key === '__severity') {
        comparison = (left.__severity?.score || 0) - (right.__severity?.score || 0);
      } else {
        comparison = compareTemperatureTableValues(left[key], right[key]);
      }

      if (comparison === 0) {
        comparison = compareTemperatureTableValues(
          left.sequence_text || left.header_sequence || left.temperature_label || left.source_label || '',
          right.sequence_text || right.header_sequence || right.temperature_label || right.source_label || ''
        );
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [classifyTemperatureTableRow, compareTemperatureTableValues, temperatureTableModal, temperatureTableSort]);

  const criticalTemperatureTableCount = useMemo(
    () => sortableTemperatureTableRows.filter((row) => row.__severity?.score === 2).length,
    [sortableTemperatureTableRows]
  );

  const toggleTemperatureTableSort = (columnKey) => {
    setTemperatureTableSort((current) => {
      if (current.key === columnKey) {
        return {
          key: columnKey,
          direction: current.direction === 'desc' ? 'asc' : 'desc',
        };
      }
      return {
        key: columnKey,
        direction: columnKey === '__severity' ? 'desc' : 'asc',
      };
    });
  };

  const renderTable = (columns, rows, options = {}) => (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>#</th>
            {options.sortable ? (
              <th key="__severity">
                <button
                  type="button"
                  className={styles.tableSortButton}
                  onClick={() => toggleTemperatureTableSort('__severity')}
                >
                  <span>{tx('Status', 'Stato')}</span>
                  <span className={styles.tableSortIndicator}>
                    {temperatureTableSort.key === '__severity'
                      ? (temperatureTableSort.direction === 'asc' ? '↑' : '↓')
                      : '↕'}
                  </span>
                </button>
              </th>
            ) : null}
            {columns.map((column) => (
              <th key={column.key}>
                {options.sortable ? (
                  <button
                    type="button"
                    className={styles.tableSortButton}
                    onClick={() => toggleTemperatureTableSort(column.key)}
                  >
                    <span>{column.label}</span>
                    <span className={styles.tableSortIndicator}>
                      {temperatureTableSort.key === column.key
                        ? (temperatureTableSort.direction === 'asc' ? '↑' : '↓')
                        : '↕'}
                    </span>
                  </button>
                ) : column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr
              key={`${row.cycle_key || row.sequence_text || row.temperature_bucket || 'row'}-${index}`}
              className={options.sortable ? row.__severity?.className : ''}
            >
              <td>{index + 1}</td>
              {options.sortable ? (
                <td key="__severity">
                  <span className={row.__severity?.badgeClassName}>{row.__severity?.label}</span>
                </td>
              ) : null}
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render ? column.render(row[column.key], row) : toExportCell(row[column.key], column.format)}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td className={styles.emptyTableCell} colSpan={columns.length + (options.sortable ? 2 : 1)}>
                {tx('No rows are available for the selected burn-in scope.', 'Nessuna riga disponibile per il perimetro burn-in selezionato.')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const temperatureChartModalSizeClass = temperatureChartModalSize === 'full'
    ? styles.chartModalFull
    : temperatureChartModalSize === 'medium'
      ? styles.chartModalMedium
      : styles.chartModalLarge;
  const temperatureChartModalBodyClass = temperatureChartModalSize === 'full'
    ? styles.chartModalBodyFull
    : temperatureChartModalSize === 'medium'
      ? styles.chartModalBodyMedium
      : styles.chartModalBodyLarge;

  const openTemperatureChartModal = (chartKey) => {
    setTemperatureChartModalKey(chartKey);
    setTemperatureChartModalSize('large');
  };

  const closeTemperatureChartModal = () => {
    setTemperatureChartModalKey('');
    setTemperatureChartModalSize('large');
  };

  const openTemperatureTableModal = (config) => {
    setTemperatureTableSort({ key: '__severity', direction: 'desc' });
    setTemperatureTableModal(config);
  };

  const closeTemperatureTableModal = () => {
    setTemperatureTableModal(null);
  };

  const renderTableLauncher = (title, description, columns, rows, fileSuffix, pdfTitle, disabled = false) => (
    <div className={styles.expandablePanel}>
      <div className={styles.expandableSummary}>
        <div>
          <strong>{title}</strong>
          <span>{description}</span>
        </div>
        <div className={styles.exportActions}>
          <button
            type="button"
            className={styles.exportButton}
            onClick={() => openTemperatureTableModal({
              title,
              description,
              columns,
              rows,
              fileSuffix,
              pdfTitle,
            })}
            disabled={disabled}
          >
            {tx('Open details table', 'Apri tabella dettagli')}
          </button>
        </div>
      </div>
    </div>
  );

  const activeTemperatureChartModal = useMemo(() => {
    if (!temperatureChartModalKey) return null;

    if (temperatureChartModalKey === 'selected-step') {
      return {
        title: tx('Temperature analysis for selected step', 'Analisi temperatura per step selezionato'),
        fileSuffix: 'selected-step-temperature-trend',
        exportRef: selectedStepChartRef,
        isEmpty: loading || !selectedStepOption || !selectedStepTrendRows.length,
        render: () => {
          if (loading) {
            return <div className={styles.emptyState}>{tx('Loading selected-step analysis...', 'Caricamento analisi step selezionato...')}</div>;
          }
          if (!selectedStepOption) {
            return <div className={styles.emptyState}>{tx('Select a step to analyze it in detail against temperature.', 'Seleziona uno step per analizzarlo in dettaglio rispetto alla temperatura.')}</div>;
          }
          if (!selectedStepTrendRows.length) {
            return <div className={styles.emptyState}>{tx('No data is available for the selected step in the current scope.', 'Nessun dato disponibile per lo step selezionato nel perimetro attuale.')}</div>;
          }
          return <Chart type="bar" data={selectedStepTrendData} options={selectedStepTrendOptions} />;
        },
      };
    }

    if (temperatureChartModalKey === 'overview') {
      return {
        title: tx('Temperature overview', 'Panoramica temperatura'),
        fileSuffix: 'temperature-overview',
        exportRef: overviewChartRef,
        isEmpty: loading || !temperatureSummary.length,
        render: () => {
          if (loading) {
            return <div className={styles.emptyState}>{tx('Loading burn-in temperature overview...', 'Caricamento panoramica temperatura burn-in...')}</div>;
          }
          if (!temperatureSummary.length) {
            return <div className={styles.emptyState}>{tx('No temperature cycles were found in the selected burn-in scope.', 'Nessun ciclo di temperatura trovato nel perimetro burn-in selezionato.')}</div>;
          }
          return <Chart type="bar" data={temperatureOverviewData} options={temperatureOverviewOptions} />;
        },
      };
    }

    if (temperatureChartModalKey === 'headers') {
      return {
        title: tx('Performance headers most affected by temperature', 'Header performance più influenzati dalla temperatura'),
        fileSuffix: 'header-fails-by-temperature',
        exportRef: headerChartRef,
        isEmpty: loading || !topHeaders.length || !temperatureSummary.length,
        render: () => {
          if (loading) {
            return <div className={styles.emptyState}>{tx('Loading temperature analysis for performance headers...', 'Caricamento analisi temperatura per header performance...')}</div>;
          }
          if (!topHeaders.length || !temperatureSummary.length) {
            return <div className={styles.emptyState}>{tx('No FAIL clusters were found for performance headers in the selected scope.', 'Nessun cluster FAIL degli header performance trovato per il perimetro selezionato.')}</div>;
          }
          return <Bar data={headerChartData} options={headerChartOptions} />;
        },
      };
    }

    if (temperatureChartModalKey === 'sensitivity') {
      return {
        title: tx('Detailed steps most sensitive to temperature', 'Step di dettaglio più sensibili alla temperatura'),
        fileSuffix: 'temperature-sensitive-steps',
        exportRef: sensitivityChartRef,
        isEmpty: loading || !sensitivityChartRows.length,
        render: () => {
          if (loading) {
            return <div className={styles.emptyState}>{tx('Loading temperature-sensitive step analysis...', 'Caricamento analisi step sensibili alla temperatura...')}</div>;
          }
          if (selectedStepOption && selectedStepSensitivitySummary && !selectedStepHasVisibleSensitivity) {
            return (
              <div className={styles.insightGrid}>
                <article className={styles.insightCard}>
                  <span className={styles.insightEyebrow}>{selectedStepSensitivitySummary.sequence_text}</span>
                  <strong className={styles.insightTitle}>{selectedStepSensitivitySummary.step_name}</strong>
                  <div className={styles.insightStats}>
                    <span>{tx('Temperatures seen', 'Temperature viste')}: {formatNumber(selectedStepSensitivitySummary.temperature_count)}</span>
                    <span>{tx('Min FAIL rate', 'FAIL minimo')}: {formatDecimal(selectedStepSensitivitySummary.min_fail_rate)}%</span>
                    <span>{tx('Max FAIL rate', 'FAIL massimo')}: {formatDecimal(selectedStepSensitivitySummary.max_fail_rate)}%</span>
                    <span>{tx('Spread', 'Spread')}: {formatDecimal(selectedStepSensitivitySummary.fail_rate_spread)}%</span>
                  </div>
                </article>
              </div>
            );
          }
          if (!sensitivityChartRows.length) {
            return <div className={styles.emptyState}>{tx('No step-sensitivity data is available for the selected filters.', 'Nessun dato di sensibilità dello step disponibile per i filtri selezionati.')}</div>;
          }
          return <Bar data={sensitivityChartData} options={sensitivityChartOptions} />;
        },
      };
    }

    if (temperatureChartModalKey === 'correlation') {
      return {
        title: tx('Correlation between temperature and FAIL', 'Correlazione tra temperatura e FAIL'),
        fileSuffix: 'temperature-fail-correlation',
        exportRef: correlationChartRef,
        isEmpty: loading || !correlationChartRows.length,
        render: () => {
          if (loading) {
            return <div className={styles.emptyState}>{tx('Loading temperature/FAIL correlation...', 'Caricamento correlazione temperatura/FAIL...')}</div>;
          }
          if (selectedStepOption && selectedStepCorrelationSummary && !selectedStepHasCorrelationValue) {
            return (
              <div className={styles.insightGrid}>
                <article className={styles.insightCard}>
                  <span className={styles.insightEyebrow}>{selectedStepCorrelationSummary.sequence_text}</span>
                  <strong className={styles.insightTitle}>{selectedStepCorrelationSummary.step_name}</strong>
                  <div className={styles.insightStats}>
                    <span>{tx('Temperatures seen', 'Temperature viste')}: {formatNumber(selectedStepCorrelationSummary.temperature_count)}</span>
                    <span>{tx('Total FAILs', 'FAIL totali')}: {formatNumber(selectedStepCorrelationSummary.total_fails)}</span>
                    <span>{tx('Cold FAIL rate', 'FAIL freddo')}: {formatDecimal(selectedStepCorrelationSummary.coldest_fail_rate)}%</span>
                    <span>{tx('Hot FAIL rate', 'FAIL caldo')}: {formatDecimal(selectedStepCorrelationSummary.hottest_fail_rate)}%</span>
                  </div>
                </article>
              </div>
            );
          }
          if (!correlationChartRows.length) {
            return <div className={styles.emptyState}>{tx('Not enough temperature points were found to calculate a meaningful correlation.', 'Non sono stati trovati abbastanza punti di temperatura per calcolare una correlazione significativa.')}</div>;
          }
          return <Bar data={correlationChartData} options={correlationChartOptions} />;
        },
      };
    }

    return null;
  }, [
    correlationChartData,
    correlationChartOptions,
    correlationChartRows.length,
    headerChartData,
    headerChartOptions,
    loading,
    selectedStepCorrelationSummary,
    selectedStepHasCorrelationValue,
    selectedStepHasVisibleSensitivity,
    selectedStepOption,
    selectedStepSensitivitySummary,
    selectedStepTrendData,
    selectedStepTrendOptions,
    selectedStepTrendRows.length,
    sensitivityChartData,
    sensitivityChartOptions,
    sensitivityChartRows.length,
    temperatureChartModalKey,
    temperatureOverviewData,
    temperatureOverviewOptions,
    temperatureSummary.length,
    topHeaders.length,
    tx,
  ]);

  return (
    <div className={styles.container}>
      <GoToDashboardButton />

      <div className={styles.content}>
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>{tx('Burn-in thermal behavior', 'Comportamento termico burn-in')}</p>
            <h1 className={styles.title}>{tx('Temperature Analyzer', 'Analizzatore Temperatura')}</h1>
            <p className={styles.description}>
              {tx(
                'This page reconstructs the burn-in temperature phases starting from step ',
                'Questa pagina ricostruisce le fasi di temperatura del burn-in partendo dallo step '
              )}
              <strong>0.1 Test Chamber Temperature</strong>
              {tx(
                ', then measures which performance-test headers and detailed steps get worse as temperature changes.',
                ', poi misura quali header dei test di performance e quali step di dettaglio peggiorano al variare della temperatura.'
              )}
            </p>
          </div>

          <aside className={styles.scopeSummary}>
            <span>{tx('Scope', 'Perimetro')}</span>
            <strong>{selectedUploadId ? `Upload #${selectedUploadId}` : tx('All uploads', 'Tutti gli upload')}</strong>
            <small>{loading ? tx('Refreshing analysis...', 'Aggiornamento analisi...') : tx(`${formatNumber(summary.cycle_count)} temperature cycles found`, `${formatNumber(summary.cycle_count)} cicli temperatura trovati`)}</small>
          </aside>
        </header>

        <section className={styles.navSection}>
          <div className={styles.navActions}>
            <button type="button" className={styles.hubButton} onClick={() => navigate('/burn-in-dashboard')}>
              {tx('Back to Burn-In Dashboard', 'Torna alla Dashboard Burn-In')}
            </button>
            <button type="button" className={styles.inlineGhostButton} onClick={() => navigate('/burn-in-control-chart')}>
              {tx('Open Burn-In Control Chart', 'Apri Carta di Controllo Burn-In')}
            </button>
            <button type="button" className={styles.inlineGhostButton} onClick={() => navigate('/burn-in-pareto-chart')}>
              {tx('Open Burn-In Pareto Chart', 'Apri Grafico Pareto Burn-In')}
            </button>
          </div>
        </section>

        <section className={styles.filterBar}>
          <div>
            <h3>{tx('Choose the burn-in scope', 'Scegli il perimetro burn-in')}</h3>
            <p>{tx('Select an upload or keep the whole burn-in database visible before refining the thermal analysis.', 'Seleziona un upload oppure mantieni visibile l’intero database burn-in prima di affinare l’analisi termica.')}</p>
          </div>

          <select
            className={styles.select}
            value={selectedUploadId}
            onChange={(event) => setSelectedUploadId(event.target.value)}
          >
            <option value="">{tx('All burn-in uploads', 'Tutti gli upload burn-in')}</option>
            {uploads.map((upload) => (
              <option key={upload.id} value={upload.id}>
                #{upload.id} · {upload.original_filename || `Upload ${upload.id}`}
              </option>
            ))}
          </select>
        </section>

        <section className={styles.advancedFilterPanel}>
          <div className={styles.advancedFilterHeader}>
            <div>
              <h3>{tx('Thermal filters', 'Filtri termici')}</h3>
              <p>{tx('Focus the analysis by metadata, source file, serial number, line, software version, and measured date.', 'Concentra l’analisi per metadati, file sorgente, numero seriale, linea, versione software e data di misura.')}</p>
            </div>
            <div className={styles.filterSummary}>
              <span>{filterOptionsLoading ? tx('Refreshing filter options', 'Aggiornamento opzioni filtro') : tx('Advanced filters ready', 'Filtri avanzati pronti')}</span>
              <button
                type="button"
                className={styles.exportButton}
                onClick={() => {
                  setFilters(buildDefaultBurnInFilters());
                  setPendingTemperaturePerformanceTestNumber('');
                }}
              >
                {tx('Reset filters', 'Reimposta filtri')}
              </button>
            </div>
          </div>

          <div className={styles.filterGrid}>
            <label className={`${styles.filterField} ${styles.controlChartSeriesField}`}>
              <span>{tx('Step to analyze', 'Step da analizzare')}</span>
              <select
                className={`${styles.filterInput} ${selectedStepIsCritical ? styles.filterInputCritical : ''}`}
                value={selectedStepKey}
                onChange={(event) => setSelectedStepKey(event.target.value)}
              >
                <option value="">{tx('All steps', 'Tutti gli step')}</option>
                {stepOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.optionLabel || option.label}
                  </option>
                ))}
              </select>
              {selectedStepOption ? (
                <>
                  <small className={`${styles.stepSelectorHint} ${selectedStepIsCritical ? styles.stepSelectorHintCritical : ''}`}>
                    {selectedStepIsCritical
                      ? tx(
                        'This selected step is currently ranked as critical in the thermal analysis.',
                        'Lo step selezionato è attualmente classificato come critico nell’analisi termica.'
                      )
                      : null}
                    {selectedStepIsCritical ? ' ' : ''}
                    {selectedStepVariation?.hasEnoughVariation
                      ? tx(
                        `This step has enough thermal variation for sensitivity analysis across ${formatNumber(selectedStepVariation.temperatureCount)} temperature buckets.`,
                        `Questo step ha abbastanza variazione termica per l’analisi di sensibilità su ${formatNumber(selectedStepVariation.temperatureCount)} bucket di temperatura.`
                      )
                      : selectedStepVariation?.reason === 'insufficient_temperatures'
                        ? tx(
                          `This step does not have enough thermal variation yet because it appears in only ${formatNumber(selectedStepVariation?.temperatureCount || 0)} temperature bucket.`,
                          `Questo step non ha ancora abbastanza variazione termica perché compare in solo ${formatNumber(selectedStepVariation?.temperatureCount || 0)} bucket di temperatura.`
                        )
                        : tx(
                          `This step is present across ${formatNumber(selectedStepVariation?.temperatureCount || 0)} temperature buckets, but its FAIL behavior is currently flat.`,
                          `Questo step è presente in ${formatNumber(selectedStepVariation?.temperatureCount || 0)} bucket di temperatura, ma il suo comportamento FAIL è attualmente piatto.`
                        )}
                    {selectedStepCorrelationSummary ? ` ${tx(
                      `Fail-bearing temperature points: ${formatNumber(selectedStepCorrelationSummary.fail_temperature_point_count || 0)}.`,
                      `Punti temperatura con FAIL: ${formatNumber(selectedStepCorrelationSummary.fail_temperature_point_count || 0)}.`
                    )}` : ''}
                  </small>
                  <div className={styles.stepSelectorDetails}>
                    {selectedStepOption?.sensitivity?.total_fails > 0 ? (
                      <span className={styles.stepSelectorDetailDanger}>
                        {tx('FAILs', 'FAIL')}: {formatNumber(selectedStepOption.sensitivity.total_fails)}
                      </span>
                    ) : null}
                    {selectedStepOption?.sensitivity ? (
                      <span>
                        {tx('Spread', 'Spread')}: {formatDecimal(selectedStepOption.sensitivity.fail_rate_spread)}%
                      </span>
                    ) : null}
                    {selectedStepOption?.sensitivity ? (
                      <span>
                        {tx('Worst temp', 'Temp peggiore')}: {selectedStepOption.sensitivity.worst_temperature_label || '-'}
                      </span>
                    ) : null}
                    {selectedStepOption?.correlation ? (
                      <span>
                        {tx('Fail temp points', 'Punti temp FAIL')}: {formatNumber(selectedStepOption.correlation.fail_temperature_point_count || 0)}
                      </span>
                    ) : null}
                    {selectedStepOption?.correlation && Number.isFinite(Number(selectedStepOption.correlation.temperature_fail_correlation)) ? (
                      <span>
                        {tx('Correlation', 'Correlazione')}: {formatDecimal(selectedStepOption.correlation.temperature_fail_correlation, 3)}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : (
                <small className={styles.stepSelectorHint}>
                  {tx(
                    'Each step now shows whether it has enough thermal variation for sensitivity/correlation analysis.',
                    'Ogni step mostra ora se ha abbastanza variazione termica per l’analisi di sensibilità/correlazione.'
                  )}
                </small>
              )}
              {criticalStepQuickPicks.length ? (
                <div className={styles.stepSelectorQuickPicks}>
                  <span className={styles.stepSelectorQuickPicksLabel}>
                    {tx('Critical quick picks', 'Scelte rapide critiche')}
                  </span>
                  <div className={styles.stepSelectorQuickPicksGrid}>
                    {criticalStepQuickPicks.map((option) => {
                      const isActive = option.key === selectedStepKey;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          className={`${styles.stepSelectorQuickPick} ${isActive ? styles.stepSelectorQuickPickActive : ''}`}
                          onClick={() => setSelectedStepKey(option.key)}
                        >
                          <strong>{option.sequence_text} · {option.step_name}</strong>
                          <span>
                            {tx('FAILs', 'FAIL')}: {formatNumber(option.sensitivity?.total_fails || 0)}
                            {' · '}
                            {tx('Spread', 'Spread')}: {formatDecimal(option.sensitivity?.fail_rate_spread || 0)}%
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </label>

            {burnInDateFilterFields.map((field) => (
              <label key={field.key} className={styles.filterField}>
                <span>{field.label}</span>
                <input
                  className={styles.filterInput}
                  type="date"
                  value={filters[field.key]}
                  onChange={(event) => setFilters((current) => ({ ...current, [field.key]: event.target.value }))}
                />
              </label>
            ))}

            {burnInTemperatureFilterFields.map((field) => {
              const values = Array.isArray(filterOptions?.[field.key]) ? filterOptions[field.key] : [];
              return (
                <label key={field.key} className={styles.filterField}>
                  <span>{field.label}</span>
                  {field.multi ? (
                    <div className={styles.filterMultiBuilder}>
                      <div className={styles.filterMultiActions}>
                        <button
                          type="button"
                          className={styles.inlineGhostButton}
                          onClick={() => {
                            setFilters((current) => ({ ...current, [field.key]: [] }));
                            setPendingTemperaturePerformanceTestNumber(getFirstUnselectedFilterOption(values, []));
                          }}
                        >
                          {tx('All', 'Tutti')}
                        </button>
                        <select
                          className={styles.filterInput}
                          value={pendingTemperaturePerformanceTestNumber}
                          onChange={(event) => setPendingTemperaturePerformanceTestNumber(event.target.value)}
                        >
                          <option value="">{tx('Select number', 'Seleziona numero')}</option>
                          {values.map((value) => (
                            <option key={value} value={value}>
                              n° {value}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className={styles.filterAddButton}
                          onClick={() => {
                            if (!pendingTemperaturePerformanceTestNumber) return;
                            setFilters((current) => {
                              const existing = Array.isArray(current[field.key]) ? current[field.key] : [];
                              if (existing.includes(pendingTemperaturePerformanceTestNumber)) return current;
                              return {
                                ...current,
                                [field.key]: [...existing, pendingTemperaturePerformanceTestNumber],
                              };
                            });
                          }}
                          disabled={!pendingTemperaturePerformanceTestNumber || (Array.isArray(filters[field.key]) && filters[field.key].includes(pendingTemperaturePerformanceTestNumber))}
                        >
                          + Add
                        </button>
                      </div>
                      <div className={styles.filterMultiSelected}>
                        {Array.isArray(filters[field.key]) && filters[field.key].length ? (
                          filters[field.key].map((value) => (
                            <button
                              key={value}
                              type="button"
                              className={styles.filterChip}
                              onClick={() => setFilters((current) => ({
                                ...current,
                                [field.key]: (Array.isArray(current[field.key]) ? current[field.key] : []).filter((item) => item !== value),
                              }))}
                            >
                              {`n° ${value}`} ×
                            </button>
                          ))
                        ) : (
                          <span className={styles.filterAllState}>{tx('All performance tests', 'Tutti i test performance')}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <select
                      className={styles.filterInput}
                      value={filters[field.key]}
                      onChange={(event) => setFilters((current) => ({ ...current, [field.key]: event.target.value }))}
                    >
                      <option value="">{tx('All', 'Tutti')}</option>
                      {values.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              );
            })}
          </div>
        </section>

        {error ? <div className={styles.errorMessage}>{error}</div> : null}

        <section className={styles.metricsGrid}>
          {metricCards.map((card) => (
            <article key={card.label} className={styles.metricCard}>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
            </article>
          ))}
        </section>

        <section className={styles.tablePanel}>
          <div className={styles.panelHeader}>
            <div>
              <h3>{tx('Manual step selector', 'Selettore manuale step')}</h3>
              <p>{tx('Click one of the most failing steps below to inspect that exact test across every reconstructed chamber-temperature cycle. The strongest failing steps are highlighted in red.', 'Clicca uno degli step con più FAIL qui sotto per ispezionare quel test esatto attraverso tutti i cicli di temperatura ricostruiti. Gli step con più FAIL sono evidenziati in rosso.')}</p>
            </div>
            <div className={styles.stepPickerToolbar}>
              <span className={styles.stepPickerCount}>
                {tx(
                  `${formatNumber(stepPickerRows.length)} ranked steps shown`,
                  `${formatNumber(stepPickerRows.length)} step classificati mostrati`
                )}
              </span>
              {selectedStepKey ? (
                <button
                  type="button"
                  className={styles.inlineGhostButton}
                  onClick={() => setSelectedStepKey('')}
                >
                  {tx('Clear selection', 'Cancella selezione')}
                </button>
              ) : null}
            </div>
          </div>
          <div className={styles.stepPickerList}>
            {stepPickerRows.map((row, index) => {
              const key = `${row.sequence_text}|||${row.step_name}`;
              const isSelected = key === selectedStepKey;
              const isCritical = Number(row.total_fails) > 0;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.stepPickerRow} ${isSelected ? styles.stepPickerRowSelected : ''} ${isCritical ? styles.stepPickerRowCritical : ''}`}
                  onClick={() => setSelectedStepKey(key)}
                >
                  <div className={styles.stepPickerMain}>
                    <span className={styles.stepPickerRank}>#{index + 1}</span>
                    <div className={styles.stepPickerText}>
                      <span className={styles.stepPickerEyebrow}>{formatPerformanceTestCoverage(row)}</span>
                      <strong className={styles.stepPickerTitle}>{row.sequence_text} · {row.step_name}</strong>
                    </div>
                  </div>
                  <div className={styles.stepPickerStatsInline}>
                    <span>{tx('FAILs', 'FAIL')}: {formatNumber(row.total_fails)}</span>
                    <span>{tx('Temps', 'Temp')}: {formatNumber(row.temperature_count)}</span>
                    <span>{tx('Spread', 'Spread')}: {formatDecimal(row.fail_rate_spread)}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className={styles.tablePanel}>
          <div className={styles.panelHeader}>
            <div>
              <h3>{tx('Thermal data readiness', 'Prontezza dei dati termici')}</h3>
              <p>{readinessSummaryText}</p>
            </div>
          </div>
          <div className={styles.insightGrid}>
            <article className={styles.insightCard}>
              <span className={styles.insightEyebrow}>{tx('Overall scope', 'Perimetro complessivo')}</span>
              <strong className={styles.insightTitle}>
                {tx('Temperature coverage and fail distribution', 'Copertura temperatura e distribuzione FAIL')}
              </strong>
              <div className={styles.insightStats}>
                <span>{tx('Temperature cycles', 'Cicli temperatura')}: {formatNumber(summary.cycle_count)}</span>
                <span>{tx('Distinct temperature points', 'Punti temperatura distinti')}: {formatNumber(summary.temperature_point_count)}</span>
                <span>{tx('Fail-bearing temp points', 'Punti temperatura con FAIL')}: {formatNumber(summary.fail_temperature_point_count)}</span>
                <span>{tx('Fail temperatures', 'Temperature con FAIL')}: {failTemperatureLabels.length ? failTemperatureLabels.join(', ') : '-'}</span>
              </div>
            </article>
            <article className={styles.insightCard}>
              <span className={styles.insightEyebrow}>{tx('Step readiness', 'Prontezza step')}</span>
              <strong className={styles.insightTitle}>
                {tx('How many steps support thermal analysis', 'Quanti step supportano l’analisi termica')}
              </strong>
              <div className={styles.insightStats}>
                <span>{tx('Steps ready for sensitivity', 'Step pronti per sensibilità')}: {formatNumber(summary.steps_ready_for_sensitivity)}</span>
                <span>{tx('Steps ready for correlation', 'Step pronti per correlazione')}: {formatNumber(summary.steps_ready_for_correlation)}</span>
                <span>{tx('Strongest step', 'Step più forte')}: {summary.strongest_step_name || '-'}</span>
                <span>{tx('Scope readiness', 'Prontezza perimetro')}: {summary.analysis_readiness || '-'}</span>
              </div>
            </article>
          </div>
        </section>

        <div className={styles.stackedPanels}>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Temperature analysis for selected step', 'Analisi temperatura per step selezionato')}</h3>
                <p>
                  {selectedStepOption
                    ? tx(
                      `Dedicated view for ${selectedStepOption.label}: here you can see how failed rows, total rows, and FAIL rate change with temperature.`,
                      `Vista dedicata per ${selectedStepOption.label}: qui puoi vedere come cambiano righe FAIL, righe totali e tasso FAIL al variare della temperatura.`
                    )
                    : tx(
                      'Select a step from the filter above to open a dedicated view of its temperature response.',
                      'Seleziona uno step dal filtro qui sopra per aprire una vista dedicata della sua risposta alla temperatura.'
                    )}
                </p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(selectedStepChartRef.current, `${exportBaseName}-selected-step-temperature-trend`)}
                  disabled={loading || !selectedStepOption || !selectedStepTrendRows.length}
                >
                  {tx('Chart PNG', 'Grafico PNG')}
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={selectedStepChartRef}>
              {loading ? (
                <div className={styles.emptyState}>{tx('Loading selected-step analysis...', 'Caricamento analisi step selezionato...')}</div>
              ) : !selectedStepOption ? (
                <div className={styles.emptyState}>{tx('Select a step to analyze it in detail against temperature.', 'Seleziona uno step per analizzarlo in dettaglio rispetto alla temperatura.')}</div>
              ) : selectedStepTrendRows.length ? (
                <Chart type="bar" data={selectedStepTrendData} options={selectedStepTrendOptions} />
              ) : (
                <div className={styles.emptyState}>{tx('No data is available for the selected step in the current scope.', 'Nessun dato disponibile per lo step selezionato nel perimetro attuale.')}</div>
              )}
            </div>
            {selectedStepOption ? renderTableLauncher(
              tx('Selected step temperature detail', 'Dettaglio temperatura step selezionato'),
              tx('Open the full temperature-by-step matrix for the currently selected test.', 'Apri la matrice completa temperatura-per-step per il test attualmente selezionato.'),
              stepAnalysisColumns,
              selectedStepTemperatureDetailRows,
              'selected-step-temperature-detail',
              tx('Selected Step Temperature Detail', 'Dettaglio Temperatura Step Selezionato'),
              !selectedStepTemperatureDetailRows.length
            ) : null}
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Temperature overview', 'Panoramica temperatura')}</h3>
                <p>{tx('Compare total rows, failed rows, and FAIL rate for each chamber-temperature phase reconstructed from burn-in step 0.1.', 'Confronta righe totali, righe FAIL e tasso FAIL per ogni fase di temperatura della camera ricostruita dallo step burn-in 0.1.')}</p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => openTemperatureChartModal('overview')}
                  disabled={loading || !temperatureSummary.length}
                >
                  {tx('Expand', 'Espandi')}
                </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(overviewChartRef.current, `${exportBaseName}-temperature-overview`)}
                  disabled={loading || !temperatureSummary.length}
                >
                  {tx('Chart PNG', 'Grafico PNG')}
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={overviewChartRef}>
              {loading ? (
                <div className={styles.emptyState}>{tx('Loading burn-in temperature overview...', 'Caricamento panoramica temperatura burn-in...')}</div>
              ) : temperatureSummary.length ? (
                <Chart type="bar" data={temperatureOverviewData} options={temperatureOverviewOptions} />
              ) : (
                <div className={styles.emptyState}>{tx('No temperature cycles were found in the selected burn-in scope.', 'Nessun ciclo di temperatura trovato nel perimetro burn-in selezionato.')}</div>
              )}
            </div>
            {renderTableLauncher(
              tx('Temperature summary table', 'Tabella riepilogo temperatura'),
              tx('Open the reconstructed temperature buckets and their totals.', 'Apri i bucket di temperatura ricostruiti e i relativi totali.'),
              temperatureSummaryColumns,
              temperatureSummary,
              'temperature-summary',
              tx('Burn-In Temperature Summary', 'Riepilogo Temperatura Burn-In'),
              !temperatureSummary.length
            )}
            {renderTableLauncher(
              tx('Cycle-detail analysis table', 'Tabella analisi dettaglio cicli'),
              tx('Open individual cycles and the first-FAIL rows only when you are tracing a specific source file.', 'Apri i singoli cicli e le righe del primo FAIL solo quando stai tracciando un file sorgente specifico.'),
              cycleDetailsColumns,
              cycleTableRows,
              'cycle-details',
              tx('Burn-In Temperature Cycle Detail', 'Dettaglio Cicli Temperatura Burn-In'),
              !cycleTableRows.length
            )}
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Performance headers most affected by temperature', 'Header performance più influenzati dalla temperatura')}</h3>
                <p>{tx('These stacked FAIL bars show which main performance-test sections contribute most to failures at each temperature point.', 'Queste barre FAIL impilate mostrano quali sezioni principali dei test di performance contribuiscono di più ai guasti in ogni punto di temperatura.')}</p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => openTemperatureChartModal('headers')}
                  disabled={loading || !topHeaders.length || !temperatureSummary.length}
                >
                  {tx('Expand', 'Espandi')}
                </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(headerChartRef.current, `${exportBaseName}-header-fails-by-temperature`)}
                  disabled={loading || !topHeaders.length || !temperatureSummary.length}
                >
                  {tx('Chart PNG', 'Grafico PNG')}
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={headerChartRef}>
              {loading ? (
                <div className={styles.emptyState}>{tx('Loading temperature analysis for performance headers...', 'Caricamento analisi temperatura per header performance...')}</div>
              ) : topHeaders.length && temperatureSummary.length ? (
                <Bar data={headerChartData} options={headerChartOptions} />
              ) : (
                <div className={styles.emptyState}>{tx('No FAIL clusters were found for performance headers in the selected scope.', 'Nessun cluster FAIL degli header performance trovato per il perimetro selezionato.')}</div>
              )}
            </div>
            {renderTableLauncher(
              tx('Performance-header analysis table', 'Tabella analisi header performance'),
              tx('Open the full header-by-temperature detail only when you need the exact rows.', 'Apri il dettaglio completo header-per-temperatura solo quando ti servono le righe dettagliate.'),
              headerAnalysisColumns,
              headerTableRows,
              'header-analysis',
              tx('Burn-In Header Analysis', 'Analisi Header Burn-In'),
              !headerTableRows.length
            )}
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Detailed steps most sensitive to temperature', 'Step di dettaglio più sensibili alla temperatura')}</h3>
                <p>{tx('The FAIL-rate spread highlights the steps whose failure behavior changes the most between cold, ambient, and hot burn-in phases.', 'Lo spread del tasso FAIL evidenzia gli step il cui comportamento di guasto cambia di più tra fasi burn-in fredde, ambiente e calde.')}</p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => openTemperatureChartModal('sensitivity')}
                  disabled={loading || !sensitivityChartRows.length}
                >
                  {tx('Expand', 'Espandi')}
                </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(sensitivityChartRef.current, `${exportBaseName}-temperature-sensitive-steps`)}
                  disabled={loading || !sensitivityChartRows.length}
                >
                  {tx('Chart PNG', 'Grafico PNG')}
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={sensitivityChartRef}>
              {loading ? (
                <div className={styles.emptyState}>{tx('Loading temperature-sensitive step analysis...', 'Caricamento analisi step sensibili alla temperatura...')}</div>
              ) : selectedStepOption && selectedStepSensitivitySummary && !selectedStepHasVisibleSensitivity ? (
                <div className={styles.insightGrid}>
                  <article className={styles.insightCard}>
                    <span className={styles.insightEyebrow}>{selectedStepSensitivitySummary.sequence_text}</span>
                    <strong className={styles.insightTitle}>{selectedStepSensitivitySummary.step_name}</strong>
                    <div className={styles.insightStats}>
                      <span>{tx('Temperatures seen', 'Temperature viste')}: {formatNumber(selectedStepSensitivitySummary.temperature_count)}</span>
                      <span>{tx('Min FAIL rate', 'FAIL minimo')}: {formatDecimal(selectedStepSensitivitySummary.min_fail_rate)}%</span>
                      <span>{tx('Max FAIL rate', 'FAIL massimo')}: {formatDecimal(selectedStepSensitivitySummary.max_fail_rate)}%</span>
                      <span>{tx('Spread', 'Spread')}: {formatDecimal(selectedStepSensitivitySummary.fail_rate_spread)}%</span>
                    </div>
                    <div className={styles.insightStats}>
                      <span>
                        {selectedStepSensitivitySummary.temperature_count < 2
                          ? tx(
                            'This step appears in only one temperature bucket, so spread cannot be compared yet.',
                            'Questo step compare in un solo bucket di temperatura, quindi lo spread non può ancora essere confrontato.'
                          )
                          : tx(
                            'This step keeps the same FAIL rate across the available temperatures, so the spread chart is flat at 0.',
                            'Questo step mantiene lo stesso tasso FAIL tra le temperature disponibili, quindi il grafico dello spread resta piatto a 0.'
                          )}
                      </span>
                    </div>
                  </article>
                </div>
              ) : sensitivityChartRows.length ? (
                <Bar data={sensitivityChartData} options={sensitivityChartOptions} />
              ) : (
                <div className={styles.emptyState}>{tx('No step-sensitivity data is available for the selected filters.', 'Nessun dato di sensibilità dello step disponibile per i filtri selezionati.')}</div>
              )}
            </div>
            {renderTableLauncher(
              tx('Detailed step-sensitivity table', 'Tabella sensibilità dettagliata step'),
              tx('Open the full sensitivity ranking when you want all spread metrics.', 'Apri la classifica completa della sensibilità quando vuoi tutte le metriche di spread.'),
              stepSensitivityColumns,
              stepSensitivity,
              'step-sensitivity',
              tx('Burn-In Step Temperature Sensitivity', 'Sensibilità Temperatura Step Burn-In'),
              !stepSensitivity.length
            )}
            {renderTableLauncher(
              tx('Detailed step-by-temperature table', 'Tabella step dettagliati per temperatura'),
              tx('Open the raw temperature-step matrix when you need the exact rows behind the charts.', 'Apri la matrice grezza temperatura-step quando ti servono le righe esatte dietro ai grafici.'),
              stepAnalysisColumns,
              stepTableRows,
              'step-analysis',
              tx('Detailed Burn-In Step Analysis', 'Analisi Dettagliata Step Burn-In'),
              !stepTableRows.length
            )}
          </section>

          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Correlation between temperature and FAIL', 'Correlazione tra temperatura e FAIL')}</h3>
                <p>{tx('This chart ranks the steps whose FAIL rate is most strongly correlated with temperature. Positive values mean the FAIL rate rises as temperature rises. Negative values mean the FAIL rate falls as temperature rises.', 'Questo grafico classifica gli step il cui tasso FAIL è più fortemente correlato con la temperatura. Valori positivi indicano che il tasso FAIL cresce all’aumentare della temperatura. Valori negativi indicano che il tasso FAIL diminuisce all’aumentare della temperatura.')}</p>
              </div>
              <div className={styles.exportActions}>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => openTemperatureChartModal('correlation')}
                  disabled={loading || !correlationChartRows.length}
                >
                  {tx('Expand', 'Espandi')}
                </button>
                <button
                  type="button"
                  className={styles.exportButton}
                  onClick={() => exportChartNodeAsPNG(correlationChartRef.current, `${exportBaseName}-temperature-fail-correlation`)}
                  disabled={loading || !correlationChartRows.length}
                >
                  {tx('Chart PNG', 'Grafico PNG')}
                </button>
              </div>
            </div>
            <div className={styles.chartWrap} ref={correlationChartRef}>
              {loading ? (
                <div className={styles.emptyState}>{tx('Loading temperature/FAIL correlation...', 'Caricamento correlazione temperatura/FAIL...')}</div>
              ) : selectedStepOption && selectedStepCorrelationSummary && !selectedStepHasCorrelationValue ? (
                <div className={styles.insightGrid}>
                  <article className={styles.insightCard}>
                    <span className={styles.insightEyebrow}>{selectedStepCorrelationSummary.sequence_text}</span>
                    <strong className={styles.insightTitle}>{selectedStepCorrelationSummary.step_name}</strong>
                    <div className={styles.insightStats}>
                      <span>{tx('Temperatures seen', 'Temperature viste')}: {formatNumber(selectedStepCorrelationSummary.temperature_count)}</span>
                      <span>{tx('Total FAILs', 'FAIL totali')}: {formatNumber(selectedStepCorrelationSummary.total_fails)}</span>
                      <span>{tx('Cold FAIL rate', 'FAIL freddo')}: {formatDecimal(selectedStepCorrelationSummary.coldest_fail_rate)}%</span>
                      <span>{tx('Hot FAIL rate', 'FAIL caldo')}: {formatDecimal(selectedStepCorrelationSummary.hottest_fail_rate)}%</span>
                    </div>
                    <div className={styles.insightStats}>
                      <span>
                        {selectedStepCorrelationSummary.temperature_count < 2
                          ? tx(
                            'Correlation needs at least two temperature buckets for this step.',
                            'La correlazione richiede almeno due bucket di temperatura per questo step.'
                          )
                          : tx(
                            'Correlation cannot be computed because this step does not change enough across temperatures.',
                            'La correlazione non può essere calcolata perché questo step non cambia abbastanza tra le temperature.'
                          )}
                      </span>
                    </div>
                  </article>
                </div>
              ) : correlationChartRows.length ? (
                <Bar data={correlationChartData} options={correlationChartOptions} />
              ) : (
                <div className={styles.emptyState}>{tx('Not enough temperature points were found to calculate a meaningful correlation.', 'Non sono stati trovati abbastanza punti di temperatura per calcolare una correlazione significativa.')}</div>
              )}
            </div>
            {renderTableLauncher(
              tx('Temperature/FAIL correlation table', 'Tabella correlazione temperatura/FAIL'),
              tx('Open the full step-correlation table when you need every coefficient and the hot-cold delta.', 'Apri la tabella completa di correlazione per step quando ti servono tutti i coefficienti e il delta caldo-freddo.'),
              stepCorrelationColumns,
              stepCorrelationRows,
              'step-correlation',
              tx('Burn-In Step Temperature Correlation', 'Correlazione Temperatura Step Burn-In'),
              !stepCorrelationRows.length
            )}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Main performance sections at a glance', 'Sezioni performance principali a colpo d’occhio')}</h3>
                <p>{tx('A fast visual ranking of the performance-test headers most affected by temperature, without forcing you to read a long table.', 'Classifica visiva rapida degli header dei test di performance più influenzati dalla temperatura, senza dover leggere una tabella lunga.')}</p>
              </div>
            </div>
            {renderInsightCards(headerInsightRows, (row) => (
              <>
                <span className={styles.insightEyebrow}>{row.temperature_label}</span>
                <strong className={styles.insightTitle}>{row.header_name}</strong>
                <div className={styles.insightStats}>
                  <span>{tx('Failed rows', 'Righe FAIL')}: {formatNumber(row.fail_rows)}</span>
                  <span>{tx('FAIL rate', 'Tasso FAIL')}: {formatDecimal(row.fail_rate)}%</span>
                  <span>{tx('Cycles', 'Cicli')}: {formatNumber(row.cycle_count)}</span>
                </div>
              </>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Main FAIL steps at a glance', 'Step principali in FAIL a colpo d’occhio')}</h3>
                <p>
                  {selectedStepOption
                    ? tx(
                      `This section shows only the selected step ${selectedStepOption.label} in thermal detail.`,
                      `Questa sezione mostra solo lo step selezionato ${selectedStepOption.label} nel dettaglio termico.`
                    )
                    : tx(
                      'This section replaces the heavier step table with compact cards that surface the main temperature-driven FAILs immediately.',
                      'Questa sezione sostituisce la tabella più pesante degli step con card compatte che mostrano subito i principali FAIL guidati dalla temperatura.'
                    )}
                </p>
              </div>
            </div>
            {renderInsightCards(stepInsightRows, (row) => (
              <>
                <span className={styles.insightEyebrow}>{row.temperature_label}</span>
                <strong className={styles.insightTitle}>{row.sequence_text} · {row.step_name}</strong>
                <div className={styles.insightStats}>
                  <span>{tx('Failed rows', 'Righe FAIL')}: {formatNumber(row.fail_rows)}</span>
                  <span>{tx('FAIL rate', 'Tasso FAIL')}: {formatDecimal(row.fail_rate)}%</span>
                  <span>{tx('Perf. tests', 'Test perf.')}: {formatPerformanceTestCoverage(row)}</span>
                </div>
              </>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>{tx('Correlation key points', 'Punti chiave della correlazione')}</h3>
                <p>
                  {selectedStepOption
                    ? tx(
                      `Here you can see the temperature/FAIL correlation for the selected step ${selectedStepOption.label}.`,
                      `Qui vedi la correlazione temperatura/FAIL per lo step selezionato ${selectedStepOption.label}.`
                    )
                    : tx(
                      'These cards surface the failure relationships most strongly linked to temperature first, making them much easier to read than the full correlation table.',
                      'Queste card mostrano per prime le relazioni di guasto più fortemente legate alla temperatura, molto più facili da leggere rispetto alla tabella completa di correlazione.'
                    )}
                </p>
              </div>
            </div>
            {renderInsightCards(correlationInsightRows, (row) => (
              <>
                <span className={styles.insightEyebrow}>{row.correlation_strength}</span>
                <strong className={styles.insightTitle}>{row.sequence_text} · {row.step_name}</strong>
                <div className={styles.insightStats}>
                  <span>{tx('Correlation', 'Correlazione')}: {formatDecimal(row.temperature_fail_correlation, 3)}</span>
                  <span>{row.correlation_direction}</span>
                  <span>{tx('Hot-cold delta', 'Delta caldo-freddo')}: {formatDecimal(row.fail_rate_delta_hot_vs_cold)}%</span>
                </div>
              </>
            ))}
          </section>
        </div>

        {activeTemperatureChartModal && (
          <div
            className={styles.modalOverlay}
            onClick={closeTemperatureChartModal}
            role="presentation"
          >
            <div
              className={`${styles.chartModal} ${temperatureChartModalSizeClass}`}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={activeTemperatureChartModal.title}
            >
              <div className={styles.modalHeader}>
                <div>
                  <h3>{activeTemperatureChartModal.title}</h3>
                  <p>{tx('Use the size buttons to inspect the chart more comfortably, then export the exact expanded view if needed.', 'Usa i pulsanti di dimensione per ispezionare il grafico più comodamente, poi esporta la vista espansa esatta se necessario.')}</p>
                </div>
                <div className={styles.exportActions}>
                  {CONTROL_MODAL_SIZES.map((size) => (
                    <button
                      key={size.key}
                      type="button"
                      className={styles.exportButton}
                      onClick={() => setTemperatureChartModalSize(size.key)}
                    >
                      {size.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => exportChartNodeAsPNG(
                      activeTemperatureChartModal.exportRef.current,
                      `${exportBaseName}-${activeTemperatureChartModal.fileSuffix}`
                    )}
                    disabled={activeTemperatureChartModal.isEmpty}
                  >
                    {tx('Chart PNG', 'Grafico PNG')}
                  </button>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={closeTemperatureChartModal}
                  >
                    {tx('Close', 'Chiudi')}
                  </button>
                </div>
              </div>
              <div className={`${styles.chartModalBody} ${temperatureChartModalBodyClass}`}>
                {activeTemperatureChartModal.render()}
              </div>
            </div>
          </div>
        )}

        {temperatureTableModal && (
          <div
            className={styles.modalOverlay}
            onClick={closeTemperatureTableModal}
            role="presentation"
          >
            <div
              className={`${styles.chartModal} ${styles.chartModalLarge}`}
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={temperatureTableModal.title}
            >
              <div className={styles.modalHeader}>
                <div>
                  <h3>{temperatureTableModal.title}</h3>
                  <p>{temperatureTableModal.description}</p>
                  <p>
                    {tx(
                      `${criticalTemperatureTableCount} critical row(s) are currently in this table. Click the headers to sort by status or any column.`,
                      `${criticalTemperatureTableCount} riga/e critica/che sono attualmente presenti in questa tabella. Clicca le intestazioni per ordinare per stato o per qualsiasi colonna.`
                    )}
                  </p>
                </div>
                <div className={styles.exportActions}>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => exportRowsAsCSV(
                      temperatureTableModal.columns,
                      temperatureTableModal.rows,
                      `${exportBaseName}-${temperatureTableModal.fileSuffix}`
                    )}
                    disabled={!temperatureTableModal.rows.length}
                  >
                    CSV
                  </button>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={() => exportRowsAsPDF(
                      temperatureTableModal.pdfTitle,
                      temperatureTableModal.columns,
                      temperatureTableModal.rows,
                      `${exportBaseName}-${temperatureTableModal.fileSuffix}`
                    )}
                    disabled={!temperatureTableModal.rows.length}
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    className={styles.exportButton}
                    onClick={closeTemperatureTableModal}
                  >
                    {tx('Close', 'Chiudi')}
                  </button>
                </div>
              </div>
              <div className={`${styles.chartModalBody} ${styles.chartModalBodyLarge}`}>
                {renderTable(temperatureTableModal.columns, sortableTemperatureTableRows, { sortable: true })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function BurnInStatusPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-status" />;
}

export function BurnInFailureParetoPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-failure-pareto" />;
}

export function BurnInStepFailRatePage() {
  return <BurnInAnalyticsPage configPath="/burn-in-step-fail-rate" />;
}

export function BurnInStepVolumePage() {
  return <BurnInAnalyticsPage configPath="/burn-in-step-volume" />;
}

export function BurnInTimelinePage() {
  return <BurnInAnalyticsPage configPath="/burn-in-timeline" />;
}

export function BurnInHourlyActivityPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-hourly-activity" />;
}

export function BurnInSourceHealthPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-source-health" />;
}

export function BurnInUploadComparisonPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-upload-comparison" />;
}

export function BurnInUnitsPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-units" />;
}

export function BurnInDataCompletenessPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-data-completeness" />;
}

export function BurnInSequenceHotspotsPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-sequence-hotspots" />;
}

export function BurnInSequenceBandsPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-sequence-bands" />;
}

export function BurnInValueSpreadPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-value-spread" />;
}

export function BurnInLimitCompliancePage() {
  return <BurnInAnalyticsPage configPath="/burn-in-limit-compliance" />;
}

export function BurnInNearLimitRiskPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-near-limit-risk" />;
}

export function BurnInCategoricalValuesPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-categorical-values" />;
}

export function BurnInPieChartPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-pie-chart" />;
}

export function BurnInParetoChartPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-pareto-chart" />;
}

export function BurnInControlChartPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-control-chart" />;
}

export function BurnInUUTStatusSummaryPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-uut-status-summary" />;
}

export function BurnInRootCauseAnalyzerPage() {
  return <BurnInAnalyticsPage configPath="/burn-in-root-cause-analyzer" />;
}
